/**
 * The world: a sparse grid of chunks addressed by chunk coordinates.
 *
 * Phase 0 generates a fixed island up front. Phase 3 makes this streaming -
 * chunks generated on demand around the player and unloaded behind them,
 * which is what keeps memory bounded on a 16 GB machine.
 */

import {
  Chunk, CHUNK_SIZE, blockIndex,
  PAD, PADDED_VOLUME, paddedIndex,
} from "./chunk.js";
import {
  BlockId, isSolid, isOpaque, isFluid, isFarmland, isAdultOnly, isChest, isStove,
  stoveLit, LAVA,
  isWater, isWaterFlow, waterFlowId, WATER_RANGE, STAR_LIGHT, isNaturalGround, chestId,
  isTorch,
  isLavaFlow, lavaFlowId, LAVA_RANGE, isLava,
  isOilFlow, oilFlowId, OIL_RANGE, OIL,
} from "../block/registry.js";
import { ROAD_FOOTING, ROAD_HEADROOM } from "./walk.js";
import { waterFill } from "./mesher.js";
import { raise, footprintOf, FOUNDATION_DEPTH } from "./blueprint.js";
import {
  VILLAGE_CELL, VILLAGE_REACH, VILLAGE_SLOPE, VILLAGE_STEP,
  villageTries, planVillage, buildPart, partBox, bedsIn, chestCell, furnish,
  dwellings, plotCell, type Part,
  needsLevelling, type Put,
} from "./village.js";
import {
  TREASURE_CELL, TREASURE_LOOT, treasureSpot,
} from "./treasure.js";
import {
  LAIR_CELL, LAIR_REACH, HALL, HALL_DEPTH, BORE, WAYS,
  lairTries, inHall,
} from "./lair.js";
import {
  KREMLIN, KREMLIN_SPAN, KREMLIN_DROP, KREMLIN_APPROACH,
  KREMLIN_STAIR_HALF, KREMLIN_STAIR_PIER,
} from "./kremlin.js";
import {
  CASTLE, CASTLE_SPAN, CASTLE_HEIGHT, CASTLE_DROP, APPROACH_REACH,
  castleProfile,
} from "./castle.js";
import { Containers, CHEST_SLOTS } from "./containers.js";
import {
  LightField, MAX_LIGHT, LIGHT_RADIUS, TORCH_LIGHT, STOVE_LIGHT, LAVA_LIGHT,
  type Emitter, type LightReads,
} from "./light.js";
import { Furnaces } from "./furnace.js";
import { fbm2, noiseColumn3, hashInt } from "./noise.js";
import { WAR_KIT } from "./campKit.js";
import { HILL_SPAN, HILL_FLOOR } from "./kingHill.js";
import { oneBlockAt } from "./oneBlock.js";
import { oneIsland, oneIslandBlock, oneIslandTrees } from "./oneIsland.js";
import {
  WALL_HEIGHT, FLAG_HEIGHT, onWall, flagFor as flagAt,
  SUPPLY_CELL, supplySpot,
} from "./war.js";
import { PlainItemId } from "../block/items.js";
import {
  biomeFrom, warmthAt, ruggedAt, liftAt, roughAt, treeChance, SNOW_LINE,
  type Biome,
} from "./biome.js";

/**
 * How thick a hemp stand has to be before anything grows in it, and how
 * densely it fills once it does.
 *
 * Tuned to "uncommon but easy to find": a few stands on an average world,
 * enough that walking the steppe turns one up, not so many that it becomes
 * the ground cover.
 */
/**
 * How hard running water shoves, in blocks per second.
 *
 * Enough to be felt and to carry you somewhere, not enough to make a stream
 * impassable: you should be able to wade upstream if you mean it.
 */
export const FLOW_PUSH = 2.6;

/**
 * How fast a fluid runs, in blocks a second.
 *
 * Water was instantaneous: the whole answer appeared in the frame the block
 * came away, because the flood wrote as it walked. Which is fine for a bucket
 * and wrong for a trench - somebody digging a channel from a lake watched the
 * far end fill before the near one had a chance to look wet.
 *
 * Two for water and one for лава, asked for in those words. Лава being the
 * slower of the two is the whole of what makes a pool of it a thing you can
 * get away from.
 */
export const WATER_SPEED = 2;
export const LAVA_SPEED = 1;

/**
 * And нафта, thirty per cent slower over the ground than water.
 *
 * "Поведінка нафти як у води, тільки тече повільніше на 30%." Water crosses
 * two cells a second; oil crosses one and two fifths. It is the one number
 * that makes the two read as different liquids rather than as two colours.
 */
export const OIL_SPEED = WATER_SPEED * 0.7;

/**
 * How wide the causeway is, as a fraction of the world, and how high it counts
 * as land.
 *
 * Narrow, and barely above the water. It is a chokepoint on purpose: the orcs
 * have no boats, so this is the only way across, which makes it the one place
 * worth fortifying and the one place worth breaking through. Raise the height
 * and it becomes a second island; widen it and there is nothing to defend.
 */
/** How far the land rises and the seabed falls, and how sharply. */
const SHORE_AMPLITUDE = 70;
const SHORE_CURVE = 1.8;

const NECK_WIDTH = 0.035;
// High enough to stay dry once the relief on it is damped: 0.72 of `land`
// puts the bar about four blocks clear of the water.
const NECK_HEIGHT = 0.72;

/** 1 well inside, 0 well outside, smooth across the edge. */
function falloff(t: number): number {
  if (t <= 0.55) return 1;
  if (t >= 1) return 0;
  const k = (1 - t) / 0.45;
  return k * k * (3 - 2 * k);
}

/** How far (x, z) is from the line between two points. */
function distanceToSegment(
  x: number, z: number, ax: number, az: number, bx: number, bz: number,
): number {
  const dx = bx - ax;
  const dz = bz - az;
  const len2 = dx * dx + dz * dz;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / len2));
  return Math.hypot(x - (ax + dx * t), z - (az + dz * t));
}

/**
 * How much light a block gives off.
 *
 * Two things do: a смолоскип, and a stove with a fire in it. Kept here rather
 * than as a field on every block definition, because two entries in a
 * function is smaller than a column of zeroes down a table of a hundred and
 * twenty blocks.
 */
function emission(id: number): number {
  // Both kinds: the one standing on the floor and the four hung on walls.
  if (isTorch(id)) return TORCH_LIGHT;
  if (id === BlockId.RubyStar) return STAR_LIGHT;
  // The замок is lit by whatever Чахлик is, and it is the wrong colour.
  if (id === BlockId.CursedLamp) return TORCH_LIGHT;
  // Лава is the one thing down there that lights itself, which is what makes
  // a pool of it visible from the far end of a cave instead of a hole you
  // fall into.
  // Flowing лава too: a stream that went dark as it left the pool would be
  // the one thing in this game giving off less light the nearer it got.
  if (isLava(id)) return LAVA_LIGHT;
  return stoveLit(id) ? STOVE_LIGHT : 0;
}

/**
 * How thick a clay lens can be, in blocks, under the soil.
 *
 * Three: enough that hitting one gives you a useful handful rather than a
 * single block, and thin enough that it reads as a seam rather than as a
 * second layer of the world.
 */
const CLAY_LENS_DEPTH = 3;
/**
 * Where a shore turns from sand to mud to meadow.
 *
 * Two bars on one field, so the three run into each other along a bank
 * rather than tiling it. Sand above the higher bar is about a third of the
 * waterline, grass below the lower one about a third, and the mud between
 * them is the rest.
 */
const SHORE_SAND = 0.55;
const SHORE_GRASS = 0.45;
/** How much of a lake floor is clay rather than sand or mud. */
const BED_CLAY = 0.56;
/**
 * How much of the land gets one.
 *
 * Read off the noise field's own distribution rather than guessed, and pooled
 * over ten seeds rather than taken from one. Both of those cost a try: 0.54 by
 * eye put clay under one dry column in nine, and 0.453 - correct for a single
 * world - ranged from a third to seven eighths across the ten, which on the
 * generous end is not something you look for at all.
 *
 * At 0.527 roughly a third of dry columns carry a lens. Some worlds are
 * clay country and some are not, and that is worth keeping: what matters is
 * that no world has none.
 */
const CLAY_LENS_THRESHOLD = 0.527;

/**
 * How far above the waterline сосна takes over from the other two, in blocks.
 *
 * Measured rather than chosen: at +17 it is the top fifth of the ground a
 * tree can grow on at all, which leaves the oak the commonest tree and the
 * acacia its share of the dry steppe.
 */
const PINE_LINE = 17;

/**
 * Where the grass is on «Сучасна війна».
 *
 * Sixty blocks of ground under it, which is what the map was asked for, and
 * the floor of the world under that - so the surface is at 61 and everything
 * above is sky to dig upward into or build in.
 */
export const WAR_GROUND = 61;

/**
 * How high above the sea a tree will still grow.
 *
 * Under the snowline by a good margin, which is what makes a mountain read
 * as a mountain: bare rock, then white, and the wood stops where the slope
 * gets serious.
 */
const TREE_LINE = 22;

/**
 * How thick a wood gets, and where the wood is.
 *
 * A ліс has to be somewhere you can be *in*, not a scattering of trees you
 * walk between - вовкулаки live in one, and a forest you can see across is
 * not somewhere anything hides. The seven-block cell used to hold at most
 * one trunk, which puts a ceiling of about a quarter on how much of the
 * ground can be under leaves however high the chance is set: a crown is
 * thirteen columns and a cell is forty-nine. So a cell now has three seats
 * and the damp country fills most of them.
 *
 * The dry number is the other half of the same decision. A steppe with a
 * tree every hundred metres is a steppe; make it thicker and the whole world
 * is woodland and the forest stops meaning anything.
 */
const TREE_SEATS = 4;
const FOREST_DAMP = 0.5;
const FOREST_THICK = 0.80;
const FOREST_OPEN = 0.05;

/**
 * Blocks between one lair and the next.
 *
 * The rule survives the move off the island: lairs are meant to be broken one
 * at a time, and two within a sabre's swing of each other are one lair with
 * two blocks in it. The кремль keeps its own to this spacing.
 */
export const LAIR_SPACING = 12;

const HEMP_THRESHOLD = 0.60;
const HEMP_DENSITY = 1.4;

/** Pack chunk coords into one string key. Cheap enough at this scale. */
function key(cx: number, cy: number, cz: number): string {
  return `${cx},${cy},${cz}`;
}

/** Floor-divide that behaves correctly for negative coordinates. */
function floorDiv(a: number, b: number): number {
  return Math.floor(a / b);
}

/** The six chunks sharing a face with a given one. */
interface Cell { x: number; y: number; z: number; }

/** One kind of ore: where it lies, how rare it is, and how big a seam runs. */
interface OreVein {
  block: BlockId;
  /** Ore only appears inside this rock, and a seam stops where the rock does. */
  host: BlockId;
  /** Depth band, as fractions of the world's height. */
  from: number;
  to: number;
  /**
   * Blocks up from the bottom of the world, instead of the band above.
   *
   * For адамантій, which is "на самому дні світів + 10 блоків" - a depth in
   * blocks, not a fraction. Written as a fraction it would drift with the
   * world's height: ten blocks of a 128-tall world is a different rule from
   * ten blocks of a 96-tall one, and the whole point of this ore is that
   * everybody has to go all the way down for it.
   */
  floorBand?: number;
  /**
   * Blocks below and above the waterline, instead of the band above.
   *
   * For anything that is a *shoreline* phenomenon rather than a depth one. The
   * titanium placers are old beaches, so where they belong depends on where
   * the water is and not on how deep the world happens to be - and expressing
   * them as a fraction of the world's height put them, after the waterline
   * moved, in a band with no sand in it at all.
   */
  shore?: { below: number; above: number };
  /** Cells of the band per seam. Higher is rarer. */
  rarity: number;
  /** How many blocks one seam walks. */
  size: number;
  /** Salt, so two ores with the same numbers do not land on each other. */
  salt: number;
  /**
   * Never step up or down while growing.
   *
   * A tuning choice, not a rule, and no test pins it: seeding the seam in its
   * host is what made the titanium findable at all - blind seeding gave six
   * blocks of ilmenite in a whole world - and this adds about a third again on
   * top by keeping the walk inside a sheet of sand one to three blocks deep.
   * What it mostly buys is the look: a bed you dig along, rather than a clump.
   */
  flat?: boolean;
}

/**
 * Is this cell inside a cave?
 *
 * Two fields multiplied rather than one thresholded: a single field gives
 * blobs, and two that both have to be near their middle gives the long thin
 * winding thing a cave actually is - the intersection of two sheets is a
 * line. The same trick that draws worm-caves anywhere they are drawn.
 *
 * The frequencies are the shape of the caves: 1/34 is a passage a few blocks
 * across, and the second field is turned a little faster so the passages
 * bend rather than run straight.
 */

/**
 * How near the middle of each field a cell has to be to be hollow.
 *
 * The width of the passages, and the one number that decides whether this
 * map is stone with caves in it or cheese. Measured: at 0.085 about one cell
 * in fifty underground is open, which is caves you have to find.
 */
const CAVE_BORE = 0.065;

/** Below this, a cave has lava in the bottom of it rather than air. */
export const LAVA_DEPTH = 9;

/** And the lowest a cave is cut at all: under it is bedrock. */
export const CAVE_FLOOR = 3;

/**
 * How high water may stand here without spilling out of the picture.
 *
 * Every column works out its own water table from its own noise, and there
 * is nothing in that to stop one standing higher than the ground of the
 * column beside it: the lake then ends in a vertical face of water two or
 * three blocks tall with dry sand at the foot of it, held in by nothing.
 * Seven per cent of the water in the world was doing this, and it was
 * reported the day it shipped - twice, because it also looks like water that
 * has forgotten how to flow.
 *
 * The rule is the obvious one: water may reach a height only if every
 * neighbour either has ground that high or has water that high too. Working
 * that out exactly is a flood fill over the whole basin, which a world grown
 * a column at a time cannot do; taking each neighbour's own unclamped level
 * is one step of the same calculation, and it is what turns a wall into a
 * shore.
 *
 * Cells are the packed form `adventureCell` returns: ground in the low half,
 * the water table above it.
 */
function heldIn(
  cell: number, level: number, around: readonly (readonly [number, number])[],
): number {
  let held = level;
  for (const [near, nearLevel] of around) {
    const ground = near & 0xffff;
    // The higher of what that neighbour is made of and what stands on it.
    const holds = ground > nearLevel ? ground : nearLevel;
    if (holds < held) held = holds;
  }
  // Never below this column's own ground: that is not a lower lake, it is
  // no lake, and the fill rule already says so.
  const floor = cell & 0xffff;
  return held < floor ? floor : held;
}

/**
 * Is this column dry land, high enough above the water to grow something on?
 *
 * Written down once, because the same off-by-one quietly emptied the whole
 * adventure map of trees *and* of wild plants and nothing noticed for weeks.
 *
 * `heldIn` clamps a column's water table to the column's own ground - a
 * table below the ground is not a lower lake, it is no lake - so on dry land
 * the table reads *exactly* the ground. The old test was
 * `ground <= water + 1`, which on dry land is `ground <= ground + 1`: true
 * everywhere. Every tree and every stalk of wild wheat in the adventure was
 * refused on the grounds of being underwater.
 *
 * So: water stands over this column only when the table is strictly above
 * the ground, and the sea has its own rule because the sea's level is not in
 * the table at all - the beach at its edge grows nothing either.
 */
export function dryGround(ground: number, table: number, seaLevel: number): boolean {
  if (table > ground) return false;
  return ground > seaLevel + 1;
}

/** The countries of the adventure world, in the order their numbers go. */
const ADVENTURE_KINDS: readonly Biome[] =
  ["meadow", "mountain", "winter", "desert"];

/**
 * How much country an adventure starts with, in chunks either way.
 *
 * Six is a patch two hundred blocks across, about a third of a second - far
 * enough to stand in and look around at while the rest catches up, and near
 * enough that starting is not a wait.
 */
const SPAWN_REACH = 6;

/**
 * How far out an adventure keeps its country grown, in chunks, and how far
 * out it keeps what it grew.
 *
 * Ten is a hundred and sixty blocks, which is as far as the fog lets anybody
 * see; growing further would be paying for hills nobody can look at. The
 * forgetting reach is comfortably beyond it, because somebody pacing back
 * and forth over the line between the two would otherwise spend every second
 * throwing country away and making it again.
 *
 * Here rather than in main.ts because the server walks the same country for
 * the same reasons, and two numbers that must agree should be one number.
 */
export const GROW_REACH = 10;

/**
 * How far out the country is grown *before* the first frame after a load.
 *
 * "Коли я загружаю збережену гру я опиняюсь за межами створених блоків і
 * просто бескінечно падаю в пустоту."
 *
 * A save holds where the player was standing and the chunks they changed -
 * and nothing else, because everything else comes back out of the seed. But
 * an adventure grows its country two columns a frame, and `generate()` grows
 * it around the *start*, so a save made anywhere the player had walked to
 * without building loads with nothing under their feet. They fall while the
 * columns trickle in, and by the time the ground arrives they are below it,
 * still falling, with the country they need now above them.
 *
 * Four chunks is sixty-four blocks of solid ground in every direction: the
 * player stands, and the frame loop fills in the rest of the view at its own
 * pace. Ten - the full reach - would be four hundred columns and a wait on
 * top of the load; four is eighty-one and comes in under a tenth of a second.
 */
export const LOAD_REACH = 4;
export const FORGET_REACH = 16;

/**
 * How far the water table swings, and how far under the ground it sits.
 *
 * Its own noise, so a lake is a place rather than a consequence. The drop is
 * most of the rise: the table is under the smooth line of the ground four
 * times in five, and the fifth is where the hollows fill.
 */
/**
 * How many times the water is asked to settle against its neighbours.
 *
 * Each pass is one block of spilling, and costs a ring of noise around every
 * column grown. Three is where the ledges stop being worth another ring.
 */
const WATER_SETTLE = 3;

const LAKE_RISE = 16;
const LAKE_DROP = 11;

/**
 * How high a river stands at its head, and how wide its valley is.
 *
 * The head is measured above the sea and reached where the continent field
 * has saturated - so the surface of a river runs from twelve blocks up, deep
 * inland, down to sea level at the coast. Twelve rather than more because
 * the water has to *stay in the channel*: a river standing twenty blocks
 * above the sea a hundred blocks from it is a river running down a staircase.
 *
 * The width is in units of the noise field rather than in blocks, because
 * that is the only thing a column can know about it. Measured on the ground,
 * 0.03 comes out about fourteen blocks of valley with four or five of water
 * in the bottom of it.
 */
const RIVER_HEAD = 12;
/**
 * How near the middle of the noise band is worth measuring properly, and
 * how wide the valley actually is in blocks.
 *
 * The first number is only a filter: anything further from the middle of the
 * field than this cannot be a river however slowly the field is changing, so
 * it is refused before the four samples that measure the distance in blocks.
 */
const RIVER_NEAR = 0.25;
/*
 * Half again as wide as it was, which was seven.
 *
 * "Збільши глибину та ширину річок на 50%". Ten and a half rather than ten
 * because this is compared against a distance in blocks and not an index:
 * seven times one and a half, exactly, and the channel comes out twenty-one
 * blocks across instead of fourteen.
 */
const RIVER_HALF = 10.5;
/**
 * How wide a river valley may get, in blocks from the middle.
 *
 * Sixty: seven of channel and the rest of bank, rising at two thirds of a
 * block each, which is thirty-five blocks of climb. The cap is safe to stop
 * at because the cut only ever lowers ground - where the bank has risen to
 * meet the land there is nothing left to cut, so the edge of the valley is
 * wherever the two meet rather than wherever this number falls.
 */
const RIVER_REACH = 60;
/** How many blocks out the bank goes for every block it rises. */
const RIVER_FLARE = 1.5;
/** And how far out the bank is built up as well as cut down. */
const RIVER_LEVEE = 3;


/**
 * And how rough the country may be and still hold one, and how fast the
 * table sinks out of sight past that.
 *
 * Sunk rather than switched off. A gate is a wall: the column on the rough
 * side of it has no lake and no extra ground either, so the water beside it
 * stands with nothing holding it in. Sinking the table instead lets the
 * ground rise out of the water, which is what a shore is.
 *
 * Twenty-two hundredths, measured. At thirty-four there are more lakes -
 * 4773 cells against 3328 - and seven of them have a three-block waterfall
 * down one side, because the table follows the broad line of the ground and
 * rough ground has a slope. At twenty-two nothing steps more than one.
 */
const LAKE_CALM = 0.22;
const LAKE_SINK = 60;

/** A column that has its rock and its water, but nothing growing on it. */
const COLUMN_TERRAIN = 1;
/** A column with everything in it. Nothing more will be generated there. */
const COLUMN_FULL = 2;

/**
 * How many columns either side of the one asked for get lit with it.
 *
 * Two is a five-by-five patch: big enough that the thrown-away skirt is a
 * third of the work rather than eight ninths of it, small enough that a
 * player who turns around has not paid for a quarter of the map.
 */
const LIGHT_PATCH = 2;

/** The four compass directions. Water spreads sideways, never diagonally. */
const HORIZONTAL: readonly (readonly [number, number])[] =
  [[1, 0], [-1, 0], [0, 1], [0, -1]];

/** Key for the visited map. One string per cell examined, and no more. */
function cellKey(c: Cell): string {
  return `${c.x},${c.y},${c.z}`;
}

const NEIGHBOURS: readonly [number, number, number][] = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];

/** Positive modulo - `-1 % 16` is `-1` in JS, we need `15`. */
function mod(a: number, b: number): number {
  return ((a % b) + b) % b;
}

export interface WorldGenOptions {
  seed: number;
  /** World size in chunks along X and Z. */
  sizeXZ: number;
  /** World height in chunks. */
  sizeY: number;
  /**
   * Whether the far island grows a кремль. On unless the map says otherwise.
   *
   * A map with no fortress has no lairs and therefore no raids, which is
   * exactly what a mini-game on a flat field wants.
   */
  kremlin?: boolean;
  /**
   * Whether the 18+ switch is on.
   *
   * Deliberately **not** part of the save: it describes the person holding the
   * mouse, not the world. The same seed generates the same terrain either way
   * - the only difference is hemp, which is placed solely in cells the food
   * crops left empty, so turning the switch on or off never moves a single
   * stalk of wheat or a single tree.
   */
  adult?: boolean;
  /**
   * What shape of world to grow.
   *
   * "islands" is the game: two of them, a causeway, a кремль on the far one.
   * "hill" is «Король гори» - a single cube of вибухоблок standing on a
   * floor, and nothing else at all. A branch here rather than a second World
   * class because everything else about a world - chunks, light, saving,
   * reach, the mesher - is the same either way, and two classes would have to
   * be kept in step for ever over one function's worth of difference.
   */
  terrain?: "islands" | "hill" | "adventure" | "war" | "one";
}

export class World {
  private readonly chunks = new Map<string, Chunk>();

  /**
   * Chunks the player has changed since generation.
   *
   * Terrain is deterministic from the seed, so a save only needs the
   * differences. This set is what keeps a save small now and possible at all
   * once the world streams infinitely.
   */
  private readonly modified = new Set<string>();

  readonly opts: WorldGenOptions;

  /**
   * What the chests hold.
   *
   * Lives on the world because it is world state and the save has to reach
   * it, but deliberately not in the voxel grid: twenty-seven stacks do not
   * pack into a block id.
   */
  readonly containers = new Containers();

  /** What every stove is burning and smelting. Same arrangement as the chests. */
  readonly furnaces = new Furnaces();

  /**
   * How much sky and how much torchlight reaches every cell.
   *
   * Built the first time anything asks and patched in a box around every edit
   * afterwards. See light.ts for what it holds and why it is a flat array.
   *
   * **Lazy on purpose.** Lighting a full-size world costs about a sixth of a
   * second, which is nothing next to a game session and a great deal next to
   * a test suite that builds a few dozen worlds and cares about the light in
   * three of them: doing it eagerly took the suite from twelve seconds to a
   * hundred and fifty-seven, and a suite nobody wants to wait for is a suite
   * that stops being run.
   */
  get light(): LightReads {
    return this.reads;
  }

  /**
   * Light, read out of whichever chunk holds it.
   *
   * The same questions the world-sized field answered, asked of the chunks
   * instead. A column that has not been lit yet is lit by the first person
   * to look at it, which is what makes it possible never to light ground
   * nobody walks to.
   */
  private readonly reads: LightReads = {
    sky: (x: number, y: number, z: number): number =>
      this.lightPacked(x, y, z) >> 4,
    block: (x: number, y: number, z: number): number =>
      this.lightPacked(x, y, z) & 0x0f,
    packed: (x: number, y: number, z: number): number =>
      this.lightPacked(x, y, z),
    copyRow: (out: Uint8Array, at: number, x0: number, y: number, z: number,
      count: number): void => {
      for (let i = 0; i < count; i++) out[at + i] = this.lightPacked(x0 + i, y, z);
    },
  };

  /**
   * What light reaches this cell, sky in the high nibble and torch in the low.
   *
   * **An absent chunk reads as daylight.** A chunk of pure air is never
   * stored, and in a heightmap world every such chunk is above the ground -
   * so open sky is the right answer and costs nothing to keep. The one place
   * it could lie is a cave big enough to fill a whole chunk with air, which
   * this terrain does not make. Minecraft answers the same question with a
   * per-chunk heightmap, and that is the upgrade if caves ever get that big.
   */
  private lightPacked(x: number, y: number, z: number): number {
    /*
     * The hill is lit through, floor to summit and inside the cube.
     *
     * It is a hundred-block block of вибухоблок and the way up is often a
     * tunnel through the middle of it - which, lit honestly, is a black
     * corridor with nothing in it to see by, on a map with no torches and
     * nothing to make one from. A race nobody can see is not a race, so the
     * whole world is daylight and none of the walk below runs at all.
     */
    if (this.opts.terrain === "hill") return MAX_LIGHT << 4;
    if (y >= this.opts.sizeY * CHUNK_SIZE) return MAX_LIGHT << 4;
    if (y < 0) return 0;
    /*
     * Outside the map is dark, not daylight.
     *
     * The mesher reads one block past every edge, and answering "open sky"
     * there lights the outermost faces of the world from the side - which is
     * how the margin beyond the edge came out at full brightness the first
     * time this moved into the chunks.
     */
    const limit = this.opts.sizeXZ * CHUNK_SIZE;
    if (x < 0 || z < 0 || x >= limit || z >= limit) return 0;
    const cx = floorDiv(x, CHUNK_SIZE);
    const cz = floorDiv(z, CHUNK_SIZE);
    const chunk = this.chunks.get(key(cx, floorDiv(y, CHUNK_SIZE), cz));
    if (chunk === undefined) return MAX_LIGHT << 4;
    if (chunk.light === null) this.lightPatch(cx, cz);
    return chunk.light?.[
      blockIndex(mod(x, CHUNK_SIZE), mod(y, CHUNK_SIZE), mod(z, CHUNK_SIZE))
    ] ?? 0;
  }

  /**
   * Scratch for lighting one column: is this cell opaque, and the field.
   *
   * Kept between calls rather than allocated per column - this runs for
   * every column anybody looks at, and two megabytes of fresh arrays each
   * time is how a lazy world becomes a stuttering one.
   *
   * It used to be a map of the *whole world*, one byte a cell, beside a
   * light field of the same size. Those two arrays are what capped how big a
   * world could be.
   */
  private scratchOpaque: Uint8Array | null = null;
  private scratchLight: LightField | null = null;
  private scratchWide = 0;
  private scratchDeep = 0;

  /**
   * Every смолоскип in the world, so the light walk need not go looking.
   *
   * Kept up to date by setBlock **and** rebuilt from the blocks themselves
   * whenever the world is lit from scratch. The second half is not belt and
   * braces: a save is loaded by writing whole chunks straight into the world,
   * which never goes through setBlock, so a world that came off disk had
   * torches in it that this list had never heard of. They stood there burning
   * and lit nothing - which is exactly what it looked like.
   */
  private readonly torches = new Map<string, Emitter>();

  /**
   * Every orc lair still standing.
   *
   * Kept as a list rather than found by scanning: the HUD wants the count on
   * every frame, and a scan of four and a half million cells is not something
   * to do sixty times a second. setBlock keeps it honest - the same choke
   * point that empties a chest when the chest stops being one.
   */
  private readonly lairs = new Map<string, Cell>();

  /**
   * Everything below this fills with water.
   *
   * Held on the world rather than computed inside generate(), because water
   * finding its level after an edit needs the same number.
   */
  /**
   * The middle of each island, in blocks.
   *
   * Two of them: one the players wake on, one the orcs come from. Held on the
   * world because everything that has to *put* something somewhere needs them
   * - where a cossack spawns, where a spawner goes - and working them out
   * again from the seed in three places is how they come to disagree.
   */
  readonly islands: readonly { x: number; z: number; radius: number }[];

  readonly seaLevel: number;

  constructor(opts: WorldGenOptions) {
    this.opts = opts;

    /*
     * Where the water stands, and so how far there is to dig.
     *
     * Forty-two hundredths rather than thirty-five, on a taller world: what is
     * *under* the sea is where the game happens once there are ores to find
     * and shafts to sink, and a seabed you reach in fifteen blocks is not a
     * mine, it is a hole.
     *
     * Before the islands, because on an adventure where people arrive is
     * worked out from the shape of the country and the country needs to know
     * where its waterline is.
     */
    this.seaLevel = Math.floor(opts.sizeY * CHUNK_SIZE * 0.42);

    // Laid out along X and centred in Z, a quarter of the way in from each
    // end, which leaves each of them room to be an island and the middle to be
    // the sea between.
    const span = opts.sizeXZ * CHUNK_SIZE;
    // A quarter of the span each would put their edges exactly together at the
    // middle - which is what happened, and gave one continuous landmass with a
    // dent in it. Seventeen hundredths leaves open water between them.
    const radius = span * 0.17;
    /*
     * On a hill world there is one "island", and it is not land.
     *
     * `islands[0]` is what everything that has to *put* somebody somewhere
     * reads - a spawn, a herd, a wave coming ashore. A cube world has no
     * coastline, but it still has a place people should arrive: the floor
     * beside the hill, not the top of it. Putting it in the middle would
     * spawn every arrival on the summit already crowned.
     *
     * There is no second one, so `landRaid` finds no far island and lands no
     * waves - which is right: this map has no orcs in it.
     */
    /*
     * An adventure has one "island" and it is the whole continent.
     *
     * `islands[0]` is what everything that has to put somebody somewhere
     * reads - a spawn, a herd. There are no islands here, so it is the
     * nearest water to the middle of the map instead of the middle itself:
     * lakes cover about two hundredths of the country, so four seeds in five
     * put a player down with nothing to fish in within a hundred and thirty
     * blocks, and людина сідає біля води. `findSpawn` walks outward from
     * whatever it is given until it finds dry ground, so pointing it at a
     * lake puts the start on the shore of one.
     */
    this.islands = opts.terrain === "adventure"
      ? [{
        /*
         * Near the middle, and near water, in that order.
         *
         * The reach was three tenths of the map, which was three hundred
         * blocks when the map was a thousand across and is twelve hundred
         * now - so on a dry seed the start could wander most of the way to
         * the coast. Four hundred, flat: "гравець нехай спавниться недалеко
         * від центру", and a lake further off than that is a lake to walk
         * to rather than a reason to start somewhere else. With none inside
         * it, `waterNear` hands back the middle itself.
         */
        ...this.waterNear(span * 0.5, span * 0.5, Math.min(span * 0.3, 400)),
        radius: span * 0.35,
      }]
      : opts.terrain === "hill"
      ? [{ x: span * 0.5 - HILL_SPAN / 2 - 6, z: span * 0.5, radius: 4 }]
      : [
        { x: span * 0.26, z: span * 0.5, radius },
        { x: span * 0.74, z: span * 0.5, radius },
      ];
  }

  /**
   * The nearest lake to a place, asked of the noise rather than of the world.
   *
   * Nothing is generated to answer this: a column's ground and its water
   * table both come straight out of the seed, so "is there water here" is
   * two noise lookups rather than a column built and thrown away. Which
   * matters, because this is asked of a few thousand places at once and
   * building them all would be ten seconds before the title screen.
   *
   * Coarse on purpose - a lake smaller than the step is missed, and a lake
   * that small is a puddle.
   *
   * @returns the water it found, or the place it was asked about if the
   * country round there is dry.
   */
  private waterNear(x: number, z: number, reach: number): { x: number; z: number } {
    const step = 6;
    for (let ring = 1; ring * step <= reach; ring++) {
      for (let dz = -ring; dz <= ring; dz++) {
        for (let dx = -ring; dx <= ring; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== ring) continue;
          const at = { x: Math.round(x + dx * step), z: Math.round(z + dz * step) };
          if (!this.inColumnBounds(
            floorDiv(at.x, CHUNK_SIZE), floorDiv(at.z, CHUNK_SIZE))) continue;
          // Two blocks of water, so it is a lake rather than a wet patch,
          // above the sea, so it is not simply the coast, and wet four
          // blocks out in every direction, so it is a lake you could put a
          // boat on rather than a puddle you could stand in.
          if (!this.wetEnough(at.x, at.z, false)) continue;
          if (!this.wetEnough(at.x + 4, at.z, false) ||
            !this.wetEnough(at.x - 4, at.z, false)) continue;
          if (!this.wetEnough(at.x, at.z + 4, false) ||
            !this.wetEnough(at.x, at.z - 4, false)) continue;
          /*
           * And then properly, for the handful that get this far.
           *
           * The cheap test asks the column's own water table, which is where
           * the water would stand if nothing were beside it. Most of what
           * that calls a lake drains once it is allowed to settle against
           * its neighbours - so a start chosen on the cheap answer alone put
           * the player beside a dry hollow. Asking properly costs five times
           * as much and is asked of perhaps a dozen places.
           */
          if (!this.wetEnough(at.x, at.z, true)) continue;
          return at;
        }
      }
    }
    return { x, z };
  }

  /** Is this column under two blocks of water that is not the sea? */
  private wetEnough(x: number, z: number, settled: boolean): boolean {
    // The raw table is a fifth of the cost and is asked of thousands of
    // places; the settled level is the truth and is asked of a dozen.
    const cell = this.adventureCell(x, z);
    const table = settled ? this.adventureLevel(x, z) : cell >>> 20;
    return table > this.seaLevel + 1 && table - (cell & 0xffff) >= 2;
  }

  getChunk(cx: number, cy: number, cz: number): Chunk | undefined {
    return this.chunks.get(key(cx, cy, cz));
  }

  allChunks(): IterableIterator<Chunk> {
    return this.chunks.values();
  }

  /** World-space block read. Out of bounds reads as air. */
  getBlock = (x: number, y: number, z: number): number => {
    const cx = floorDiv(x, CHUNK_SIZE);
    const cy = floorDiv(y, CHUNK_SIZE);
    const cz = floorDiv(z, CHUNK_SIZE);
    const chunk = this.chunks.get(key(cx, cy, cz));
    if (chunk === undefined) return BlockId.Air;
    return chunk.get(mod(x, CHUNK_SIZE), mod(y, CHUNK_SIZE), mod(z, CHUNK_SIZE));
  };

  /** Are these chunk coordinates inside the generated world? */
  private inBounds(cx: number, cy: number, cz: number): boolean {
    const { sizeXZ, sizeY } = this.opts;
    return (
      cx >= 0 && cx < sizeXZ &&
      cz >= 0 && cz < sizeXZ &&
      cy >= 0 && cy < sizeY
    );
  }

  /**
   * Fetch a chunk, creating an empty one if it is in bounds but absent.
   *
   * Chunks that generate as pure air are never stored, so placing a block in
   * open sky has to be able to bring one into existence.
   */
  private ensureChunk(cx: number, cy: number, cz: number): Chunk | undefined {
    const k = key(cx, cy, cz);
    const existing = this.chunks.get(k);
    if (existing !== undefined) return existing;
    if (!this.inBounds(cx, cy, cz)) return undefined;
    const created = new Chunk(cx, cy, cz);
    this.chunks.set(k, created);
    return created;
  }

  /** World-space block write. Marks the chunk and any touched neighbour dirty. */
  setBlock(x: number, y: number, z: number, id: number): void {
    const cx = floorDiv(x, CHUNK_SIZE);
    const cy = floorDiv(y, CHUNK_SIZE);
    const cz = floorDiv(z, CHUNK_SIZE);
    const chunk = this.ensureChunk(cx, cy, cz);
    if (chunk === undefined) return;

    const lx = mod(x, CHUNK_SIZE);
    const ly = mod(y, CHUNK_SIZE);
    const lz = mod(z, CHUNK_SIZE);

    /*
     * A chest that stops being a chest loses what it held.
     *
     * This is the only place that can notice, and it has to be here rather
     * than in the code that breaks blocks: water, a falling plant, an import,
     * anything at all that overwrites the cell has the same consequence. Left
     * out, a chest broken and replaced in the same spot hands back the
     * previous owner's things.
     *
     * Guarded on there being any chests at all, because setBlock is also the
     * hot path for ore seams and flood fill.
     */
    if (this.containers.size > 0 || this.furnaces.size > 0 || this.lairs.size > 0) {
      const was = chunk.get(lx, ly, lz);
      if (isChest(was) && !isChest(id)) this.containers.clear(x, y, z);
      // A stove turned to face another way is still that stove, and must keep
      // its fire - the same rule the chests have, and the same trap.
      if (isStove(was) && !isStove(id)) this.furnaces.clear(x, y, z);
      // A lair that stops being a lair is one the players have broken, which
      // is the only way this game is won.
      if (was === BlockId.OrcSpawner && id !== BlockId.OrcSpawner) {
        this.lairs.delete(cellKey({ x, y, z }));
      }
    }
    /*
     * A lair that appears is registered, not only one that goes.
     *
     * This used to delete and never add, because the only lairs there were
     * had been planted during generation. Now a кремль brings its own, and a
     * register that only ever shrinks would have meant raids that never came
     * from a fortress full of lairs.
     */
    if (id === BlockId.OrcSpawner) {
      this.lairs.set(cellKey({ x, y, z }), { x, y, z });
    }

    // What the light cares about, decided before the write.
    const wasId = chunk.get(lx, ly, lz);
    const wasOpaque = isOpaque(wasId);
    const wasGlow = emission(wasId);
    const isGlow = emission(id);

    const before = chunk.version;
    chunk.set(lx, ly, lz, id);
    // Only record a real change - setting a block to what it already was must
    // not grow the save.
    if (chunk.version !== before) this.modified.add(key(cx, cy, cz));

    // Light, but only when this block could have changed any. Water spreading
    // through a cavern is a hundred setBlocks and not one of them casts or
    // stops a ray, so this guard is the difference between an edit costing
    // 2.5 ms and a flood costing a quarter of a second.
    if (this.inWorld(x, y, z) && (wasOpaque !== isOpaque(id) || wasGlow !== isGlow)) {
      // The torch list is kept whether or not anything has asked for light
      // yet: it is what the first full pass will be seeded from.
      const at = cellKey({ x, y, z });
      if (isGlow > 0) this.torches.set(at, { x, y, z, level: isGlow });
      else this.torches.delete(at);
      this.relightAround(x, y, z);
    }

    // A block on a chunk face changes what the neighbour should cull.
    if (lx === 0) this.markDirty(cx - 1, cy, cz);
    if (lx === CHUNK_SIZE - 1) this.markDirty(cx + 1, cy, cz);
    if (ly === 0) this.markDirty(cx, cy - 1, cz);
    if (ly === CHUNK_SIZE - 1) this.markDirty(cx, cy + 1, cz);
    if (lz === 0) this.markDirty(cx, cy, cz - 1);
    if (lz === CHUNK_SIZE - 1) this.markDirty(cx, cy, cz + 1);
  }

  /** Is this cell inside the map at all? */
  withinBounds(x: number, y: number, z: number): boolean {
    return this.inWorld(x, y, z);
  }

  private inWorld(x: number, y: number, z: number): boolean {
    const span = this.opts.sizeXZ * CHUNK_SIZE;
    const high = this.opts.sizeY * CHUNK_SIZE;
    return x >= 0 && z >= 0 && y >= 0 && x < span && z < span && y < high;
  }

  /**
   * Fill the opacity map and light the world.
   *
   * Reads chunk arrays directly rather than going through getBlock: this
   * touches every cell in the world, and that is exactly the shape of loop
   * that has cost this project a second of load time before.
   */
  /**
   * Light one column of chunks, from scratch, needing nothing outside it.
   *
   * **Why a column can be lit alone.** Light dies after fifteen steps and the
   * box below is padded by a whole chunk - sixteen - on every side, so
   * anything that could reach the middle is inside the box and anything
   * outside could not have arrived whatever its value. Sky comes from above
   * and the box is the full height of the world. Nothing has to be seeded
   * from the neighbours, and that is the whole trick: a column is correct on
   * its own, so it can be lit when somebody walks up to it rather than when
   * the world is made.
   *
   * The padding is computed and thrown away - a box nine times the column's
   * own footprint. That is the price of a world that need not exist all at
   * once, and it is the same price Minecraft pays.
   */
  /**
   * Light the column somebody asked for, and its unlit neighbours with it.
   *
   * Every box carries a one-chunk skirt that is computed and thrown away, so
   * a single column costs nine columns' worth of work. Lighting a patch at a
   * time spreads that skirt over twenty-five columns instead of one, and a
   * player who looks at one column is about to look at the ones beside it.
   *
   * Measured on the кремль map: every column one at a time is 2091ms, the
   * whole map in one box is 304ms, and this patch is what buys most of that
   * difference back without giving up a world that lights as it is needed.
   *
   * The patch is shrunk to the columns that are actually dark. Relighting a
   * lit column gives the same answer - the light of a column depends only on
   * the blocks around it - but it is work nobody asked for.
   */
  /**
   * Scratch for relighting around a change, kept between blocks dug.
   *
   * Separate from the column scratch because the two are different shapes:
   * this one is a small cube and that one is the height of the world.
   *
   * Kept by height rather than as a single buffer: the box is tall when the
   * change is under open sky and short when it is not, and a floor laid down
   * block by block alternates between them. Throwing away a hundred kilobytes
   * and allocating another for every block placed cost fifteen seconds in one
   * check of the water alone.
   */
  /**
   * How far generation has got in each column, for a world grown lazily.
   *
   * Empty for the maps that are made all at once - the кремль and the hill
   * both exist in full the moment they are asked for, and nothing consults
   * this for them.
   */
  private readonly stages = new Map<string, number>();

  /** Surfaces worked out lately, by column. See `adventureSurface`. */
  private readonly lastSurface = new Map<string, {
    heights: Int16Array; biomes: Uint8Array; tables: Int16Array;
  }>();

  private readonly relit = new Map<number, {
    field: LightField; opaque: Uint8Array; lit: Uint8Array;
  }>();

  private lightPatch(cx: number, cz: number): void {
    const last = this.opts.sizeXZ - 1;
    let x0 = Math.max(0, cx - LIGHT_PATCH);
    let x1 = Math.min(last, cx + LIGHT_PATCH);
    let z0 = Math.max(0, cz - LIGHT_PATCH);
    let z1 = Math.min(last, cz + LIGHT_PATCH);
    let lowX = x1, highX = x0, lowZ = z1, highZ = z0;
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        if (this.columnLit(x, z)) continue;
        if (x < lowX) lowX = x;
        if (x > highX) highX = x;
        if (z < lowZ) lowZ = z;
        if (z > highZ) highZ = z;
      }
    }
    if (lowX > highX) return;
    this.lightColumns(lowX, lowZ, highX, highZ);
  }

  /** Has anybody asked for this column's light yet? */
  private columnAsked(cx: number, cz: number): boolean {
    for (let cy = 0; cy < this.opts.sizeY; cy++) {
      if (this.chunks.get(key(cx, cy, cz))?.light != null) return true;
    }
    return false;
  }

  /** Does every chunk of this column already know its light? */
  private columnLit(cx: number, cz: number): boolean {
    for (let cy = 0; cy < this.opts.sizeY; cy++) {
      const chunk = this.chunks.get(key(cx, cy, cz));
      if (chunk !== undefined && chunk.light === null) return false;
    }
    return true;
  }

  /**
   * Light a block of columns at once, and say which chunks actually changed.
   *
   * One box for the lot rather than one box each: nine columns lit one at a
   * time is nine overlapping boxes and nine times the work, when a single box
   * with the same one-chunk skirt covers all of them.
   *
   * The changed chunks are what comes back because that is what has to be
   * redrawn, and "everything nearby" is the wrong answer - each redraw is a
   * GPU buffer thrown away and made again. A dig that touched nothing
   * visible should cost nothing, and that is the common case.
   */
  private lightColumns(
    cx0: number, cz0: number, cx1: number, cz1: number,
  ): Chunk[] {
    const high = this.opts.sizeY * CHUNK_SIZE;
    const wide = (cx1 - cx0 + 3) * CHUNK_SIZE;
    const deep = (cz1 - cz0 + 3) * CHUNK_SIZE;
    const x0 = (cx0 - 1) * CHUNK_SIZE;
    const z0 = (cz0 - 1) * CHUNK_SIZE;

    /*
     * Scratch, kept between calls and grown when it has to be.
     *
     * This runs for every column anybody looks at and for every block
     * anybody digs; allocating megabytes each time is how a lazy world
     * becomes a stuttering one.
     */
    if (this.scratchLight === null || this.scratchWide !== wide ||
      this.scratchDeep !== deep) {
      this.scratchLight = new LightField(wide, high, deep);
      this.scratchOpaque = new Uint8Array(wide * high * deep);
      this.scratchWide = wide;
      this.scratchDeep = deep;
    }
    const opaque = this.scratchOpaque!;
    const field = this.scratchLight;
    const emitters: Emitter[] = [];

    /*
     * Filled from the chunk arrays, not by asking the world cell by cell.
     *
     * This is the loop the old whole-world version had a comment about, and
     * writing it the obvious way cost exactly what that comment said it
     * would: a quarter of a million `getBlock` calls - a map lookup and an
     * index each - to light one column. Walking the chunks that exist and
     * writing their cells straight in does the same work without the
     * lookups, and skips whole chunks of air for free.
     *
     * Outside the world is left as stone, so a column at the edge of the map
     * is not lit from the side by a sun shining through the wall of it -
     * hence the fill rather than a clear.
     */
    opaque.fill(1);
    for (let cy = 0; cy < this.opts.sizeY; cy++) {
      for (let ccz = cz0 - 1; ccz <= cz1 + 1; ccz++) {
        for (let ccx = cx0 - 1; ccx <= cx1 + 1; ccx++) {
          if (!this.inBounds(ccx, cy, ccz)) continue;
          const lx0 = ccx * CHUNK_SIZE - x0;
          const lz0 = ccz * CHUNK_SIZE - z0;
          const chunk = this.chunks.get(key(ccx, cy, ccz));
          if (chunk === undefined) {
            // A chunk that was never stored is pure air: clear, not stone.
            for (let y = 0; y < CHUNK_SIZE; y++) {
              const row = (cy * CHUNK_SIZE + y) * deep;
              for (let z = 0; z < CHUNK_SIZE; z++) {
                opaque.fill(0, (row + lz0 + z) * wide + lx0,
                  (row + lz0 + z) * wide + lx0 + CHUNK_SIZE);
              }
            }
            continue;
          }
          const blocks = chunk.blocks;
          for (let y = 0; y < CHUNK_SIZE; y++) {
            const wy = cy * CHUNK_SIZE + y;
            for (let z = 0; z < CHUNK_SIZE; z++) {
              const out = (wy * deep + lz0 + z) * wide + lx0;
              for (let x = 0; x < CHUNK_SIZE; x++) {
                const id = blocks[blockIndex(x, y, z)]!;
                opaque[out + x] = isOpaque(id) ? 1 : 0;
                const gives = emission(id);
                if (gives > 0) {
                  emitters.push({ x: lx0 + x, y: wy, z: lz0 + z, level: gives });
                }
              }
            }
          }
        }
      }
    }
    field.computeAll(opaque, emitters);

    // And keep only the middle, which is the part that was worth computing.
    const moved: Chunk[] = [];
    for (let cz = cz0; cz <= cz1; cz++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        for (let cy = 0; cy < this.opts.sizeY; cy++) {
          const chunk = this.chunks.get(key(cx, cy, cz));
          if (chunk === undefined) continue;
          const light = chunk.light ?? new Uint8Array(CHUNK_SIZE ** 3);
          const fresh = chunk.light === null;
          let changed = fresh;
          for (let y = 0; y < CHUNK_SIZE; y++) {
            for (let z = 0; z < CHUNK_SIZE; z++) {
              for (let x = 0; x < CHUNK_SIZE; x++) {
                const was = field.packed(
                  (cx - cx0 + 1) * CHUNK_SIZE + x, cy * CHUNK_SIZE + y,
                  (cz - cz0 + 1) * CHUNK_SIZE + z);
                const i = blockIndex(x, y, z);
                if (light[i] !== was) { light[i] = was; changed = true; }
              }
            }
          }
          chunk.light = light;
          if (changed && !fresh) moved.push(chunk);
        }
      }
    }
    return moved;
  }

  /** Throw away a column's light, so it is worked out again when asked for. */
  private forgetLight(cx: number, cz: number): void {
    for (let cy = 0; cy < this.opts.sizeY; cy++) {
      const chunk = this.chunks.get(key(cx, cy, cz));
      if (chunk !== undefined) chunk.light = null;
    }
  }

  /**
   * Redo the light around one changed cell, and redraw what it touched.
   *
   * Only called when the change could possibly matter: water spreading a
   * hundred cells is a hundred setBlocks, and relighting a box for each of
   * them would cost a quarter of a second for a change no light can see.
   */
  private relightAround(x: number, y: number, z: number): void {
    /*
     * A box around the block, not the columns it stands in.
     *
     * Relighting the three-by-three columns is correct and costs twenty-six
     * milliseconds a block: a column is the full height of the world, and
     * nine of them is eight hundred thousand cells walked to move light that
     * reached fifteen. Chopping a house down at that price is a frozen tab,
     * and it is what made the whole check suite take ten minutes.
     *
     * So: copy the neighbourhood into a scratch field, redo the box in the
     * middle of it, copy back what moved. The ring of cells around the box
     * keeps the light it already had, which is what the flood seeds itself
     * from - that ring is the whole trick, and why this gives the same answer
     * as lighting the column from scratch.
     */
    /*
     * Nothing to patch where nobody has looked.
     *
     * A column works out its light the first time somebody reads a cell in
     * it, so a column still dark is a column whose light will be worked out
     * from the blocks as they stand by then - patching it now is work for an
     * answer that gets thrown away. This is what lets a building go up, or a
     * check lay a floor block by block, without lighting anything: laying a
     * forty-eight square floor was twelve milliseconds before the light moved
     * into the chunks and nineteen seconds after, and this line is the whole
     * difference.
     */
    if (!this.columnAsked(floorDiv(x, CHUNK_SIZE), floorDiv(z, CHUNK_SIZE))) return;

    const high = this.opts.sizeY * CHUNK_SIZE;
    const wide = 2 * LIGHT_RADIUS + 3;
    const x0 = x - LIGHT_RADIUS - 1;
    const z0 = z - LIGHT_RADIUS - 1;
    const y1 = Math.min(high, y + LIGHT_RADIUS + 2);
    /*
     * How far down the box has to reach, rounded to a chunk.
     *
     * Rounding is what keeps the scratch reusable: the exact depth changes
     * with every block, a chunk's worth of it changes rarely, and a handful
     * of buffers then serves the whole session.
     */
    const want = y1 - Math.max(0, y - this.sunReach(x, y, z) - LIGHT_RADIUS - 1);
    const y0 = Math.max(0, y1 - Math.ceil(want / CHUNK_SIZE) * CHUNK_SIZE);
    const tall = y1 - y0;

    let scratch = this.relit.get(tall);
    if (scratch === undefined) {
      scratch = {
        field: new LightField(wide, tall, wide),
        opaque: new Uint8Array(wide * tall * wide),
        lit: new Uint8Array(wide * tall * wide),
      };
      this.relit.set(tall, scratch);
    }
    const { field, opaque, lit } = scratch;

    /*
     * Every column the box touches has to know its light before we copy it,
     * and asking once a column is not the same as asking once a cell: the
     * cell-by-cell version of this loop was forty thousand map lookups a
     * block dug. The same mistake as the one the old whole-world lighting
     * left a comment about, in a smaller box.
     */
    const cxA = floorDiv(x0, CHUNK_SIZE), cxB = floorDiv(x0 + wide - 1, CHUNK_SIZE);
    const czA = floorDiv(z0, CHUNK_SIZE), czB = floorDiv(z0 + wide - 1, CHUNK_SIZE);
    for (let cz = czA; cz <= czB; cz++) {
      for (let cx = cxA; cx <= cxB; cx++) {
        if (cx < 0 || cz < 0) continue;
        if (cx >= this.opts.sizeXZ || cz >= this.opts.sizeXZ) continue;
        if (!this.columnLit(cx, cz)) this.lightPatch(cx, cz);
      }
    }

    /**
     * Everything in the box that makes its own light, found while the blocks
     * are being read. See where it is filled in.
     */
    const emitters: Emitter[] = [];

    // Outside the map is stone and dark, as everywhere else in the lighting:
    // a sun must not shine in through the wall of the world.
    opaque.fill(1);
    lit.fill(0);
    for (let cz = czA; cz <= czB; cz++) {
      for (let cx = cxA; cx <= cxB; cx++) {
        for (let cy = floorDiv(y0, CHUNK_SIZE);
          cy <= floorDiv(y1 - 1, CHUNK_SIZE); cy++) {
          if (!this.inBounds(cx, cy, cz)) continue;
          const chunk = this.chunks.get(key(cx, cy, cz));
          const bx = cx * CHUNK_SIZE, by = cy * CHUNK_SIZE, bz = cz * CHUNK_SIZE;
          const fromX = Math.max(x0, bx), toX = Math.min(x0 + wide, bx + CHUNK_SIZE);
          const fromY = Math.max(y0, by), toY = Math.min(y1, by + CHUNK_SIZE);
          const fromZ = Math.max(z0, bz), toZ = Math.min(z0 + wide, bz + CHUNK_SIZE);
          for (let wy = fromY; wy < toY; wy++) {
            for (let wz = fromZ; wz < toZ; wz++) {
              const out = ((wy - y0) * wide + (wz - z0)) * wide - x0;
              for (let wx = fromX; wx < toX; wx++) {
                if (chunk === undefined) {
                  // A chunk nobody stored is open air under open sky.
                  opaque[out + wx] = 0;
                  lit[out + wx] = MAX_LIGHT << 4;
                  continue;
                }
                const i = blockIndex(wx - bx, wy - by, wz - bz);
                const id = chunk.blocks[i]!;
                opaque[out + wx] = isOpaque(id) ? 1 : 0;
                lit[out + wx] = chunk.light?.[i] ?? 0;
                /*
                 * And anything in the box that makes its own light.
                 *
                 * Read off the blocks rather than out of the torch list. The
                 * list is kept by `setBlock`, and лава is not put down by
                 * anybody - it is generated - so no лава has ever been in
                 * it. Breaking a block anywhere near a pool relit the box
                 * with no лава in it and the glow went out: "вона перестає
                 * світитись якщо зламати блок біля неї".
                 *
                 * The same loop was already reading every block in the box,
                 * so this costs an `emission` call per cell and cannot go
                 * stale the way a list can.
                 */
                const glow = emission(id);
                if (glow > 0) {
                  emitters.push({ x: wx - x0, y: wy - y0, z: wz - z0, level: glow });
                }
              }
            }
          }
        }
      }
    }
    field.load(lit);

    // The box redone is the field less its seed ring - except where the ring
    // would fall outside the world, which has no light to seed from anyway.
    const moved = field.relightBox(opaque, emitters, {
      x0: 1, z0: 1, x1: wide - 1, z1: wide - 1,
      y0: y0 === 0 ? 0 : 1,
      y1: y1 === high ? tall : tall - 1,
    });
    if (moved === null) return;

    /*
     * Only the chunks whose light actually differs are redrawn. Marking the
     * whole neighbourhood dirty would be seventy-two GPU buffers thrown away
     * for one block dug, which is what the first version of this did and what
     * the check about redrawing a handful is there to catch.
     */
    const touched = new Set<string>();
    for (let ly = moved.y0; ly < moved.y1; ly++) {
      for (let lz = moved.z0; lz < moved.z1; lz++) {
        for (let lx = moved.x0; lx < moved.x1; lx++) {
          const wx = x0 + lx, wy = y0 + ly, wz = z0 + lz;
          const cx = floorDiv(wx, CHUNK_SIZE);
          const cy = floorDiv(wy, CHUNK_SIZE);
          const cz = floorDiv(wz, CHUNK_SIZE);
          const chunk = this.chunks.get(key(cx, cy, cz));
          if (chunk?.light == null) continue;
          const i = blockIndex(
            mod(wx, CHUNK_SIZE), mod(wy, CHUNK_SIZE), mod(wz, CHUNK_SIZE));
          const now = field.packed(lx, ly, lz);
          if (chunk.light[i] === now) continue;
          chunk.light[i] = now;
          touched.add(key(cx, cy, cz));
        }
      }
    }
    for (const at of touched) {
      const chunk = this.chunks.get(at);
      if (chunk !== undefined) this.markDirty(chunk.cx, chunk.cy, chunk.cz);
    }
  }

  /**
   * How far a sunbeam could fall below this cell before something stops it.
   *
   * Taking the roof off a sealed shaft lights it to the bottom, however deep
   * it is; sealing one darkens the same. Either way the box that has to be
   * redone reaches as far down as the air does, and asking costs one read a
   * block - nothing next to relighting a cell that did not need it.
   */
  private sunReach(x: number, y: number, z: number): number {
    let fell = 0;
    for (let wy = y - 1; wy >= 0; wy--) {
      if (isOpaque(this.getBlock(x, wy, z))) break;
      fell++;
    }
    return fell;
  }

  private markDirty(cx: number, cy: number, cz: number): void {
    this.chunks.get(key(cx, cy, cz))?.touch();
  }

  /**
   * Write many blocks at once.
   *
   * `setBlock` relights around every cell it changes. That is right for one
   * block dug out of a hillside and catastrophic for a building: a кремль is
   * some twenty thousand blocks, and twenty thousand patches at 2.5 ms each is
   * fifty seconds of a frozen tab. It is also wasted work, since each patch is
   * immediately invalidated by the next block placed beside it.
   *
   * So the writes go straight into the chunk arrays, and the light is thrown
   * away and rebuilt once - a single 166 ms pass over the whole world, which
   * is less than one per-cell patch would have cost twelve times over.
   *
   * Everything else `setBlock` does still has to happen, and forgetting any of
   * it is a quiet bug rather than a loud one: chests and stoves buried by a
   * building must give up what they held, and every chunk touched *and its
   * neighbours* must be remeshed or the walls have holes along the seams.
   *
   * @returns how many cells actually changed.
   */
  setBlocks(cells: Iterable<{ x: number; y: number; z: number; id: number }>): number {
    let written = 0;
    const dirty = new Set<string>();

    for (const { x, y, z, id } of cells) {
      const cx = floorDiv(x, CHUNK_SIZE);
      const cy = floorDiv(y, CHUNK_SIZE);
      const cz = floorDiv(z, CHUNK_SIZE);
      const chunk = this.ensureChunk(cx, cy, cz);
      if (chunk === undefined) continue;

      const lx = mod(x, CHUNK_SIZE);
      const ly = mod(y, CHUNK_SIZE);
      const lz = mod(z, CHUNK_SIZE);

      // The same rule setBlock has, and for the same reason: this is the only
      // place that can notice a chest has stopped being a chest.
      if (this.containers.size > 0 || this.furnaces.size > 0 || this.lairs.size > 0) {
        const was = chunk.get(lx, ly, lz);
        if (isChest(was) && !isChest(id)) this.containers.clear(x, y, z);
        if (isStove(was) && !isStove(id)) this.furnaces.clear(x, y, z);
        if (was === BlockId.OrcSpawner && id !== BlockId.OrcSpawner) {
          this.lairs.delete(cellKey({ x, y, z }));
        }
      }
      if (id === BlockId.OrcSpawner) {
        this.lairs.set(cellKey({ x, y, z }), { x, y, z });
      }

      const before = chunk.version;
      chunk.set(lx, ly, lz, id);
      if (chunk.version === before) continue;
      written++;
      this.modified.add(key(cx, cy, cz));

      // Neighbours as well as the chunk itself: a block on a chunk face
      // changes what the chunk next door is allowed to cull away.
      for (let dz = -1; dz <= 1; dz++) {
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            dirty.add(key(cx + dx, cy + dy, cz + dz));
          }
        }
      }
    }

    if (written === 0) return 0;
    for (const k of dirty) this.chunks.get(k)?.touch();
    /*
     * Drop the light rather than patch it.
     *
     * `lit = false` is what a chunk load does, and it is the one honest way
     * to say "everything I knew about light is now wrong": the next read
     * rebuilds the field and the torch list from the blocks as they stand,
     * which is exactly what a building full of new смолоскипи needs.
     */
    /*
     * Everything known about light here is now wrong.
     *
     * It used to be one flag over the world; the light lives in the chunks
     * now, so this drops it chunk by chunk and the next read works it out
     * from the blocks as they actually stand - which is exactly what a
     * building full of new смолоскипи needs.
     */
    for (const chunk of this.chunks.values()) chunk.light = null;
    return written;
  }

  /**
   * Generate the phase-0 test island.
   *
   * Writes straight into the chunk's backing array rather than going through
   * `setBlock` - millions of calls with bounds maths would be needlessly slow,
   * so we bulk-fill and `recount()` afterwards.
   */
  generate(): void {
    if (this.opts.terrain === "hill") { this.generateHill(); return; }
    if (this.opts.terrain === "war") { this.generateWar(); return; }
    if (this.opts.terrain === "adventure") { this.generateAdventure(); return; }
    if (this.opts.terrain === "one") { this.generateOneBlock(); return; }
    const { seed, sizeXZ, sizeY } = this.opts;
    const worldHeight = sizeY * CHUNK_SIZE;
    const seaLevel = this.seaLevel;

    // Heightmap first - one noise lookup per column, not per block.
    const spanXZ = sizeXZ * CHUNK_SIZE;
    const heights = new Int16Array(spanXZ * spanXZ);
    for (let z = 0; z < spanXZ; z++) {
      for (let x = 0; x < spanXZ; x++) {
        const n = fbm2(x / 48, z / 48, seed, 4);
        const detail = fbm2(x / 12, z / 12, seed + 77, 3) * 0.15;

        /*
         * Land where the islands are, sea everywhere else.
         *
         * `land` runs 0 in open water to 1 in the middle of an island, and the
         * height is two things added: the noise, scaled down to nothing at the
         * shore so the coast is not a cliff of hills cut off mid-slope, and a
         * slab that lifts the land clear of the water and drops the seabed
         * away from it.
         */
        const { land, neck } = this.landAt(x, z, spanXZ);
        /*
         * The shore has to be gentle, and the sea still deep.
         *
         * A straight line between them gives both a deep ocean and a coast
         * that climbs four blocks in a single column - which an orc, jumping
         * exactly one, can never get up. It would arrive at the beach of the
         * island it is raiding and stand there.
         *
         * The curve does both: near the waterline it is almost flat, and it
         * steepens with distance, so a beach is walkable and the bottom is
         * still twenty blocks down.
         */
        const t = land - 0.52;
        const shape = Math.sign(t) * SHORE_AMPLITUDE * Math.abs(t) ** SHORE_CURVE;
        /*
         * Relief on the land, and a little on the seabed too.
         *
         * Multiplying it all by `land` made the ocean floor perfectly flat -
         * one slab of sand at a single height, thirty-nine thousand blocks of
         * it, which is both dull to swim over and the reason the shoreline
         * ores had nothing to sit in. The constant term gives the bottom some
         * shape without giving the shore a cliff.
         */
        /*
         * Relief on the land, a little on the seabed, and almost none on the
         * causeway.
         *
         * The neck is a bar of land a few blocks above the water, and hills
         * of the same size as the island's would put most of it *under* the
         * water - which they did, and the crossing the orcs need was
         * submerged in the middle. It is a causeway, not a ridge.
         */
        /*
         * Hills fade out at the waterline as well as at the causeway.
         *
         * The shape curve alone gives a gentle beach and the noise then puts a
         * nine-block hill on it, which is a cliff at exactly the place it must
         * not be: a walker could cross the neck and then not get up the far
         * shore. Descents are free and climbs are not, so the far shore is the
         * one that matters, and it is the one that was failing.
         */
        const shoreEase = Math.min(1, Math.abs(t) / 0.18);
        const relief = (n - 0.45 + detail) * (26 * land + 7)
          * (1 - neck * 0.92) * shoreEase;
        const h = seaLevel + Math.round(shape + relief);
        heights[z * spanXZ + x] = Math.max(1, Math.min(worldHeight - 1, h));
      }
    }

    for (let cy = 0; cy < sizeY; cy++) {
      for (let cz = 0; cz < sizeXZ; cz++) {
        for (let cx = 0; cx < sizeXZ; cx++) {
          const chunk = new Chunk(cx, cy, cz);
          const blocks = chunk.blocks;

          for (let y = 0; y < CHUNK_SIZE; y++) {
            const wy = cy * CHUNK_SIZE + y;
            for (let z = 0; z < CHUNK_SIZE; z++) {
              const wz = cz * CHUNK_SIZE + z;
              for (let x = 0; x < CHUNK_SIZE; x++) {
                const wx = cx * CHUNK_SIZE + x;
                const surface = heights[wz * spanXZ + wx]!;
                if (wy > surface) {
                  // Everything above the ground and below sea level is water,
                  // so seas and lakes fall out of the heightmap for free.
                  if (wy <= seaLevel) blocks[blockIndex(x, y, z)] = BlockId.Water;
                  continue;
                }

                let id: BlockId;
                if (wy === surface || (wy > surface - 2 && surface < seaLevel - 1)) {
                  /*
                   * Clay lies in beds on the floor of a lake.
                   *
                   * It is the only place it occurs, which makes the first
                   * stove something you have to swim for. Patchy rather than
                   * universal, so a lake bed is worth searching rather than
                   * scraping anywhere.
                   */
                  const drowned = surface < seaLevel - 1;
                  const clayHere = drowned &&
                    fbm2(wx / 18, wz / 18, seed + 6607, 3) > 0.56;

                  id = clayHere ? BlockId.Clay
                    : wy !== surface ? BlockId.Sand
                    : surface <= seaLevel + 1 ? BlockId.Sand
                    : BlockId.Grass;
                } else if (wy > surface - 4) {
                  // A band of chornozem under the grass - the black earth.
                  id = surface > seaLevel + 8 ? BlockId.Dirt : BlockId.Chornozem;
                } else {
                  /*
                   * Clay lenses, right where the soil gives out and the rock
                   * begins - which is where they sit in the ground here.
                   *
                   * Lake beds were the only clay there was, and a player who
                   * settled inland had none at all: a measurement over the
                   * whole world found not one clay block under dry land, and
                   * without clay there is no піч, no bucket and no чан. That
                   * is the whole first half of the game behind a lake.
                   *
                   * Patchy on purpose. About a third of dry columns have a
                   * lens under them, so clay is something you find by digging
                   * two or three holes rather than something every hole
                   * yields - it should still be worth going to a lake for a
                   * lot of it at once.
                   */
                  const lensTop = surface - 4;
                  const inBand = wy <= lensTop && wy > lensTop - CLAY_LENS_DEPTH;
                  const dryLand = surface > seaLevel;
                  const lensHere = inBand && dryLand &&
                    fbm2(wx / 26, wz / 26, seed + 7717, 3) > CLAY_LENS_THRESHOLD;
                  id = lensHere ? BlockId.Clay : BlockId.Stone;
                }
                blocks[blockIndex(x, y, z)] = id;
              }
            }
          }

          chunk.recount();
          if (!chunk.isEmpty) this.chunks.set(key(cx, cy, cz), chunk);
        }
      }
    }

    this.scatterOres(spanXZ, worldHeight);
    this.layPeat(heights, spanXZ, seaLevel);
    // Ten rather than seventeen: see `pineLine`. The high ground here is
    // the fortress's, so сосна has to start lower or there is none at all.
    /*
     * Sixteen rather than seventeen, and the ceiling above lifted with it.
     *
     * Measured on the seed this game ships with: at seventeen the map grew
     * four pines - all of them on the dome the кремль is founded on, and the
     * fortress is raised after the trees and clears what it covers, so every
     * one of them went with it. Sixteen leaves fifteen or so standing
     * outside the walls, which is смола and соснові паростки and a reason to
     * go up the hill.
     */
    this.plantTrees(heights, spanXZ, seaLevel, worldHeight, false, 16, 0.4);
    this.scatterWildPlants(heights, spanXZ, seaLevel);
    /*
     * No lairs are planted any more.
     *
     * They used to be scattered over the far island, and finding them was the
     * campaign. They live in the кремль now - one in each of its buildings -
     * so the fortress is where the orcs come from, and a world with no кремль
     * in it has no raids in it either.
     *
     * The island-scattering code is gone rather than kept unused: dead code
     * the compiler has to be argued with is worse than none, and it is in the
     * history if the far island ever wants a nest of its own again.
     */
    this.lairs.clear();
    this.raiseKremlin();
  }

  /**
   * The adventure world: country to walk into rather than a map to win.
   *
   * Land first and sea second, which is the other way round from the кремль:
   * there the two islands are the game and the water is the reason crossing
   * matters. Here the water is lakes and inlets in a continent, because the
   * point is to walk a long way and find that the ground has changed.
   *
   * **Height is continuous and the ground is not.** Lift and roughness come
   * from the biome noise as smooth functions, so a mountain rises out of its
   * own foothills instead of standing behind a cliff; what changes sharply at
   * a border is only what the surface is made of, and grass giving way to
   * sand is a line deserts really have.
   */
  /**
   * Пригода: grown a column at a time, when somebody comes near.
   *
   * A world meant to be endless cannot be made all at once, and it cannot
   * hold a heightmap the size of itself either - the old version of this
   * allocated one array per cell of the whole map before it placed a single
   * block, which caps the world at whatever fits in memory rather than at
   * whatever anybody walks to.
   *
   * So generation is in stages, the way Minecraft does it: a column gets its
   * terrain, and then, once its eight neighbours also have terrain, it gets
   * the things that grow on it. The second stage is the one that writes
   * outside its own column - a tree on a border puts leaves next door, an ore
   * seam wanders - and the rule is what makes that safe: whatever it writes
   * into, the ground is already there and will not be generated over.
   *
   * The rule is also what makes the world the same whichever way you walk
   * into it, which is the property the check holds.
   */
  private generateAdventure(): void {
    /*
     * Only the country you can see from where you start.
     *
     * The rest arrives as somebody walks towards it. Growing all of it here
     * would put the whole point back: the map is a thousand blocks across,
     * which is four thousand columns, and nobody waits twelve seconds to
     * begin.
     */
    /*
     * Around where people actually arrive, which is not the middle of the
     * map: the start is put by water, and the nearest lake to the middle can
     * be a couple of hundred blocks off it. Growing the middle instead left
     * the player standing in country that had not been made - which is a
     * fall through the world, not a bad view.
     */
    const home = this.islands[0] ?? { x: 0, z: 0 };
    const atX = floorDiv(Math.round(home.x), CHUNK_SIZE);
    const atZ = floorDiv(Math.round(home.z), CHUNK_SIZE);
    for (let cz = atZ - SPAWN_REACH; cz <= atZ + SPAWN_REACH; cz++) {
      for (let cx = atX - SPAWN_REACH; cx <= atX + SPAWN_REACH; cx++) {
        this.ensureColumn(cx, cz);
      }
    }
    this.lairs.clear();
    this.raiseCastle();
  }

  /**
   * Where the замок stands, once this world has decided. Null on every other
   * map, and null until the adventure has been generated.
   */
  castleAt: { x: number; y: number; z: number } | null = null;

  /**
   * Cut Чахлик's замок into a mountain, once, somewhere out there.
   *
   * **Between five hundred and a thousand blocks from the start**, which is
   * the distance that was asked for and a good one: far enough that nobody
   * finds it on their first afternoon, near enough that it is not a rumour.
   * One to a world - there is one Чахлик - and only on the adventure.
   *
   * The site is the highest ground found in that ring. A castle cut into a
   * mountain needs a mountain, and the alternative - searching for the
   * "mountain" biome - picks country that is *called* mountain rather than
   * country that is actually high, which on a map made of noise is not the
   * same thing at all.
   *
   * The columns it covers are grown first. Everything else in the adventure
   * arrives when somebody walks to it; this cannot, because a building
   * written into a column that does not exist yet is a building written into
   * nothing.
   */
  private raiseCastle(): void {
    if (this.opts.terrain !== "adventure") return;
    const home = this.islands[0] ?? { x: 0, z: 0 };
    const ceiling = this.opts.sizeY * CHUNK_SIZE;

    /*
     * A gate on a slope, with the mountain rising behind it.
     *
     * The first version put the castle on the highest ground it could find
     * and `raise` refused the lot: a peak in this world stands at about 111
     * and the box is thirty-four tall, which is above a ceiling of 128. The
     * refusal was right and the siting was wrong - a castle *cut into* a
     * mountain does not stand on the summit, it opens on the hillside with
     * the hill behind it.
     *
     * So each candidate is judged on two numbers: the ground at the gate,
     * which has to leave room for the whole box under the sky, and the
     * ground thirty blocks behind it, which is how much mountain there is to
     * be inside. The best site is the one with the most hill behind a gate
     * that fits.
     */
    /*
     * The best site is the one the mountain covers most, not the one with
     * the steepest bank behind it.
     *
     * "Castle is not in/under the mountain" - and it was not: judged on
     * `rise` alone, a knoll thirty blocks behind a flat shelf scores as well
     * as a mountainside, and what went up was a black box standing beside a
     * mountain rather than cut into one. What is actually wanted is a
     * measurement of the thing asked for, so this counts the footprint: how
     * much of the forty-five by forty-five has hill over the top of the box.
     * Swept densely, the country can give between a half and two thirds, so
     * that is what it is asked to find.
     */
    const shape = castleProfile();
    let best:
      { x: number; z: number; front: number; rise: number; above: number }
      | null = null;
    /*
     * Twenty-one rings of forty-eight, and it is not what costs the time.
     *
     * Halving it to eleven by thirty-six was tried when the browser check
     * came back with the world still growing: the mean went from 13% proud
     * to 17% and the worst seed from 36% to 54%, and generation took exactly
     * as long either way - a sweep of a thousand sites is a few hundred
     * probes of noise, and what the second and a half actually goes on is
     * growing the country round the start. Most candidates are thrown out by
     * the cheap tests before the footprint is ever sampled.
     */
    for (let ring = 0; ring < 21; ring++) {
      const away = 500 + ring * 25;
      for (let a = 0; a < 48; a++) {
        const turn = (a / 48) * Math.PI * 2;
        const x = Math.round(home.x + Math.cos(turn) * away);
        const z = Math.round(home.z + Math.sin(turn) * away);
        if (!this.withinBounds(x - CASTLE_SPAN, 1, z - CASTLE_SPAN)) continue;
        if (!this.withinBounds(x + CASTLE_SPAN, 1, z + CASTLE_SPAN)) continue;

        /*
         * The ground the gate is actually cut through.
         *
         * The face of the box is one course further on than the point the
         * site is named by, and the arch is five wide - so a single probe at
         * the naming point can be four blocks lower than the bank the
         * doorway meets, which buries it. The highest of the five columns
         * the arch passes through is the one that has to clear.
         */
        let front = -Infinity;
        for (let dx = -2; dx <= 2; dx++) {
          front = Math.max(front, this.probeColumn(x + dx, z + 1).ground);
        }
        // Dry land, and room for the whole of it under the sky.
        if (front < this.seaLevel + 4) continue;
        if (front + CASTLE_HEIGHT + 2 >= ceiling) continue;
        // And a hill behind the gate rather than a field.
        const behind = this.probeColumn(x, z + 30).ground;
        const rise = behind - front;
        if (rise < 6) continue;
        /*
         * And open country in front of it, which is the half that was
         * missing. A site is judged on the ground *at* the gate, so a slope
         * that keeps climbing across the last few blocks approaching it
         * leaves the arch at the bottom of a pit with a bank of earth across
         * it: whole castle, every room reachable from inside, no way in from
         * outside. Two seeds in eight were like that. The mountain has to be
         * behind the gate and not in front of it.
         */
        const infront = Math.max(
          this.probeColumn(x, z - 6).ground,
          this.probeColumn(x, z - 12).ground,
        );
        if (infront > front + 2) continue;

        /*
         * How much of the замок would stand out of the hill, in blocks.
         *
         * Scored directly rather than as "what fraction of the footprint is
         * covered": the fraction counts a column as won or lost and a column
         * that misses by one block counts the same as one that misses by
         * twenty, so sites that scored better on it came out *more* proud -
         * 6% became 34% on one seed and 11% became 35% on another. The sum
         * of what sticks up is the thing complained about, so it is the
         * thing chosen on.
         */
        const floor = front - CASTLE_DROP;
        let above = 0;
        for (let dz = 0; dz < CASTLE_SPAN; dz += 3) {
          for (let dx = -22; dx <= 22; dx += 3) {
            const tall = shape[dz * CASTLE_SPAN + (dx + 22)]!;
            if (tall < 0) continue;   // nothing of the замок in this column
            const ground = this.probeColumn(x + dx, z + dz).ground;
            above += Math.max(0, floor + tall + 3 - ground);
          }
        }
        if (best === null || above < best.above ||
            (above === best.above && rise > best.rise)) {
          best = { x, z, front, rise, above };
        }
      }
    }
    if (best === null) return;

    // The columns the box covers, grown before anything is written into them.
    // The approach in front is grown with it: a road laid across columns
    // that do not exist yet is a road laid into nothing, and the ground it
    // was supposed to climb arrives later and knows nothing about it.
    const half = Math.ceil(CASTLE_SPAN / 2) + 1;
    const from = floorDiv(best.x - half, CHUNK_SIZE);
    const to = floorDiv(best.x + half, CHUNK_SIZE);
    const fromZ = floorDiv(best.z - APPROACH_REACH - 2, CHUNK_SIZE);
    const toZ = floorDiv(best.z + CASTLE_SPAN + 2, CHUNK_SIZE);
    for (let cz = fromZ; cz <= toZ; cz++) {
      for (let cx = from; cx <= to; cx++) this.ensureColumn(cx, cz);
    }

    // The gate's floor sits a course under the ground at the gate, so the
    // arch is cut into the slope rather than standing in front of it.
    const y = best.front - CASTLE_DROP;
    /*
     * The site *is* the anchor, and it was being moved half a замок west.
     *
     * `raise` puts the plan's anchor on the position it is given, and the
     * замок's anchor is the middle of its front face - `anchor: { x: 22 }`,
     * which is what makes the gate come out in the middle. Subtracting half
     * a span first therefore built the whole thing twenty-two blocks west of
     * the ground that had just been measured and chosen for it.
     *
     * "Замок чахлика стоїть біля гори а не в горі", and that is arithmetic
     * rather than luck. Measured over eight seeds: on 4242 the footprint
     * where it was built stands 55% proud of the hill and the same footprint
     * twenty-two blocks east stands 15%; on 777 it is 65% against 16%. The
     * sweep had found the mountain both times and then put the castle down
     * beside it.
     */
    const x0 = best.x;
    const put = raise(
      CASTLE, 0, x0, y, best.z,
      (bx, by, bz) => this.getBlock(bx, by, bz),
      (bx, by, bz) => this.inWorld(bx, by, bz),
      (bx, by, bz, id) => this.setGenerated(bx, by, bz, id),
      isNaturalGround,
      this.seaLevel + 1,
    );
    if (put !== "ok") return;
    this.drapeCastle(x0, y, best.z);
    this.openApproach(x0, y, best.z);
    // Held from here on: see `inCastleColumns`. The road in front counts -
    // a замок you cannot walk to is the same as a замок that is not there.
    /*
     * A chunk wider than the замок on every side.
     *
     * Not just the columns it stands in: dropping a column downgrades its
     * neighbours so their features grow again, and a seam of ore or the
     * leaves of a tree on the border then write into the замок's own box.
     * Held to the exact footprint it lost thirty-eight blocks that way.
     */
    const held = Math.ceil(CASTLE_SPAN / 2) + 2;
    this.castleColumns = {
      x0: floorDiv(x0 - held, CHUNK_SIZE) - 1,
      x1: floorDiv(x0 + held, CHUNK_SIZE) + 1,
      z0: floorDiv(best.z - APPROACH_REACH - 2, CHUNK_SIZE) - 1,
      z1: floorDiv(best.z + CASTLE_SPAN + 2, CHUNK_SIZE) + 1,
    };
    this.castleAt = { x: x0, y, z: best.z };
  }

  /**
   * Cut the way up to the gate.
   *
   * "But entrance should be visible" - and on one seed in four it was not.
   * The gate is cut through the front face at the height of the ground at
   * the *middle* of that face, and a slope that keeps rising across the last
   * few blocks in front of it buries the arch behind a bank of earth: the
   * замок was whole, every room reachable from inside, and there was no way
   * in from the country outside it. Walking to it from six blocks out is
   * what found that; nothing that looks at the building alone can.
   *
   * Six blocks of level approach, the width of the doorway, with a floor
   * laid under it only where the hill has fallen away. It reads as a road
   * cut into the slope, which is what a gate in a mountainside needs.
   */
  private openApproach(x0: number, y0: number, z0: number): void {
    /*
     * A ramp from the hillside up to the threshold.
     *
     * Every heuristic version of this traded one seed for another - clear
     * six blocks in front and the castles standing over a drop were fine and
     * the ones set into a bank were still sealed; probe the bank instead and
     * the drop ones broke. Ten of fifteen seeds was as far as guessing got.
     *
     * So it is built rather than guessed. The floor climbs from the ground
     * ten blocks out to the gate's own sill at no more than one course a
     * block - which is exactly the step a player can take - and the doorway's
     * height is cleared the whole way along. Whatever the country does in
     * front of the gate, there is a road.
     */
    const sill = y0;
    const wide = 3;
    const outside = this.probeColumn(x0, z0 + 1 - APPROACH_REACH).ground;
    // Long enough that the climb is never steeper than a step, and never
    // longer than the country that was grown for it.
    const reach = APPROACH_REACH;
    for (let d = 1; d <= reach; d++) {
      // `z0` names the site; the face of the box is the course after it, so
      // the first cell of the approach is `z0` itself.
      const z = z0 + 1 - d;
      const t = d / reach;
      const floor = Math.round(sill + (outside - sill) * t);
      for (let x = x0 - wide; x <= x0 + wide; x++) {
        if (!this.inWorld(x, floor, z)) continue;
        // Something to walk on, and the doorway's height above it.
        if (!isSolid(this.getBlock(x, floor, z))) {
          this.setGenerated(x, floor, z, BlockId.CursedBrick);
        }
        for (let y = floor + 1; y <= floor + 5; y++) {
          if (!this.inWorld(x, y, z)) continue;
          const there = this.getBlock(x, y, z);
          // Only the country's own earth: a cut that ate the gate's arch
          // would be a cut that removed the thing it exists to reveal.
          if (isNaturalGround(there) || there === BlockId.Snow) {
            this.setGenerated(x, y, z, BlockId.Air);
          }
        }
      }
    }
  }

  /**
   * Put the mountain back over the замок.
   *
   * `raise` clears its whole box and the plan fills it solid, which is right
   * for the inside and catastrophic for the outside: the box is forty-five
   * across and thirty-four tall, the hill only covers the *back* of it, and
   * what stands over the front is a bare cliff of проклятий камінь. Measured
   * on the seed somebody was playing, three quarters of the castle's outward
   * faces were open air. From a distance it reads as a black slab dropped on
   * a hillside - the report was "I got to the coordinates and there is no
   * castle", from somebody looking straight at it.
   *
   * So the fill is cut back to the hill's own silhouette afterwards rather
   * than shaped during the plan - the plan cannot see the ground, and a
   * taper written blind is what left air pockets inside the rock last time.
   * Only the solid fill is touched: rooms are lined with проклята цегла, so
   * a вежа whose top is above the hill still comes out of it, which is what
   * the picture had. And the last few courses under the surface are given
   * back their own earth, so what you walk over is the mountain and not a
   * flat black lid the size of the box.
   */
  private drapeCastle(x0: number, y0: number, z0: number): void {
    const SKIN = 3;
    /** How much rock is left over a room before the hill takes over. */
    const ROOF = 3;
    const columns = [...footprintOf(CASTLE, 0, x0, z0)];
    const top = y0 + CASTLE_HEIGHT;
    const bottom = y0 - FOUNDATION_DEPTH;

    /*
     * What each column has to keep, and the cap above it goes.
     *
     * Two wrong versions came before this one and both are worth writing
     * down, because they were wrong in opposite directions.
     *
     * The first kept the box: a solid forty-five by forty-five by thirty-four
     * of проклятий камінь standing on a hillside that only covered its back.
     * Three quarters of its outward faces were open air and it read as a
     * black slab - "I got to the coordinates and there is no castle", from
     * somebody looking straight at it.
     *
     * The second took away every cell of rock above the ground unless it
     * touched a hollow. That hollowed the замок into a lace of brick with
     * sky between: `hall` carves corridors without lining them, so their
     * walls *are* the fill. Looking at a slice of it is what showed that.
     *
     * The measurement that settles it: swept densely over the whole ring on
     * five seeds, the best mountain in this world covers between 44% and 67%
     * of the footprint. A thirty-four-tall замок cannot be buried, so it is
     * not going to be - "inside or even partly under the mountain" is what
     * was asked, and partly is what the country can give.
     *
     * So each column keeps a **contiguous** run from the bottom up to
     * whichever is higher: the hill's own surface, or three courses over the
     * highest thing the castle has in that column. Everything above that is
     * cut off. Nothing is ever holed, because what is removed is always the
     * top of a column, and the silhouette follows the hill where the castle
     * needs nothing and rises over the rooms and the вежа where it does.
     */
    const needed = new Map<string, number>();
    for (const { x, z } of columns) {
      let highest = -Infinity;
      for (let y = y0; y < top; y++) {
        const id = this.getBlock(x, y, z);
        // Air inside the box is a room or a passage; brick and lamp are the
        // building itself. Plain fill is the only thing that may go.
        if (id === BlockId.Air || id === BlockId.CursedBrick ||
            id === BlockId.CursedLamp) highest = y;
      }
      needed.set(`${x},${z}`, highest);
    }

    for (const { x, z } of columns) {
      // The natural height, read from the noise: it is what the ground was
      // before the box was cleared, which is the one thing the world can no
      // longer be asked.
      const ground = this.probeColumn(x, z).ground;
      const structure = needed.get(`${x},${z}`) ?? -Infinity;
      const keep = Math.max(ground, structure + ROOF);
      for (let y = bottom; y < top; y++) {
        if (this.getBlock(x, y, z) !== BlockId.CursedStone) continue;
        if (y > keep) { this.setGenerated(x, y, z, BlockId.Air); continue; }
        // Where the hill is what is left, give it back its own earth, so
        // what you walk over is the mountain and not a flat black lid.
        if (keep === ground && y > ground - SKIN) {
          this.setGenerated(x, y, z, this.naturalSkin(x, y, z, ground));
        }
      }
    }
  }

  /**
   * What the top of an untouched column is made of at this height.
   *
   * Asked of the noise rather than of the world, because the world's copy of
   * it has just been cleared. The same three rules the terrain itself uses:
   * snow high up, sand by the water, and grass over earth over stone.
   */
  private naturalSkin(x: number, y: number, z: number, ground: number): number {
    const here = this.probeColumn(x, z);
    const deep = ground - y;
    if (deep >= 3) return BlockId.Stone;
    if (here.biome === "desert") return BlockId.Sand;
    if (here.biome === "winter" || ground - this.seaLevel > SNOW_LINE) {
      return deep === 0 ? BlockId.Snow : BlockId.Stone;
    }
    if (here.biome === "mountain" && ground - this.seaLevel > SNOW_LINE - 8) {
      return BlockId.Stone;
    }
    return deep === 0 ? BlockId.Grass : BlockId.Dirt;
  }

  /**
   * Make sure a column is finished - terrain, and everything growing on it.
   *
   * Cheap to call on a column that is already done, which is the point: the
   * streaming loop calls it for everything near the player every time the
   * player moves.
   */
  ensureColumn(cx: number, cz: number): void {
    if (this.stages.get(key(cx, 0, cz)) === COLUMN_FULL) return;
    if (!this.inColumnBounds(cx, cz)) return;
    // The neighbour rule. Terrain for the eight around it before anything
    // grows here, because what grows here does not always stay here.
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) this.adventureTerrain(cx + dx, cz + dz);
    }
    this.adventureFeatures(cx, cz);
    this.stages.set(key(cx, 0, cz), COLUMN_FULL);
  }

  /**
   * Grow the country around somebody, a few columns at a time.
   *
   * Nearest first, so the ground under the player's feet arrives before the
   * hills on the skyline, and never more than `budget` columns in one call:
   * a column is about three milliseconds, and a player walking into country
   * nobody has been to should cost a frame here and there rather than a
   * freeze while the horizon fills in.
   *
   * @returns how many columns it grew, so a caller can tell a busy frame
   * from an idle one.
   */
  ensureAround(x: number, z: number, reach: number, budget = 2): number {
    const cx = floorDiv(Math.floor(x), CHUNK_SIZE);
    const cz = floorDiv(Math.floor(z), CHUNK_SIZE);
    let grown = 0;
    for (let ring = 0; ring <= reach && grown < budget; ring++) {
      for (let dz = -ring; dz <= ring && grown < budget; dz++) {
        for (let dx = -ring; dx <= ring && grown < budget; dx++) {
          // Only the edge of this ring: the inside was done by the last one.
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== ring) continue;
          const tx = cx + dx, tz = cz + dz;
          if (this.stages.get(key(tx, 0, tz)) === COLUMN_FULL) continue;
          if (!this.inColumnBounds(tx, tz)) continue;
          this.ensureColumn(tx, tz);
          grown++;
        }
      }
    }
    return grown;
  }

  /**
   * Forget the country nobody is near, and say what was dropped.
   *
   * A world grown as it is walked into is also a world that keeps
   * everything ever walked into, and a thousand blocks square is thirty
   * thousand columns: the memory has to go back. It can, because the country
   * came out of the seed and will come out of it again - the same column, in
   * the same order, by the rule that makes the walk-order check pass.
   *
   * A column holding anything anybody changed stays. That is the save file,
   * and there is no seed to get it back from.
   *
   * The reach here should be comfortably beyond the one things are grown at,
   * or a player pacing back and forth across the line would spend every
   * frame throwing away country and making it again.
   *
   * **A list of people, not a person.** Alone there is one of them and the
   * list has one entry; on a server there are several, standing in different
   * parts of the map, and country is only thrown away when it is far from
   * *all* of them. Written this way from the start rather than as a second
   * method, because "forget what nobody is near" with one caller passing one
   * point and another passing five is the same question asked once.
   *
   * @returns the chunks that went, so whatever is drawing them can let go.
   */
  forgetBeyond(
    near: readonly { readonly x: number; readonly z: number }[], reach: number,
  ): { cx: number; cy: number; cz: number }[] {
    const around = near.map((p) => ({
      cx: floorDiv(Math.floor(p.x), CHUNK_SIZE),
      cz: floorDiv(Math.floor(p.z), CHUNK_SIZE),
    }));
    const dropped: { cx: number; cy: number; cz: number }[] = [];
    for (const at of [...this.stages.keys()]) {
      const [cx, , cz] = at.split(",").map(Number) as [number, number, number];
      if (around.some((p) =>
        Math.max(Math.abs(cx - p.cx), Math.abs(cz - p.cz)) <= reach)) continue;

      let keep = false;
      for (let cy = 0; cy < this.opts.sizeY; cy++) {
        if (this.modified.has(key(cx, cy, cz))) { keep = true; break; }
      }
      if (keep) continue;
      /*
       * And the замок is never forgotten.
       *
       * This is the whole reason it kept not being there. It is cut once, at
       * generation, six hundred blocks from where anybody is standing - so
       * the very next sweep of this function threw all of it away, and when
       * somebody finally flew out to those coordinates the columns grew back
       * from the seed as what the seed says is there: a hillside. Measured
       * on the seed being played, thirty-eight thousand blocks of замок went
       * to nought on the first forget and stayed at nought after walking
       * back to it.
       *
       * Nothing else in the world has this problem, because everything else
       * out there *is* the seed and comes back identical. A building is not:
       * it was put there by a pass that runs once. Ninety-odd columns held
       * for the life of the world is under a megabyte, and the alternative -
       * rebuilding it whenever its country regrows - re-runs a forty-five
       * block box every time somebody crosses the line at its edge.
       */
      if (this.inCastleColumns(cx, cz)) continue;

      for (let cy = 0; cy < this.opts.sizeY; cy++) {
        if (this.chunks.delete(key(cx, cy, cz))) dropped.push({ cx, cy, cz });
      }
      this.stages.delete(at);
      /*
       * And the neighbours have their features to do again.
       *
       * What grows on a column does not always stay on it: a tree on a
       * border puts leaves next door and a seam wanders. Those blocks were
       * in the column just dropped, and the neighbour that put them there is
       * marked finished and will never put them there again - so walking
       * out and back found a country with pieces missing out of the seams
       * between columns, which is what the check caught.
       *
       * Running a column's features twice writes the same blocks twice: they
       * come from the seed and the position, and the second pass is exactly
       * the first one. So the fix is to ask for them again, and the neighbour
       * rule takes care of the order - nothing grows until the ground it
       * would grow onto is back.
       */
      for (let dz = -1; dz <= 1; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dz === 0) continue;
          const near = key(cx + dx, 0, cz + dz);
          if (this.stages.get(near) === COLUMN_FULL) {
            this.stages.set(near, COLUMN_TERRAIN);
          }
        }
      }
    }
    if (dropped.length > 0) this.lairsStale = true;
    return dropped;
  }

  /**
   * The columns the замок and its road stand in, in chunk coordinates.
   *
   * Null on every other map and until it has been raised.
   */
  private castleColumns:
    { x0: number; x1: number; z0: number; z1: number } | null = null;

  /** Is this column part of the замок, and so never to be thrown away? */
  private inCastleColumns(cx: number, cz: number): boolean {
    const c = this.castleColumns;
    return c !== null && cx >= c.x0 && cx <= c.x1 && cz >= c.z0 && cz <= c.z1;
  }

  /** Is this column inside the map at all? */
  private inColumnBounds(cx: number, cz: number): boolean {
    return cx >= 0 && cz >= 0 &&
      cx < this.opts.sizeXZ && cz < this.opts.sizeXZ;
  }

  /**
   * The ground of one column: how high it stands and what country it is in.
   *
   * Worked out per column rather than per block. It was asked per *block*
   * once - thirteen million noise evaluations for a world of twenty chunks,
   * ten times the cost of the кремль map for under three times the ground.
   */
  private adventureSurface(
    cx: number, cz: number, heights: Int16Array, biomes: Uint8Array,
    tables: Int16Array,
  ): void {
    /*
     * The last column worked out, kept.
     *
     * Terrain and features are two stages of the same column and both need
     * the same ground under them, so without this every column is worked out
     * twice - and since the water settles against a ring of neighbours, that
     * is a thousand noise lookups thrown away per column.
     */
    const remembered = this.lastSurface.get(key(cx, 0, cz));
    if (remembered !== undefined) {
      heights.set(remembered.heights);
      biomes.set(remembered.biomes);
      tables.set(remembered.tables);
      return;
    }

    /*
     * Dry country skips all of it.
     *
     * Settling costs a ring of noise five cells deep around the column -
     * three times the cells the column itself has - and it is only ever
     * about water. Nine columns in ten have none, and a column with no water
     * in it cannot gain any: settling only ever lowers a level, never raises
     * one. So the cheap question is asked first.
     */
    let anyWater = false;
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const cell = this.adventureCell(
          cx * CHUNK_SIZE + lx, cz * CHUNK_SIZE + lz);
        heights[lz * CHUNK_SIZE + lx] = cell & 0xffff;
        biomes[lz * CHUNK_SIZE + lx] = (cell >> 16) & 0x0f;
        tables[lz * CHUNK_SIZE + lx] = cell >>> 20;
        if ((cell >>> 20) > (cell & 0xffff)) anyWater = true;
      }
    }
    if (!anyWater) {
      // Written already, and nothing to settle: what is in the arrays is the
      // answer. Most of the country takes this way out.
      this.remember(cx, cz, heights, biomes, tables);
      return;
    }
    /*
     * The column and a ring of cells around it, because water has to look at
     * its neighbours before it decides how high to stand.
     *
     * Built once as a grid rather than asked for five times a cell: the
     * clamp below needs the four cells around every one of the two hundred
     * and fifty-six, and asking the noise for each of them separately is
     * fifteen hundred evaluations where three hundred will do.
     */
    const wide = CHUNK_SIZE + 2 * WATER_SETTLE;
    const ground = new Int32Array(wide * wide);
    let level = new Int32Array(wide * wide);
    for (let lz = 0; lz < wide; lz++) {
      for (let lx = 0; lx < wide; lx++) {
        const cell = this.adventureCell(
          cx * CHUNK_SIZE + lx - WATER_SETTLE, cz * CHUNK_SIZE + lz - WATER_SETTLE);
        ground[lz * wide + lx] = cell;
        level[lz * wide + lx] = cell >>> 20;
      }
    }

    /*
     * Let the water settle, one ring at a time.
     *
     * Each pass lowers every level to what its neighbours can hold, and each
     * pass is therefore only honest one cell further in than the last: the
     * outermost ring is still working from unsettled neighbours. So the
     * skirt is as deep as the number of passes, and after them the middle
     * sixteen are settled against settled neighbours.
     *
     * This is a flood fill done the only way a world grown a column at a
     * time can do one: locally, a few steps, accepting that a basin wider
     * than the passes go will not be perfectly level. Measured, that leaves
     * a handful of one-block ledges in four hundred blocks of country
     * against two thousand before any of this.
     */
    for (let pass = 0; pass < WATER_SETTLE; pass++) {
      const next = new Int32Array(level);
      const edge = pass + 1;
      for (let lz = edge; lz < wide - edge; lz++) {
        for (let lx = edge; lx < wide - edge; lx++) {
          const at = lz * wide + lx;
          next[at] = heldIn(ground[at]!, level[at]!, [
            [ground[at - 1]!, level[at - 1]!], [ground[at + 1]!, level[at + 1]!],
            [ground[at - wide]!, level[at - wide]!],
            [ground[at + wide]!, level[at + wide]!],
          ]);
        }
      }
      level = next;
    }

    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const at = (lz + WATER_SETTLE) * wide + (lx + WATER_SETTLE);
        heights[lz * CHUNK_SIZE + lx] = ground[at]! & 0xffff;
        biomes[lz * CHUNK_SIZE + lx] = (ground[at]! >> 16) & 0x0f;
        tables[lz * CHUNK_SIZE + lx] = level[at]!;
      }
    }
    this.remember(cx, cz, heights, biomes, tables);
  }

  /** Keep a column's surface, and forget the oldest when there are too many. */
  private remember(
    cx: number, cz: number,
    heights: Int16Array, biomes: Uint8Array, tables: Int16Array,
  ): void {
    this.lastSurface.set(key(cx, 0, cz), {
      heights: new Int16Array(heights),
      biomes: new Uint8Array(biomes),
      tables: new Int16Array(tables),
    });
    // A dozen is enough: what asks twice is the terrain and the features of
    // one column, with the eight neighbours' terrain in between.
    if (this.lastSurface.size > 12) {
      const oldest = this.lastSurface.keys().next().value;
      if (oldest !== undefined) this.lastSurface.delete(oldest);
    }
  }

  /**
   * The same question for a single cell, when there is no grid to hand.
   *
   * Five noise lookups instead of one, so it is for the odd cell rather than
   * for a column of them: whether a tree may stand here, whether this is a
   * lake worth starting beside.
   */
  private adventureLevel(x: number, z: number): number {
    const here = this.adventureCell(x, z);
    // One settling pass, not the full set: this answers "is this cell under
    // water" for a tree or a starting place, and one pass is already within
    // a block of the answer. The columns themselves get the full set.
    return heldIn(here, here >>> 20,
      ([[-1, 0], [1, 0], [0, -1], [0, 1]] as const).map(([dx, dz]) => {
        const near = this.adventureCell(x + dx, z + dz);
        return [near, near >>> 20] as [number, number];
      }));
  }

  /**
   * What the noise says about a column, without making one.
   *
   * A column's ground, its country and the level its water would stand at
   * all come straight out of the seed, so this is a handful of noise lookups
   * rather than a chunk built and thrown away. `waterNear` has always worked
   * this way - it has to, because it asks about a few thousand places before
   * the title screen - and this is the same thing said out loud so that
   * anything else which needs to look a long way off can do it too.
   *
   * The water level is settled against its four neighbours once, which is
   * enough to answer "is this under water" and not enough to be the level
   * the terrain will actually use. Only the adventure has any of this; every
   * other map is made all at once and can simply be looked at.
   */
  probeColumn(x: number, z: number): {
    ground: number; biome: Biome;
    /**
     * The level water stands at here, or null where there is none.
     *
     * **Null and not "the ground".** The settled table is clamped to the
     * column's own ground - a table below the ground is not a lower lake, it
     * is no lake - so a "water level" read off a dry column is exactly the
     * ground, and every test of the form `ground > water + k` is false
     * everywhere. That clamp, unnoticed, is what left the adventure with no
     * trees and no wild plants for weeks. A thing that is not there is
     * better said as nothing than as a number that looks like an answer.
     */
    water: number | null;
  } {
    const packed = this.adventureCell(x, z);
    const ground = packed & 0xffff;
    const level = Math.max(this.seaLevel, this.adventureLevel(x, z));
    return {
      ground,
      biome: ADVENTURE_KINDS[(packed >> 16) & 0x0f]!,
      water: level > ground ? level : null,
    };
  }

  /**
   * One cell of it: the height in the low half, the country in the high.
   *
   * Packed into a number rather than an object because this is called for
   * every cell of every column grown, and a short-lived object each time is
   * the kind of thing that turns a walk across the country into a stutter.
   */
  private adventureCell(x: number, z: number): number {
    const { seed, sizeXZ, sizeY } = this.opts;
    const worldHeight = sizeY * CHUNK_SIZE;
    const seaLevel = this.seaLevel;
    const spanXZ = sizeXZ * CHUNK_SIZE;
    /*
     * Continents, not islands.
     *
     * One low-frequency field decides land from water, and the edge of
     * the map is pulled under so a walker meets a coast rather than the
     * end of the world. Until the world is genuinely endless that edge
     * exists, and a shoreline is a better wall than an invisible one.
     */
    const inland = fbm2(x / 300, z / 300, seed + 1511, 4);
    const fromEdge = Math.min(x, z, spanXZ - 1 - x, spanXZ - 1 - z);
    const rim = Math.min(1, fromEdge / 40);
    /*
     * A narrow ramp from sea to land, so there is a coast rather than a
     * beach the width of the map: the wide one left most of the ground
     * sitting a block above the water, and a block above the water is
     * sand.
     */
    const land = Math.min(1, Math.max(0, (inland - 0.33) / 0.12)) * rim;

    const rugged = ruggedAt(x, z, seed);
    const detail = fbm2(x / 34, z / 34, seed + 77, 4) - 0.5;
    /*
     * The shore stays gentle whatever the country behind it is doing.
     *
     * Relief is damped to nothing at the waterline - without it a
     * mountain that happened to meet the sea arrived as a forty-block
     * wall, and there was no way up off the beach.
     */
    const shoreEase = Math.min(1, land / 0.22);
    const lift = liftAt(rugged) * land;
    const relief = detail * roughAt(rugged) * shoreEase;
    const base = seaLevel - 8 + land * 26 + lift;
    const h = Math.round(base + relief);
    // Twelve blocks of sky kept clear: a peak that reaches the ceiling
    // is flattened by it, and a flat-topped mountain is a plateau nobody
    // chose.
    const ground = Math.max(1, Math.min(worldHeight - 12, h));
    const kind = ADVENTURE_KINDS.indexOf(
      biomeFrom(rugged, warmthAt(x, z, seed)));

    /*
     * How high the water stands here, if it stands at all.
     *
     * A continent with a sea round it and nothing in the middle is what this
     * map was: four hundred tries within sight of the spawn found nowhere to
     * put a fish. So there is a water table - a level the ground is wet up
     * to, from its own slow noise - and every hollow the terrain digs below
     * that level is a lake. Minecraft's aquifers are the same idea, and it
     * is the only one that works in a world grown a column at a time: a
     * flood fill needs the whole basin at once, and a column only ever has
     * itself.
     *
     * Measured against `base` - the smooth part of the ground, before the
     * detail that makes hollows - rather than against the sea, so a lake
     * sits in the country it is in rather than only where the land happens
     * to be near sea level. The table runs below `base` most of the time,
     * which is what keeps lakes to the hollows instead of drowning the
     * plains, and it is damped to nothing at the shore so a lake never meets
     * the sea in a wall of water standing above it.
     */
    const wet = fbm2(x / 180, z / 180, seed + 2237, 3);
    /*
     * And only where the country is flat enough to hold it.
     *
     * The table follows the ground, because a lake sits at the height of the
     * land it is in and one pinned to sea level could never appear on a
     * plateau forty blocks up. Following the ground means inheriting its
     * slope, though, and a pond on a hillside then stands three blocks
     * higher at one end than at the other - 1362 such steps in four hundred
     * blocks of country, the first time this was measured.
     *
     * Sampling the slope more slowly was worse, not better: the table then
     * sits wherever it likes with respect to the actual ground, and
     * fifty-six per cent of the country drowned. What works is refusing to
     * raise the table at all where the ground is steep. Rugged country gets
     * no lakes, gentle country gets flat ones, and neither of those is a
     * thing to apologise for.
     */
    const steep = Math.max(0, rugged - LAKE_CALM) * LAKE_SINK;
    let table = Math.round(
      base + (LAKE_RISE * wet - LAKE_DROP - steep) * shoreEase * land);
    let bed = ground;

    /*
     * Ріки, and the one thing that makes a river a river: it goes somewhere.
     *
     * A line of noise across the country is a canal. What turns it into a
     * river is that its *surface* falls towards the sea and meets it there,
     * and that is arranged rather than simulated: the water level in a
     * channel is `seaLevel + RIVER_HEAD * land`, and `land` is the same
     * field that decides where the sea is. So the level falls to exactly sea
     * level at the coast, everywhere, by construction - which is what makes
     * this possible at all in a world grown a column at a time. A flow
     * accumulation needs the whole continent at once; a column only ever has
     * itself.
     *
     * The channel itself is a ridge of noise - the set of places where one
     * slow field passes through its own middle - which gives long winding
     * lines that join and part. Its width in blocks is set by how fast that
     * field changes, so `RIVER_WIDE` is a distance in noise and not in
     * metres; measured, it comes out about fourteen blocks across.
     */
    const wind = fbm2(x / 260, z / 260, seed + 6607, 3);
    const off = Math.abs(wind - 0.5);
    if (off < RIVER_NEAR && land > 0) {
      /*
       * How far from the middle of the channel this is, **in blocks**.
       *
       * Not in units of the noise, which is what the first version used and
       * which gives a river whose width depends on how fast the field
       * happens to be changing: near a flat spot in the noise the same
       * threshold spread the water forty blocks across, and the map came out
       * with a lake in the shape of a river in it. Dividing by the local
       * slope of the field turns the threshold into a distance, and the
       * channel is the same width wherever it runs.
       *
       * Four extra samples, and only for the columns that are already near
       * the line - which is about one in twenty, so the cost of it does not
       * show up in the time a world takes to start.
       */
      /*
       * Per column, and it has to be.
       *
       * This was worked out once per sixteen-block cell and kept, on the
       * reasoning that a field with a period of two hundred and sixty blocks
       * hardly changes across sixteen of them. True of the field - and the
       * distance from the middle of the channel is `off / slope`, so a slope
       * that steps at a chunk boundary steps the whole valley profile with
       * it. The result was a twenty-two-block wall of rock at x=2096,
       * exactly on the boundary between two chunks, and the check about
       * nothing being as sheer as the кремль's own wall found it.
       *
       * Measured before assuming it mattered: keeping it saved nothing worth
       * having. A world took 1.48 seconds to start with the cache and 1.47
       * without.
       */
      const gx = fbm2((x + 2) / 260, z / 260, seed + 6607, 3) -
        fbm2((x - 2) / 260, z / 260, seed + 6607, 3);
      const gz = fbm2(x / 260, (z + 2) / 260, seed + 6607, 3) -
        fbm2(x / 260, (z - 2) / 260, seed + 6607, 3);
      const slope = Math.hypot(gx, gz) / 4;
      const blocks = slope > 1e-7 ? off / slope : Infinity;
      const surface = seaLevel + Math.round(RIVER_HEAD * land);
      const out = blocks - RIVER_HALF;
      if (blocks < RIVER_REACH) {
      if (out <= 0) {
        /*
         * The channel: flat-bottomed, three blocks of water in it.
         *
         * Two was a stream you wade across without noticing, and there is a
         * човен in this game now - "збільши глибину та ширину річок на 50%".
         */
        bed = Math.min(bed, surface - 3);
        table = surface;
      } else {
        /*
         * And the valley sides, which are the whole difference between a
         * river and a slot cut in the ground.
         *
         * They rise away from the water at a fixed slope and stop wherever
         * they meet the country - so a river crossing low ground barely
         * marks it, and one crossing a ridge cuts a gorge as wide as the
         * ridge is high. Without this, a river running through country
         * forty blocks up left a forty-block vertical wall along both banks,
         * which is a canyon nobody can get out of and which the check about
         * sheerness caught immediately.
         *
         * Two-thirds of a block up for every block out: under one, so what a
         * walker meets going up the bank is a step or no step, never a
         * climb.
         */
        const rim = surface + Math.max(1, Math.round(out / RIVER_FLARE));
        if (rim < bed) bed = rim;
        /*
         * And it builds its banks as well as cutting its valley.
         *
         * Only near the water, and it is what stops a river being a wall.
         * The channel stands at whatever height `land` puts it, and the
         * country beside it does not have to agree: where the ground next to
         * a river happened to be lower than the river, the water stood five
         * blocks above the field beside it with nothing holding it in - 1507
         * such cells in one walked patch, which is the fault the lake checks
         * were written for in the first place.
         *
         * So inside `RIVER_LEVEE` the ground is held up to one block over
         * the water - just enough to hold it in - and no higher. It used to
         * be built all the way up to the valley profile, which ten blocks
         * out is seven blocks above the water: where the land beyond that
         * happened to be low, the bank ended in a seventeen-block cliff, and
         * the check about nothing being as sheer as the кремль's wall caught
         * it. A lip is a lip; a rampart is a wall across the country.
         */
        if (out < RIVER_LEVEE) {
          const hold = surface + 1;
          if (bed < hold) bed = hold;
        }
        /*
         * And the lake this column used to think it had is the river's now.
         *
         * Cutting a valley lowers the ground and says nothing about the
         * water table, so a hillside whose table stood ten blocks over it -
         * dry, because the ground was higher still - became a ten-block lake
         * clinging to the side of a gorge the moment the river cut through
         * it. That is where the eleven-block wall of water came from.
         *
         * A river drains what it cuts through, down to its own level: the
         * whole point of one.
         */
        if (table > surface) table = surface;
      }
      }
    }

    // Below the waterline it is the sea, and the sea has its own level.
    const water = Math.max(0, Math.min(worldHeight - 1, table));
    return (water << 20) | (kind << 16) | bed;
  }

  /** The rock, the soil and the water of one column. Nothing grown on it. */
  private adventureTerrain(cx: number, cz: number): void {
    if (!this.inColumnBounds(cx, cz)) return;
    if (this.stages.has(key(cx, 0, cz))) return;
    this.stages.set(key(cx, 0, cz), COLUMN_TERRAIN);

    const { sizeY } = this.opts;
    const seaLevel = this.seaLevel;
    const heights = new Int16Array(CHUNK_SIZE * CHUNK_SIZE);
    const biomes = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
    const tables = new Int16Array(CHUNK_SIZE * CHUNK_SIZE);
    this.adventureSurface(cx, cz, heights, biomes, tables);

    /*
     * Three fields read once per column rather than once per cell.
     *
     * Every one of them answers a question about the ground that does not
     * change with height - what the shore is made of, whether the bed under
     * the water is clay, whether there is a lens of it down where the soil
     * gives out - so reading them in the y loop would be the same answer
     * computed a hundred times. ([[feedback-keep-the-suite-quick]]: walking
     * the world cell by cell is the mistake that keeps getting made.)
     */
    const grains = new Float32Array(CHUNK_SIZE * CHUNK_SIZE);
    const beds = new Float32Array(CHUNK_SIZE * CHUNK_SIZE);
    const lenses = new Float32Array(CHUNK_SIZE * CHUNK_SIZE);
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const wx = cx * CHUNK_SIZE + x;
        const wz = cz * CHUNK_SIZE + z;
        const at = z * CHUNK_SIZE + x;
        grains[at] = fbm2(wx / 14, wz / 14, this.opts.seed + 3301, 2);
        beds[at] = fbm2(wx / 18, wz / 18, this.opts.seed + 6607, 3);
        lenses[at] = fbm2(wx / 26, wz / 26, this.opts.seed + 7717, 3);
      }
    }

    const made: Chunk[] = [];
    for (let cy = 0; cy < sizeY; cy++) {
      const chunk = new Chunk(cx, cy, cz);
      made.push(chunk);
      const blocks = chunk.blocks;
      for (let y = 0; y < CHUNK_SIZE; y++) {
        const wy = cy * CHUNK_SIZE + y;
        for (let z = 0; z < CHUNK_SIZE; z++) {
          for (let x = 0; x < CHUNK_SIZE; x++) {
            const surface = heights[z * CHUNK_SIZE + x]!;
            const biome = ADVENTURE_KINDS[biomes[z * CHUNK_SIZE + x]!]!;
            /*
             * The sea, or a lake standing higher than it.
             *
             * The sea has one level over the whole world; a lake has the
             * level of its own water table, which is above the sea only
             * where there is land to hold it in.
             */
            const wet = Math.max(seaLevel, tables[z * CHUNK_SIZE + x]!);

            if (wy > surface) {
              if (wy > wet) continue;
              /*
               * Water, or the lid on it.
               *
               * A frozen lake is one block of ice on top rather than a solid
               * block of it: you walk across, you break through to water, and
               * the shape of the pond is still a pond.
               */
              /*
               * Ice floats. It does not stand on the bottom.
               *
               * A lid only where there is water under it to be a lid on: a
               * lake one block deep in a winter country came out as a single
               * cell of ice sitting straight on the bed, so the whole thing
               * was frozen solid with no water in it anywhere. Digging into
               * one left a dry hole that nothing poured into - which is what
               * it looked like from inside the game, and it was reported as
               * two separate bugs.
               */
              blocks[blockIndex(x, y, z)] =
                biome === "winter" && wy === wet && wet - surface >= 2
                  ? BlockId.Ice : BlockId.Water;
              continue;
            }

            /*
             * Above the water here, which is not the same as above the sea.
             *
             * The snowline and the depth of soil are reckoned from sea
             * level, because those are heights. What is under your feet at
             * the water's edge is reckoned from whatever water is actually
             * there: the bed of a lake forty blocks up is a lake bed, and a
             * block above the water is sand whichever water it is.
             */
            const overWater = surface - wet;
            const above = surface - seaLevel;
            /*
             * Under the water at all, not under it by two.
             *
             * The old line said `wet - 1`, so a bed with exactly one block of
             * water over it counted as dry land and was given whatever the
             * country puts on dry land. In a winter one that is snow, and
             * снігу під водою is the first thing anybody noticed about these
             * lakes. It was wrong on the sea too, and had been all along -
             * there was simply nothing white to make it obvious.
             */
            const drowned = surface < wet;
            /*
             * What the ground beside the water is made of.
             *
             * It used to be sand, everywhere, on every shore of every lake
             * and sea in every country: "навколо води має бути не тільки
             * пісок але й земля", and "піску має бути менше якщо це не біом
             * пустелі". A continuous yellow ring around every body of water
             * is the single most recognisable thing about the game this must
             * not look like, and it is not what a bank looks like either -
             * most water in this country runs through grass and mud, and a
             * beach is the exception.
             *
             * So one slow field decides, and it decides in patches a dozen
             * blocks across: a beach here, a muddy bank there, meadow
             * running into the water somewhere else. Sand keeps the
             * narrowest third, which is what makes the rest of it read as a
             * beach when you do find one.
             */
            const grain = grains[z * CHUNK_SIZE + x]!;
            const sandy = grain > SHORE_SAND;
            const grassy = grain < SHORE_GRASS;
            let id: BlockId;
            if (wy === surface && !drowned) {
              /*
               * The snowline is a height, not a biome.
               *
               * It is what makes a range read as a range from a long way off,
               * and it has to hold in a warm country too - a mountain high
               * enough is white wherever it stands.
               */
              id = above > SNOW_LINE || biome === "winter" ? BlockId.Snow
                : biome === "desert" ? BlockId.Sand
                : biome === "mountain" ? BlockId.Stone
                : overWater <= 1
                  ? (sandy ? BlockId.Sand : grassy ? BlockId.Grass : BlockId.Dirt)
                : BlockId.Grass;
            } else if (wy > surface - 4) {
              /*
               * And the bed under the water, which is where clay lies.
               *
               * There was none at all on this map - "не знайшов глини" -
               * because the adventure's ground was written without it while
               * the island world had it all along. Without clay there is no
               * піч, no bucket and no чан, which is the first half of the
               * game. It goes where it goes in the ground: in beds on the
               * floor of a lake, and in lenses inland where the soil gives
               * out (below).
               */
              id = drowned
                ? (beds[z * CHUNK_SIZE + x]! > BED_CLAY ? BlockId.Clay
                  : sandy ? BlockId.Sand : BlockId.Dirt)
                : biome === "desert" ? BlockId.Sand
                : biome === "mountain" || above > SNOW_LINE ? BlockId.Stone
                : above > 8 ? BlockId.Dirt
                : BlockId.Chornozem;
            } else {
              // The clay lens, right at the top of the rock, for whoever
              // settled inland and is nowhere near a lake.
              const lensTop = surface - 4;
              id = !drowned && wy > lensTop - CLAY_LENS_DEPTH &&
                lenses[z * CHUNK_SIZE + x]! > CLAY_LENS_THRESHOLD
                ? BlockId.Clay : BlockId.Stone;
            }
            blocks[blockIndex(x, y, z)] = id;
          }
        }
      }
    }

    this.carveCaves(cx, cz, made, heights);

    for (let cy = 0; cy < sizeY; cy++) {
      const chunk = made[cy]!;
      chunk.recount();
      /*
       * Never over a chunk somebody has changed.
       *
       * A save holds the seed and the chunks that differ from it, so loading
       * one puts a handful of chunks into a world whose country has not been
       * grown yet. The first walk towards them would generate straight over
       * the top - the house is gone and the save was not what was wrong.
       */
      if (this.modified.has(key(cx, cy, cz))) continue;
      if (!chunk.isEmpty) this.chunks.set(key(cx, cy, cz), chunk);
    }
  }

  /**
   * Caves, cut out of the rock a column has just been given.
   *
   * A 3D field rather than a 2D one: a cave goes down as well as along, and
   * a field that only knows x and z cuts the same hole through every layer
   * at once - a shaft, not a cave. Two fields, and a cell is open only where
   * both are near the middle of their range, which turns two clouds of blobs
   * into the winding passages where they happen to cross.
   *
   * Column by column rather than layer by layer, because that is the shape
   * the field is cheap to read in ([[noiseColumn3]]) - and because the cheap
   * version is the difference between a world that starts in a second and
   * one that starts in nine.
   *
   * Never through the last few blocks under the surface, or every hill would
   * be a lid over a hole and the first thing anybody did on this map would
   * be fall through the ground. And never into the bedrock, which is the
   * floor of the world.
   */
  private carveCaves(
    cx: number, cz: number, made: readonly Chunk[], heights: Int16Array,
  ): void {
    const { seed } = this.opts;
    const roof = made.length * CHUNK_SIZE - 1;
    /*
     * The ground here, or next door, whichever is lower.
     *
     * A cave stops five blocks under the surface so that no hill is a lid
     * over a hole - but "the surface" was this column's own, and a cave in a
     * rock beside a lake is five blocks under the *rock*, which is well
     * above the lake bed. It came out sideways into the water: the sea stood
     * against an open cave mouth with nothing holding it in, five blocks of
     * it, and the water does not pour in because none of this is simulated.
     *
     * Taking the lowest of the five means a cave is five blocks under
     * whatever the country does within a step of it - which is also the rule
     * that stops one opening out of a cliff face. Inside the chunk the
     * heights are already to hand; only the sixty-four columns along the
     * edge cost a noise lookup each.
     */
    const groundNear = (x: number, z: number): number => {
      if (x >= 0 && x < CHUNK_SIZE && z >= 0 && z < CHUNK_SIZE) {
        return heights[z * CHUNK_SIZE + x]!;
      }
      return this.adventureCell(
        cx * CHUNK_SIZE + x, cz * CHUNK_SIZE + z) & 0xffff;
    };
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const lowest = Math.min(
          heights[z * CHUNK_SIZE + x]!,
          groundNear(x - 1, z), groundNear(x + 1, z),
          groundNear(x, z - 1), groundNear(x, z + 1),
        );
        const top = Math.min(lowest - 5, roof);
        if (top < CAVE_FLOOR) continue;
        const wx = cx * CHUNK_SIZE + x;
        const wz = cz * CHUNK_SIZE + z;
        const bore = noiseColumn3(wx / 34, wz / 34, 22, seed + 5171);
        const cross = noiseColumn3(wx / 27, wz / 27, 18, seed + 9241);
        for (let wy = CAVE_FLOOR; wy <= top; wy++) {
          if (Math.abs(bore(wy) - 0.5) > CAVE_BORE) continue;
          if (Math.abs(cross(wy) - 0.5) > CAVE_BORE) continue;
          /*
           * Лава in the bottom of them.
           *
           * A level rather than a lining: what is under the lava level is
           * lava, so a passage that runs downhill ends at a shore and the
           * lava reads as something standing in the world rather than a
           * coat of paint on the rock. Deep enough that it is a thing you go
           * looking for - a cave near the surface is a cave to shelter in.
           */
          made[Math.floor(wy / CHUNK_SIZE)]!.blocks[
            blockIndex(x, wy % CHUNK_SIZE, z)
          ] =
            wy <= LAVA_DEPTH ? LAVA : BlockId.Air;
        }
      }
    }
  }

  /**
   * What grows on a column, once its neighbours have their ground.
   *
   * Ore seams, trees and wild plants, in that order - and every one of them
   * decided from the column's own coordinates and the seed, never from a
   * count of how big the world is. A world that need not exist all at once
   * cannot ask how many veins there are in it.
   */
  private adventureFeatures(cx: number, cz: number): void {
    const seaLevel = this.seaLevel;
    const worldHeight = this.opts.sizeY * CHUNK_SIZE;
    const heights = new Int16Array(CHUNK_SIZE * CHUNK_SIZE);
    const biomes = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
    const tables = new Int16Array(CHUNK_SIZE * CHUNK_SIZE);
    this.adventureSurface(cx, cz, heights, biomes, tables);

    this.seamsInColumn(cx, cz, worldHeight);
    this.treesInColumn(cx, cz, heights, biomes, tables, seaLevel, worldHeight);
    this.plantsInColumn(cx, cz, heights, tables, seaLevel);
    this.villageInColumn(cx, cz);
    this.lairInColumn(cx, cz);
    // Last, so nothing written after it can fill the hole in again.
    this.treasureInColumn(cx, cz);
  }

  /**
   * Where this district's лігво is cut, or null if no mountain in it will do.
   *
   * Kept, like a village's: the answer is asked again by every column the
   * hall touches and by every column merely near one, and working it out
   * means probing up to thirty-six places. Arithmetic on the seed, so it is
   * a cache and never state.
   */
  lairAt(gx: number, gz: number): { x: number; z: number; way: number } | null {
    const key = `${gx},${gz}`;
    const had = this.lairChoice.get(key);
    if (had !== undefined) return had;
    let found: { x: number; z: number; way: number } | null = null;
    for (const spot of lairTries(this.opts.seed, gx, gz)) {
      const way = this.lairFits(spot.x, spot.z);
      if (way === null) continue;
      found = { ...spot, way };
      break;
    }
    this.lairChoice.set(key, found);
    return found;
  }

  private readonly lairChoice =
    new Map<string, { x: number; z: number; way: number } | null>();

  /**
   * Will a mountain here hold a hall, and which way does the mouth face?
   *
   * Three things have to be true and all of them are about rock. The ground
   * has to be a mountain - "живе в горах" - so the biome says so or it
   * stands well above the sea. There has to be enough of it: a hall thirteen
   * blocks down and six high needs stone all the way, not a hilltop with the
   * sea under it. And the ground has to *fall away* on one side, or the
   * tunnel out has nowhere to break the surface and the dragon is sealed in
   * his own hall - which is the same fault the замок's gate had, and it was
   * found by walking to it rather than by looking at it.
   */
  private lairFits(x: number, z: number): number | null {
    if (!this.withinBounds(x - LAIR_REACH, 1, z - LAIR_REACH)) return null;
    if (!this.withinBounds(x + LAIR_REACH, 1, z + LAIR_REACH)) return null;
    const here = this.probeColumn(x, z);
    if (here.water !== null) return null;
    const above = here.ground - this.seaLevel;
    if (here.biome !== "mountain" && above < 40) return null;
    // Rock under it, all the way down to the floor of the hall.
    if (here.ground - HALL_DEPTH - HALL.y < 6) return null;
    /*
     * And a hillside to open on: the way the ground falls furthest, and it
     * has to fall far enough that a level tunnel comes out of it.
     */
    let best = -1, drop = 0;
    WAYS.forEach((w, i) => {
      const out = this.probeColumn(x + w.dx * 34, z + w.dz * 34);
      if (out.water !== null) return;
      const fall = here.ground - out.ground;
      if (fall > drop) { drop = fall; best = i; }
    });
    if (best < 0 || drop < HALL_DEPTH + 2) return null;
    return best;
  }

  /**
   * This column's share of whatever лігво is cut under it.
   *
   * Air and лава written into rock that is already there, so this runs after
   * the terrain and the caves and before the treasure - a скарб buried in a
   * cell the hall is about to hollow would end up standing in the hall.
   */
  private lairInColumn(cx: number, cz: number): void {
    const x0 = cx * CHUNK_SIZE, z0 = cz * CHUNK_SIZE;
    const x1 = x0 + CHUNK_SIZE - 1, z1 = z0 + CHUNK_SIZE - 1;
    const gx0 = Math.floor((x0 - LAIR_REACH) / LAIR_CELL);
    const gx1 = Math.floor((x1 + LAIR_REACH) / LAIR_CELL);
    const gz0 = Math.floor((z0 - LAIR_REACH) / LAIR_CELL);
    const gz1 = Math.floor((z1 + LAIR_REACH) / LAIR_CELL);
    for (let gz = gz0; gz <= gz1; gz++) {
      for (let gx = gx0; gx <= gx1; gx++) {
        const at = this.lairAt(gx, gz);
        if (at === null) continue;
        this.cutLair(at, x0, x1, z0, z1);
      }
    }
  }

  /** Hollow the part of one lair that falls inside this column. */
  private cutLair(
    at: { x: number; z: number; way: number },
    cx0: number, cx1: number, cz0: number, cz1: number,
  ): void {
    const top = this.probeColumn(at.x, at.z).ground;
    const mid = top - HALL_DEPTH;
    const way = WAYS[at.way]!;
    /*
     * The floor is лава for the first two courses, and the rest is air.
     *
     * "Його лігво" in the drawing is a cave with fire in the bottom of it,
     * and лава is what this game already has for that. Two courses, so the
     * floor reads as molten rather than as a hall with a puddle in it - and
     * a ledge of stone is left round the edge by the shape of the hall
     * itself, because an ellipsoid is narrow where it is deep.
     */
    const floor = mid - HALL.y;
    for (let z = Math.max(cz0, at.z - HALL.z); z <= Math.min(cz1, at.z + HALL.z); z++) {
      for (let x = Math.max(cx0, at.x - HALL.x); x <= Math.min(cx1, at.x + HALL.x); x++) {
        for (let y = floor; y <= mid + HALL.y; y++) {
          if (!inHall(x - at.x, y - mid, z - at.z)) continue;
          if (!this.inWorld(x, y, z)) continue;
          this.setGenerated(x, y, z, y <= floor + 2 ? LAVA : BlockId.Air);
        }
      }
    }

    /*
     * And the way out, level, in the direction the ground falls.
     *
     * It stops where the country has dropped below its own floor, which is
     * where it has broken the surface - worked out from the noise per cell,
     * so every column agrees about where the mouth is without any of them
     * having to be grown. Level and not sloping: a dragon walks out of this.
     */
    const sill = mid + 1;
    for (let t = 0; t <= 48; t++) {
      const bx = at.x + way.dx * t, bz = at.z + way.dz * t;
      const ground = this.probeColumn(bx, bz).ground;
      // Out in the open: the tunnel has arrived at the hillside.
      if (t > HALL.z && ground <= sill) break;
      for (let w = -BORE.wide; w <= BORE.wide; w++) {
        const px = bx + (way.dx === 0 ? w : 0);
        const pz = bz + (way.dz === 0 ? w : 0);
        if (px < cx0 || px > cx1 || pz < cz0 || pz > cz1) continue;
        for (let y = sill; y < sill + BORE.tall; y++) {
          if (!this.inWorld(px, y, pz)) continue;
          if (!isNaturalGround(this.getBlock(px, y, pz))) continue;
          this.setGenerated(px, y, pz, BlockId.Air);
        }
      }
    }
  }

  /**
   * The скарб buried under this column, if the district's one is here.
   *
   * One chest to a district and a district is 250 blocks, so most columns
   * have nothing to do here and say so after a single test. The chest is
   * written into solid rock: if the block it would occupy is a cave, or
   * water, or anything but the country's own stone and earth, that district
   * has no treasure. A chest standing in an open passage is not buried.
   */
  private treasureInColumn(cx: number, cz: number): void {
    const { seed } = this.opts;
    const x0 = cx * CHUNK_SIZE, z0 = cz * CHUNK_SIZE;
    const x1 = x0 + CHUNK_SIZE - 1, z1 = z0 + CHUNK_SIZE - 1;
    for (let gz = Math.floor(z0 / TREASURE_CELL);
         gz <= Math.floor(z1 / TREASURE_CELL); gz++) {
      for (let gx = Math.floor(x0 / TREASURE_CELL);
           gx <= Math.floor(x1 / TREASURE_CELL); gx++) {
        const spot = treasureSpot(seed, gx, gz);
        if (spot.x < x0 || spot.x > x1 || spot.z < z0 || spot.z > z1) continue;
        if (!this.withinBounds(spot.x, 1, spot.z)) continue;
        const here = this.probeColumn(spot.x, spot.z);
        // Dry land: a chest under the seabed is a chest nobody digs for.
        if (here.water !== null) continue;
        if (here.ground < this.seaLevel + 2) continue;
        const y = here.ground - spot.depth;
        if (y < 2) continue;
        /*
         * Solid rock on every side of it, not only where it goes.
         *
         * The cell itself is what does the work - two chests in twelve came
         * out standing in open cave air, and that is what the picture guard
         * caught. The six neighbours are belt and braces: on every seed
         * measured they have never once been the test that refused a spot,
         * because a cave that reaches a block has almost always taken it.
         * They are here for the one that runs right up to it and stops,
         * which would leave a скриня in a wall for anybody walking past.
         */
        /*
         * Solid *and* natural, and the first version only said natural.
         *
         * `isNaturalGround` answers "has anything been built here", and air
         * has not - so it is true of an empty cave, and the test it was
         * written for passed on chests hanging in one. Two in twelve.
         */
        const solidAt = (dx: number, dy: number, dz: number): boolean => {
          const id = this.getBlock(spot.x + dx, y + dy, spot.z + dz);
          return isSolid(id) && isNaturalGround(id);
        };
        if (!solidAt(0, 0, 0)) continue;
        if (!solidAt(1, 0, 0) || !solidAt(-1, 0, 0)) continue;
        if (!solidAt(0, 0, 1) || !solidAt(0, 0, -1)) continue;
        if (!solidAt(0, 1, 0) || !solidAt(0, -1, 0)) continue;
        this.setGenerated(spot.x, y, spot.z, chestId(0));
        this.stockTreasure(spot.x, y, spot.z);
      }
    }
  }

  /**
   * Fill one buried chest, once and only once.
   *
   * The same rule the village скрині live by, and for the same reason: this
   * column can be thrown away and grown again any number of times, so "has
   * this been filled" cannot be "is there anything in it". Empty a chest,
   * walk two hundred blocks, walk back, and without `markStocked` it is full
   * again - which turns a thing you find once into a thing you farm.
   */
  private stockTreasure(x: number, y: number, z: number): void {
    if (this.containers.at(x, y, z) !== null) return;
    const box = this.containers.open(x, y, z);
    TREASURE_LOOT.forEach((stack, i) => box.set(i, { ...stack }));
    this.containers.markStocked(x, y, z);
  }

  /**
   * This column's share of whatever village stands over it.
   *
   * Written per column, like the trees and the seams, and *not* like the
   * замок. The замок is one building cut once, so it had to be held in
   * memory for ever or it came back as the hillside the seed says is there.
   * There are sixty-odd villages in a world: holding them all is holding the
   * map. Growing each column's own share out of the seed means a village
   * that is forgotten and walked back to is the same village, because it was
   * never anywhere except in the arithmetic.
   *
   * Nine districts are asked about rather than one: a village sits anywhere
   * inside its own, and a column on a district border can be under the
   * village next door.
   */
  private villageInColumn(cx: number, cz: number): void {
    const { seed } = this.opts;
    const x0 = cx * CHUNK_SIZE, z0 = cz * CHUNK_SIZE;
    const x1 = x0 + CHUNK_SIZE - 1, z1 = z0 + CHUNK_SIZE - 1;
    const gx0 = Math.floor((x0 - VILLAGE_REACH) / VILLAGE_CELL);
    const gx1 = Math.floor((x1 + VILLAGE_REACH) / VILLAGE_CELL);
    const gz0 = Math.floor((z0 - VILLAGE_REACH) / VILLAGE_CELL);
    const gz1 = Math.floor((z1 + VILLAGE_REACH) / VILLAGE_CELL);

    for (let gz = gz0; gz <= gz1; gz++) {
      for (let gx = gx0; gx <= gx1; gx++) {
        const spot = this.villageAt(gx, gz);
        if (spot === null) continue;
        const floor = this.villageFloor(spot.x, spot.z);
        if (floor === null) continue;
        const village = planVillage(seed, spot);
        // Anything outside this column is dropped, so the column can be
        // grown in any order and still write only its own share.
        const put: Put = (x, y, z, id) => {
          if (x < x0 || x > x1 || z < z0 || z > z1) return;
          if (!this.inWorld(x, y, z)) return;
          this.setGenerated(x, y, z, id);
        };
        /*
         * Four passes, and the order of them is the whole design.
         *
         * Cut every footing, then raise every building, then sow the городи,
         * then put the furniture in. It was one pass doing level-and-build
         * part by part, and a part's footing is cut across its *padded* box -
         * three blocks out, to clear the eaves - so levelling for the second
         * хата blew a three-block hole of air through the first one's wall,
         * which was already standing. Three houses in forty had a wall
         * missing, the worst of them thirty cells of it, and a хата whose
         * bed was near that edge lost the bed: "десь зникли ліжка".
         *
         * Every write here is clipped to this column, so a cell is only ever
         * written while the column that owns it is being grown - which is
         * what makes an order *within* the column the whole of the answer.
         */
        const mine = village.parts.filter((part) => {
          const box = partBox(part);
          return !(box.x1 < x0 || box.x0 > x1 || box.z1 < z0 || box.z0 > z1);
        });
        /** Where a part stands, and whether its own ground will have it. */
        const standing = (part: Part): number | null => {
          if (!needsLevelling(part.kind)) return floor;
          const stands = this.partFloor(part);
          /*
           * A building standing where its own ground will not have it is not
           * built at all.
           *
           * Adapting to the surface is what a village does; standing on a
           * thirteen-block plinth of made earth is not adapting, it is
           * levelling the hill and pretending. So a part whose own footprint
           * drops more than a few blocks is left out, and the village has a
           * gap there - which is what a real village on a hillside has.
           */
          return this.tooSteepFor(part, stands) ? null : stands;
        };

        // 1. The ground. Cut flat under every part before anything is on it:
        // a хата half-buried in a slope is worse than no хата.
        for (const part of mine) {
          if (!needsLevelling(part.kind)) continue;
          const stands = standing(part);
          if (stands === null) continue;
          this.levelFor(put, partBox(part), stands, x0, x1, z0, z1);
        }
        // 2. The buildings, on ground nothing will cut again.
        for (const part of mine) {
          if (part.kind === "город") continue;
          const stands = standing(part);
          if (stands === null) continue;
          buildPart(put, part, stands,
            (gx2, gz2) => this.probeColumn(gx2, gz2).ground);
        }
        // 3. The городи, sown rather than built: soil, water and what is
        // growing in it, over ground that is finished with.
        for (const part of mine) {
          if (part.kind !== "город") continue;
          const stands = standing(part);
          if (stands === null) continue;
          buildPart(put, part, stands,
            (gx2, gz2) => this.probeColumn(gx2, gz2).ground);
        }
        // 4. And the furniture, which nothing writes after.
        for (const part of mine) {
          if (bedsIn(part.kind) === 0) continue;
          const stands = standing(part);
          if (stands === null) continue;
          furnish(put, part, stands);
          this.stockCottage(part, stands, x0, x1, z0, z1);
        }

      }
    }
  }

  /**
   * Where this district's village stands, or null if nowhere in it will do.
   *
   * Kept, and it has to be: the answer is asked for again by every column
   * the village touches and by every column merely *near* one, and working
   * it out means probing up to thirty-six places. It is arithmetic on the
   * seed, so this is a cache and never state - dropped, it comes back the
   * same, which is the rule the whole of «Пригода» is built on.
   */
  villageAt(gx: number, gz: number): { x: number; z: number } | null {
    const key = `${gx},${gz}`;
    const had = this.villageChoice.get(key);
    if (had !== undefined) return had;
    let found: { x: number; z: number } | null = null;
    for (const spot of villageTries(this.opts.seed, gx, gz)) {
      if (this.villageFloor(spot.x, spot.z) === null) continue;
      found = spot;
      break;
    }
    this.villageChoice.set(key, found);
    return found;
  }

  private readonly villageChoice =
    new Map<string, { x: number; z: number } | null>();

  /**
   * The one height a whole village stands at, or null where it may not be.
   *
   * One height for all of it, because a village on a slope with each хата at
   * its own level is a staircase. What decides it is the ground in the
   * middle; what decides whether there is a village at all is whether the
   * country round that point is flat enough and dry enough to hold one.
   *
   * Read from the noise, so every column that asks gets the same answer
   * without any of them having to be grown first.
   */
  private villageFloor(x: number, z: number): number | null {
    if (!this.withinBounds(x - VILLAGE_REACH, 1, z - VILLAGE_REACH)) return null;
    if (!this.withinBounds(x + VILLAGE_REACH, 1, z + VILLAGE_REACH)) return null;
    const middle = this.probeColumn(x, z);
    if (middle.water !== null) return null;
    if (middle.ground < this.seaLevel + 2) return null;
    if (middle.biome === "mountain") return null;
    /*
     * Water and mountain, and nothing else.
     *
     * "Село не обов'язково має бути на рівній поверхні - може бути де
     * завгодно крім води та гори, просто має адаптуватись під поверхню."
     *
     * There used to be a flatness rule here - no more than seven blocks of
     * drop across the site - and it was doing the work that the *buildings*
     * should do. It refused most of the country: the median drop across a
     * dry candidate in this world is about thirty, so seven turned down four
     * sites in five, and what it was protecting against was a village laid
     * out at one height with хати hanging in the air at the low end of it.
     *
     * That is fixed where it belongs. Each building now stands at the height
     * of the ground under *itself*, so the village follows the hillside
     * instead of ignoring it, and the rule here is the one that was asked
     * for: not in the water, not on a mountain.
     */
    let low = middle.ground, high = middle.ground;
    for (const [dx, dz] of [[-34, 0], [34, 0], [0, -34], [0, 34],
      [-24, -24], [24, 24], [-24, 24], [24, -24]]) {
      const there = this.probeColumn(x + dx!, z + dz!);
      if (there.water !== null) return null;
      if (there.biome === "mountain") return null;
      low = Math.min(low, there.ground);
      high = Math.max(high, there.ground);
    }
    // A cliff is a mountain whatever the biome calls it. This is not the old
    // flatness rule wearing a hat: that asked for seven blocks across the
    // site and this allows thirty, which is four storeys of hillside.
    if (high - low > VILLAGE_SLOPE) return null;
    return middle.ground;
  }

  /**
   * The height one building stands at.
   *
   * The middle of what is under its own footprint - not the village's own
   * level, which is what made a flat site necessary in the first place, and
   * not the highest or lowest corner either. The middle means a хата on a
   * slope is cut a little into the bank at the back and stands on a little
   * made ground at the front, which is what a хата on a slope looks like.
   * The highest would bury the front; the lowest would put it on a plinth.
   *
   * Read from the noise, like everything else about a village, so the column
   * holding the near corner and the column holding the far one agree without
   * either having been grown.
   */
  /**
   * What the скриня in a хата holds.
   *
   * A little food and a little of what can be put in the ground - a village
   * keeps its seed corn indoors, which is the whole reason a хата has a
   * chest in it. Everything is drawn from the seed and the chest's own
   * position, so the same хата holds the same things whoever walks into it
   * and however many times the country round it has been forgotten.
   *
   * Filled once, and `markStocked` is what makes that true. See there: the
   * alternative is a chest that refills every time somebody walks away from
   * it, which is a bread machine rather than a village.
   */
  private stockCottage(
    part: Part, stands: number,
    cx0: number, cx1: number, cz0: number, cz1: number,
  ): void {
    const cell = chestCell(part);
    // Only the column that holds it: every column near a village runs this.
    if (cell.x < cx0 || cell.x > cx1 || cell.z < cz0 || cell.z > cz1) return;
    const y = stands + 1;
    if (this.containers.at(cell.x, y, cell.z) !== null) return;
    /*
     * One number per decision, out of the chest's own position.
     *
     * Not a running generator: the columns round a village are grown in
     * whatever order somebody walks, and a stream of numbers would come out
     * differently depending on which хата was reached first.
     */
    const roll = (salt: number): number => {
      let h = ((cell.x * 374761393) + (cell.z * 668265263) +
        ((this.opts.seed + salt) * 2246822519)) >>> 0;
      h = (h ^ (h >>> 13)) >>> 0;
      h = (h * 1274126177) >>> 0;
      return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
    };
    const box = this.containers.open(cell.x, y, cell.z);
    const FOOD = [
      243 /* Pork */, 245 /* Beef */, 247 /* Karas */, 252 /* Chicken */,
    ];
    box.set(0, { id: 200 /* WheatSeeds */, count: 4 + Math.floor(roll(1) * 9) });
    box.set(1, { id: 214 /* FlaxSeeds */, count: 2 + Math.floor(roll(2) * 5) });
    box.set(3, {
      id: FOOD[Math.floor(roll(3) * FOOD.length)] ?? 243,
      count: 1 + Math.floor(roll(4) * 3),
    });
    this.containers.markStocked(cell.x, y, cell.z);
  }

  /** Is the ground under this building too broken to stand it on? */
  private tooSteepFor(
    part: { x: number; z: number; w: number; d: number }, stands: number,
  ): boolean {
    for (let z = part.z; z < part.z + part.d; z++) {
      for (let x = part.x; x < part.x + part.w; x++) {
        const ground = this.probeColumn(x, z).ground;
        if (Math.abs(ground - stands) > VILLAGE_STEP) return true;
      }
    }
    return false;
  }

  private partFloor(part: {
    x: number; z: number; w: number; d: number;
  }): number {
    const heights: number[] = [];
    for (const [fx, fz] of [[0, 0], [1, 0], [0, 1], [1, 1], [0.5, 0.5]]) {
      heights.push(this.probeColumn(
        part.x + Math.round(fx! * (part.w - 1)),
        part.z + Math.round(fz! * (part.d - 1))).ground);
    }
    heights.sort((m, n) => m - n);
    return heights[Math.floor(heights.length / 2)]!;
  }

  /**
   * Every лігво this seed has, without growing any of it.
   *
   * `dragonLairs` and not `lairs`, because `lairs` is taken: it is the
   * register of orc spawners, which are also lairs and are nothing to do
   * with this.
   *
   * Cheap, unlike `treasures()`: whether a mountain will hold a hall is a
   * question about the shape of the ground, and the ground is noise. Nothing
   * has to be carved to answer it.
   */
  dragonLairs(): { x: number; y: number; z: number }[] {
    const span = this.opts.sizeXZ * CHUNK_SIZE;
    // «Один блок» has one, on its island, and it is not on the grid.
    if (this.opts.terrain === "one") return [oneIsland(span).lair];
    const out: { x: number; y: number; z: number }[] = [];
    const last = Math.floor(span / LAIR_CELL);
    for (let gz = 0; gz <= last; gz++) {
      for (let gx = 0; gx <= last; gx++) {
        const at = this.lairAt(gx, gz);
        if (at === null) continue;
        out.push({
          x: at.x, y: this.probeColumn(at.x, at.z).ground - HALL_DEPTH, z: at.z,
        });
      }
    }
    return out;
  }

  /**
   * Every скарб this seed has, and how deep each one lies.
   *
   * Unlike `villages()`, this cannot be answered out of the noise alone: a
   * district has a treasure only if there is solid rock where it would go,
   * and whether a cave has eaten that block is a fact about blocks. So the
   * cheap tests come first - off the map, under water, too near the surface
   * - and only what survives them is grown and looked at. Most districts are
   * refused before a single column is made.
   *
   * For the seed map, which is the only thing that wants the list. A player
   * finds these by digging.
   */
  treasures(): { x: number; y: number; z: number; depth: number }[] {
    const span = this.opts.sizeXZ * CHUNK_SIZE;
    const out: { x: number; y: number; z: number; depth: number }[] = [];
    const last = Math.floor(span / TREASURE_CELL);
    for (let gz = 0; gz <= last; gz++) {
      for (let gx = 0; gx <= last; gx++) {
        const spot = treasureSpot(this.opts.seed, gx, gz);
        if (!this.withinBounds(spot.x, 1, spot.z)) continue;
        const here = this.probeColumn(spot.x, spot.z);
        if (here.water !== null) continue;
        if (here.ground < this.seaLevel + 2) continue;
        const y = here.ground - spot.depth;
        if (y < 2) continue;
        // Grown, because the answer is about rock and a cave is not in the
        // noise. One column, not the country round it: the cell the chest
        // goes in belongs to exactly one.
        this.ensureColumn(floorDiv(spot.x, CHUNK_SIZE), floorDiv(spot.z, CHUNK_SIZE));
        if (!isChest(this.getBlock(spot.x, y, spot.z))) continue;
        out.push({ x: spot.x, y, z: spot.z, depth: spot.depth });
      }
    }
    return out;
  }

  /**
   * Somewhere a body actually fits, at or just above a cell.
   *
   * Two clear cells, because a person is two blocks tall. What wants it is
   * putting a household into a хата: the plan says "the middle of the room",
   * and on the outer ring two хати can be built close enough that one of
   * them lays its floor across the other's. Somebody put down inside that
   * floor is a body inside a block, and the collision shoves it out through
   * the wall.
   *
   * @returns null when there is no room within `up` blocks, which is the
   * honest answer for a room somebody has filled in.
   */
  roomFor(
    x: number, y: number, z: number, up = 3,
  ): { x: number; y: number; z: number } | null {
    const cx = Math.floor(x);
    const cz = Math.floor(z);
    for (let dy = 0; dy <= up; dy++) {
      const cy = Math.floor(y) + dy;
      if (isSolid(this.getBlock(cx, cy, cz))) continue;
      if (isSolid(this.getBlock(cx, cy + 1, cz))) continue;
      return { x: cx + 0.5, y: cy, z: cz + 0.5 };
    }
    return null;
  }

  /**
   * Cut a road between two places that a walker can actually walk.
   *
   * "Для того щоб вони могли дійти - додай сходи і до замку, і до острова
   * гравця." An орк steps up one block and no more, and the two islands are
   * domes: measured on the seed the game ships with, the shore of the far
   * island climbs six blocks in two, and the кремль sits on a spike above
   * that. An army that cannot get up a beach is an army that stands on it.
   *
   * So the road is cut the way a road is cut. It follows the straight line
   * between the two points, and its height follows the ground *but never
   * changes by more than a block at a time* - which is the whole of it:
   * everything above is taken away and everything below is filled in, so
   * what is left is a ramp anything on legs can climb.
   *
   * Written with `setBlock` rather than `setGenerated`, so the road is a
   * change to the world like any other: it is saved with the chunks it is
   * in, and a world that had one still has one when it comes back.
   *
   * @returns how many cells it changed, which is how a check knows it ran.
   */
  cutRoad(
    from: { x: number; z: number },
    to: { x: number; z: number },
    wide = 2,
    surface: number = BlockId.Dirt,
    /**
     * The level the last stretch has to arrive at, if it has one.
     *
     * For the кремль's gate, whose sill is wherever the fortress was
     * founded and has nothing to do with the hillside in front of it. The
     * road simply aims at that number for its last sixteen steps and the
     * one-course-a-step clamp turns it into a ramp - which is the same ramp
     * `openKremlinGate` cuts, and the reason the two no longer fight over
     * the same corridor: whichever runs last is right, and the road runs
     * last.
     */
    endLevel?: number,
  ): number {
    const span = Math.hypot(to.x - from.x, to.z - from.z);
    const steps = Math.ceil(span);
    if (steps <= 0) return 0;
    let touched = 0;
    /*
     * The road starts at the ground it starts on, and climbs by a block a
     * step at most. Below the waterline it is a causeway: a road that dips
     * into the sea is a road nobody uses, and the island's own neck is
     * already exactly this.
     */
    let level = Math.max(
      this.seaLevel + 1, this.groundAt(Math.round(from.x), Math.round(from.z)));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const cx = from.x + (to.x - from.x) * t;
      const cz = from.z + (to.z - from.z) * t;
      const ground = Math.max(
        this.seaLevel + 1, this.groundAt(Math.round(cx), Math.round(cz)));
      // The last stretch aims at the sill rather than at the hillside.
      const want = endLevel !== undefined && steps - i <= KREMLIN_APPROACH
        ? endLevel : ground;
      level += Math.max(-1, Math.min(1, want - level));
      /*
       * Across the line as well as along it, because a road one block wide
       * is a tightrope: a band of orcs shoulder to shoulder needs room, and
       * they push each other sideways as they walk.
       */
      const ax = -(to.z - from.z) / span;
      const az = (to.x - from.x) / span;
      for (let w = -wide; w <= wide; w++) {
        const x = Math.round(cx + ax * w);
        const z = Math.round(cz + az * w);
        if (!this.inWorld(x, level, z)) continue;
        if (this.getBlock(x, level, z) !== surface) {
          this.setBlock(x, level, z, surface);
          touched++;
        }
        /*
         * Headroom above, and ground under the edges of the cut.
         *
         * Six rather than three: three is a doorway, and a road that climbs
         * into a bank needs the bank taken off it. On the seeds where the
         * кремль sits in a hollow the last stretch cuts through eight or
         * nine blocks of hillside, and with three of headroom the walker
         * met the ceiling of their own trench.
         */
        /*
         * Up until the sky, not up by a fixed six.
         *
         * Six was headroom for a walker, and on a slope that is the wrong
         * question: a road cut six blocks into a hillside leaves everything
         * above the sixth block standing on nothing. That is the bug the
         * game showed as "сходи висять в повітрі" - the stepped hillside
         * the road had carved out from under, hanging over the cutting.
         *
         * So the cut is opened to the air above it. Three clear blocks in a
         * row is the sky: fewer than that and it could be the roof of a
         * cave the road has just broken into, which has to come down too.
         */
        let clear = 0;
        for (let up = 1; up <= ROAD_HEADROOM && clear < 3; up++) {
          if (this.getBlock(x, level + up, z) === BlockId.Air) { clear++; continue; }
          clear = 0;
          this.setBlock(x, level + up, z, BlockId.Air);
          touched++;
        }
        /*
         * And down to whatever it is standing on, however far that is.
         *
         * Six deep was a guess, and "сходи висять в повітрі" is what a guess
         * looks like from inside the game: where the hillside falls away
         * faster than six blocks a step - the flank of the near island, the
         * lip of the кремль's plateau - the road kept its ramp and the
         * ground under it did not, so the last stretch stood on nothing.
         *
         * Measured across four seeds before this was changed: 12 to 37 cells
         * of road hanging, over gaps of up to six blocks. The footing now
         * stops when it meets something solid rather than after a fixed
         * count, and only ROAD_FOOTING deep so that a road out over open
         * water builds a pier rather than a wall to the seabed.
         */
        for (let down = 1; down <= ROAD_FOOTING; down++) {
          if (isSolid(this.getBlock(x, level - down, z))) break;
          this.setBlock(x, level - down, z, BlockId.Dirt);
          touched++;
        }
      }
    }
    return touched;
  }

  /**
   * Every dwelling near a point, with the floor it stands on and the plot
   * that belongs to it.
   *
   * What puts people in a village. It has to be answered here rather than by
   * whoever wants it, because "was this хата actually built" is `partFloor`
   * and `tooSteepFor` - the same two questions the builder asks - and a
   * village on a hillside has gaps in it where the answer was no. People
   * standing in a house that was never built is exactly the bug that would
   * cause, and it is invisible until you walk up to it.
   *
   * Arithmetic and probes, like everything else about a village: the same
   * point gives the same houses every time, whether or not anybody has been
   * here before.
   */
  homesNear(x: number, z: number, reach: number): {
    house: Part; floor: number; plot: { x: number; y: number; z: number } | null;
  }[] {
    const out: {
      house: Part; floor: number;
      plot: { x: number; y: number; z: number } | null;
    }[] = [];
    const gx0 = Math.floor((x - reach) / VILLAGE_CELL);
    const gx1 = Math.floor((x + reach) / VILLAGE_CELL);
    const gz0 = Math.floor((z - reach) / VILLAGE_CELL);
    const gz1 = Math.floor((z + reach) / VILLAGE_CELL);
    for (let gz = gz0; gz <= gz1; gz++) {
      for (let gx = gx0; gx <= gx1; gx++) {
        const spot = this.villageAt(gx, gz);
        if (spot === null) continue;
        if (this.villageFloor(spot.x, spot.z) === null) continue;
        const homes = dwellings(planVillage(this.opts.seed, spot));
        /*
         * Every plot in this village that was actually dug.
         *
         * Gathered before anybody is paired with one, because a household
         * whose own город is on ground too steep for it has to work
         * *somewhere* - three in ten of them did, and a villager with
         * nowhere to work stands about all day. They take the nearest one
         * that was built instead, which is what a neighbour does.
         */
        const plots: { x: number; y: number; z: number }[] = [];
        for (const { garden } of homes) {
          if (garden === null) continue;
          const bed = this.partFloor(garden);
          if (this.tooSteepFor(garden, bed)) continue;
          plots.push(plotCell(garden, bed));
        }
        for (const { house, garden } of homes) {
          if (Math.hypot(house.x - x, house.z - z) > reach) continue;
          const floor = this.partFloor(house);
          if (this.tooSteepFor(house, floor)) continue;
          let plot: { x: number; y: number; z: number } | null = null;
          if (garden !== null) {
            const bed = this.partFloor(garden);
            if (!this.tooSteepFor(garden, bed)) plot = plotCell(garden, bed);
          }
          if (plot === null) {
            for (const other of plots) {
              if (plot === null ||
                Math.hypot(other.x - house.x, other.z - house.z) <
                Math.hypot(plot.x - house.x, plot.z - house.z)) plot = other;
            }
          }
          out.push({ house, floor, plot });
        }
      }
    }
    return out;
  }

  /**
   * Every село this seed has, without growing any of it.
   *
   * A village is written out of the seed and its own position - see
   * `village.ts` - so where the villages are is a question about noise and
   * nothing else, and this answers it the same way the generator does: ask
   * each district where its village would stand, then ask whether the
   * country there will hold one.
   *
   * For the seed map, which is the only thing that wants the whole list.
   * Somebody could not find a single village to look at, and the country is
   * four thousand blocks across with one село to every five hundred: walking
   * is not a search. Sixty-four districts at nine probes each is about a
   * millisecond, so it is asked outright rather than cached.
   */
  villages(): { x: number; z: number }[] {
    const span = this.opts.sizeXZ * CHUNK_SIZE;
    const out: { x: number; z: number }[] = [];
    const last = Math.floor(span / VILLAGE_CELL);
    for (let gz = 0; gz <= last; gz++) {
      for (let gx = 0; gx <= last; gx++) {
        const spot = this.villageAt(gx, gz);
        if (spot === null) continue;
        out.push(spot);
      }
    }
    return out;
  }

  /**
   * Cut a building's footprint into the ground and fill under it.
   *
   * Only the part of it in this column, and only up to the height the
   * village stands at: what is above goes, and what is missing below is made
   * up with earth, so the same wall meets the same floor on both sides of a
   * column border.
   */
  private levelFor(
    put: Put, box: { x0: number; x1: number; z0: number; z1: number },
    floor: number, cx0: number, cx1: number, cz0: number, cz1: number,
  ): void {
    const from = Math.max(box.x0, cx0), to = Math.min(box.x1, cx1);
    const fromZ = Math.max(box.z0, cz0), toZ = Math.min(box.z1, cz1);
    for (let z = fromZ; z <= toZ; z++) {
      for (let x = from; x <= to; x++) {
        const ground = this.probeColumn(x, z).ground;
        for (let y = floor + 1; y <= ground + 6; y++) put(x, y, z, BlockId.Air);
        for (let y = Math.min(ground, floor); y <= floor; y++) {
          put(x, y, z, BlockId.Dirt);
        }
      }
    }
  }

  /**
   * The ore seams that start in this column.
   *
   * The old version asked how many veins there are in the world and threw
   * that many darts at it. A world with no size cannot answer the question,
   * so the count is per column instead: how many a column of this size
   * deserves, with the fraction left over decided by a coin the column
   * tosses itself. Same density, no total.
   *
   * A seam that wanders over the border is welcome to - that is what the
   * neighbour rule is for, and a seam is at most a few blocks wide.
   */
  private seamsInColumn(cx: number, cz: number, worldHeight: number): void {
    const { seed } = this.opts;
    const cells = CHUNK_SIZE * CHUNK_SIZE;

    for (const ore of World.ORES) {
      const low = Math.max(1, ore.floorBand !== undefined
        ? 1
        : ore.shore !== undefined
          ? this.seaLevel - ore.shore.below
          : Math.floor(ore.from * worldHeight));
      const high = Math.min(worldHeight - 1, ore.floorBand !== undefined
        ? ore.floorBand
        : ore.shore !== undefined
          ? this.seaLevel + ore.shore.above
          : Math.floor(ore.to * worldHeight));
      if (high <= low) continue;

      if (ore.host === BlockId.Stone) {
        // Stone is most of what is down there, so a seed thrown at the band
        // lands in it. The walk sorts out the few that miss.
        const want = (cells * (high - low)) / ore.rarity;
        const veins = this.howMany(want, cx, cz, seed + ore.salt);
        for (let i = 0; i < veins; i++) {
          const x = cx * CHUNK_SIZE +
            Math.floor(hashInt(i, ore.salt, seed + cx * 31 + cz * 131) * CHUNK_SIZE);
          const z = cz * CHUNK_SIZE +
            Math.floor(hashInt(i, ore.salt + 1, seed + cx * 31 + cz * 131) * CHUNK_SIZE);
          const y = low + Math.floor(
            hashInt(i, ore.salt + 2, seed + cx * 31 + cz * 131) * (high - low));
          this.growVein(ore, x, y, z, seed + ore.salt + i + cx * 7919 + cz * 104729);
        }
        continue;
      }

      /*
       * Sand is a shell a few blocks thick along the shores, not a mass.
       *
       * A seed thrown at the band lands in stone or in air essentially every
       * time: thrown blind, the titanium sands came out at twenty-one blocks
       * and four in a whole world. So the sand in this column is found first
       * and the seams are planted in it - and a column with no sand in it
       * gets no titanium, which is the point of the ore.
       */
      const host = this.hostInColumn(ore.host, cx, cz, low, high);
      if (host.length === 0) continue;
      const veins = this.howMany(host.length / ore.rarity, cx, cz, seed + ore.salt);
      for (let i = 0; i < veins; i++) {
        const pick = host[Math.floor(
          hashInt(i, ore.salt, seed + cx * 31 + cz * 131) * host.length)];
        if (pick === undefined) break;
        this.growVein(
          ore, pick.x, pick.y, pick.z,
          seed + ore.salt + i + cx * 7919 + cz * 104729,
        );
      }
    }
  }

  /**
   * A count from an average that is usually less than one.
   *
   * A column deserves 0.3 of a granite seam; it cannot have 0.3 of one. The
   * whole part is certain and the fraction is a coin this column tosses, so
   * three columns in ten get a seam and the density over any stretch of
   * country is the average asked for.
   */
  private howMany(want: number, cx: number, cz: number, salt: number): number {
    const whole = Math.floor(want);
    return whole + (hashInt(cx, cz, salt) < want - whole ? 1 : 0);
  }

  /**
   * Cells of a given host inside this column, within a band of heights.
   *
   * Counts the ores that came out of that host as well as the host itself,
   * and that is not a detail: a column's features are run again whenever a
   * neighbour of it is forgotten and regrown, and the second run has to place
   * the same seams as the first. Counting bare sand alone, the first run
   * finds a thousand grains and the second finds nine hundred - because the
   * first run turned a hundred of them into titanium - so the count comes out
   * different, the seams land somewhere else, and walking out of a country
   * and back leaves ore scattered where there was sand. Which is what the
   * check about walking back caught, four blocks of it.
   */
  private hostInColumn(
    host: BlockId, cx: number, cz: number, low: number, high: number,
  ): { x: number; y: number; z: number }[] {
    const fromHost = new Set<number>([host]);
    for (const ore of World.ORES) if (ore.host === host) fromHost.add(ore.block);

    /*
     * Read out of the chunks, not through getBlock.
     *
     * This walks a band thirty-four blocks deep across the whole footprint of
     * the column - nine thousand cells - and it runs once for every ore that
     * grows in sand. Through `getBlock` that is a chunk lookup and an index
     * per cell and it cost twelve milliseconds a column, which is most of
     * what an adventure spends being born. It is the third time this exact
     * loop has been written the slow way in this file.
     */
    const found: { x: number; y: number; z: number }[] = [];
    for (let cy = floorDiv(low, CHUNK_SIZE); cy <= floorDiv(high, CHUNK_SIZE); cy++) {
      const chunk = this.chunks.get(key(cx, cy, cz));
      if (chunk === undefined) continue;
      const blocks = chunk.blocks;
      const from = Math.max(low, cy * CHUNK_SIZE);
      const to = Math.min(high, cy * CHUNK_SIZE + CHUNK_SIZE - 1);
      for (let y = from; y <= to; y++) {
        for (let lz = 0; lz < CHUNK_SIZE; lz++) {
          for (let lx = 0; lx < CHUNK_SIZE; lx++) {
            if (!fromHost.has(blocks[blockIndex(lx, y - cy * CHUNK_SIZE, lz)]!)) {
              continue;
            }
            found.push({
              x: cx * CHUNK_SIZE + lx, y, z: cz * CHUNK_SIZE + lz,
            });
          }
        }
      }
    }
    return found;
  }

  /**
   * The trees whose trunks stand in this column.
   *
   * The seven-by-seven cell grid the planter has always used is already
   * decided by position and seed alone, so a column need only look at the
   * cells that could put a trunk in it. A crown that hangs over the border
   * is fine: the ground next door is already there.
   *
   * The thinning by country happens here rather than in a pass afterwards.
   * It used to run over the whole world once everything was planted, which a
   * column cannot do - it would have to wait for neighbours that may never be
   * visited. Asking at planting time gives the same wood, and leaves a tree
   * that leans over a desert border whole instead of shaved down the middle.
   */
  private treesInColumn(
    cx: number, cz: number,
    heights: Int16Array, biomes: Uint8Array, tables: Int16Array,
    seaLevel: number, worldHeight: number,
  ): void {
    const { seed } = this.opts;
    const CELL = 7;
    const x0 = cx * CHUNK_SIZE, z0 = cz * CHUNK_SIZE;
    const x1 = x0 + CHUNK_SIZE - 1, z1 = z0 + CHUNK_SIZE - 1;

    for (let cellZ = Math.floor(z0 / CELL); cellZ <= Math.floor(z1 / CELL); cellZ++) {
      for (let cellX = Math.floor(x0 / CELL); cellX <= Math.floor(x1 / CELL); cellX++) {
        // Moisture decides both whether a tree grows and which kind. Oaks
        // gather in the damp hollows, acacias hold the dry open steppe.
        const moisture = fbm2(
          (cellX * CELL) / 70, (cellZ * CELL) / 70, seed + 991, 3,
        );
        const density = moisture > FOREST_DAMP ? FOREST_THICK : FOREST_OPEN;
        for (let seat = 0; seat < TREE_SEATS; seat++) {
        const roll = hashInt(cellX * 4 + seat, cellZ, seed + 5501);
        if (roll > density) continue;

        const x = cellX * CELL +
          Math.floor(hashInt(cellX + seat * 911, cellZ, seed + 17) * CELL);
        const z = cellZ * CELL +
          Math.floor(hashInt(cellX, cellZ + seat * 733, seed + 23) * CELL);
        // Only the trunks that stand here: a cell straddles the border, and
        // the column next door will take the ones on its own side.
        if (x < x0 || x > x1 || z < z0 || z > z1) continue;
        const span = this.opts.sizeXZ * CHUNK_SIZE;
        if (x < 3 || z < 3 || x >= span - 3 || z >= span - 3) continue;

        /*
         * The column's own ground and its own water, out of the arrays the
         * surface pass has already filled.
         *
         * Not out of `adventureLevel`, which is the same field with one
         * settling pass instead of three - and one pass leaves the table
         * standing over ground the terrain has since drained. Asked that
         * way, half the country came back as underwater and half the trees
         * in the world were refused for standing in a lake that is not
         * there. The trunk is always inside this column by the test above,
         * so the arrays have the answer.
         */
        const at = (z - z0) * CHUNK_SIZE + (x - x0);
        const ground = heights[at]!;
        // Nothing grows below the waterline - whichever water it is, the sea
        // or a lake standing above it - nor on the beach, nor on bare rock.
        if (!dryGround(ground, tables[at]!, seaLevel)) continue;
        if (ground > seaLevel + TREE_LINE) continue;

        // And nothing grows in a country that has no trees in it.
        const keep = treeChance(
          ADVENTURE_KINDS[biomes[at]!]!, ground - seaLevel);
        if (keep < 1 && hashInt(x, z, seed + 4457) >= keep) continue;

        const maxTop = worldHeight - 2;
        /*
         * Three species, and height decides between two of them the way
         * moisture decides the other. Сосна takes the high dry ground: it is
         * the tree that grows where the soil is poor, which is also the only
         * place in this world nothing else wanted.
         *
         * Only the highest ground, which is about a fifth of what a tree can
         * grow on. The first version said +14, which is fifty-eight per cent
         * of it: the world came out almost entirely сосна and the acacia
         * nearly vanished, which is not a thing that was asked for.
         */
        if (ground > seaLevel + PINE_LINE) {
          this.growPine(x, ground, z, seed, maxTop, false);
        } else if (moisture > 0.52) {
          this.growOak(x, ground, z, seed, maxTop, false);
        } else {
          this.growAcacia(x, ground, z, seed, maxTop, false);
        }
        }
      }
    }
  }

  /** Wheat and flax, on the open grass of this column. */
  private plantsInColumn(
    cx: number, cz: number, heights: Int16Array, tables: Int16Array,
    seaLevel: number,
  ): void {
    const { seed } = this.opts;
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const x = cx * CHUNK_SIZE + lx;
        const z = cz * CHUNK_SIZE + lz;
        const ground = heights[lz * CHUNK_SIZE + lx]!;
        // Whichever water is here: a lake bed grows no wheat either.
        if (!dryGround(ground, tables[lz * CHUNK_SIZE + lx]!, seaLevel)) continue;
        // Only on open grass with air above: not on sand, not under a tree.
        if (this.getBlock(x, ground, z) !== BlockId.Grass) continue;
        if (this.getBlock(x, ground + 1, z) !== BlockId.Air) continue;
        this.sowWildPlant(x, ground, z, seed);
      }
    }
  }
  /**
   * Король гори: a floor, and a cube standing on it.
   *
   * No noise, no heightmap, no ores, no caves, no water and no lairs. The
   * whole map is one shape, and every block in it is the same block - which
   * is what makes it a race rather than an expedition.
   *
   * The floor is stone rather than more вибухоблок: something has to be safe
   * to stand on at the bottom, or a player who fell off would land on a live
   * block and the map would kill you for losing. It is also what stops
   * anybody digging out of the world.
   */
  /**
   * «Один блок»: nothing at all, and one block in the middle of it.
   *
   * No floor, no sea, no sky island - the emptiness is the map. Even the
   * bottom of the world is left open: a незламний блок down there would be
   * something to land on, and there is meant to be nothing to land on.
   *
   * The chunks are still generated, or the world would grow terrain the
   * first time somebody walked into a chunk nobody had asked about yet.
   */
  private generateOneBlock(): void {
    const span = this.opts.sizeXZ * CHUNK_SIZE;
    const at = oneBlockAt(span);
    this.setGenerated(at.x, at.y, at.z, BlockId.Random);
    this.oneBlockAt = at;
    /*
     * And the far island, which is the one other thing on this map.
     *
     * Walked cell by cell over its own box - see `oneIsland` for what is
     * in it and why. Written at generation like the block, so the page and
     * the server hold the same island without a word between them.
     */
    const { box, centre } = oneIsland(span);
    for (let y = box.y0; y <= box.y1; y++) {
      for (let z = box.z0; z <= box.z1; z++) {
        for (let x = box.x0; x <= box.x1; x++) {
          const id = oneIslandBlock(span, x, y, z);
          if (id !== null) this.setGenerated(x, y, z, id);
        }
      }
    }
    // And its trees, grown by the same grower as everybody else's.
    for (const tree of oneIslandTrees(span)) {
      this.growTree(tree.x, centre.y, tree.z, tree.kind);
    }
  }

  /**
   * Where the шопопало-блок stands, on the map that has one.
   *
   * Remembered rather than searched for: it is the spawn, it is where
   * anybody who dies comes back to while there is no bed, and it is the one
   * cell the break rule cares about.
   */
  oneBlockAt: { x: number; y: number; z: number } | null = null;

  private generateHill(): void {
    const span = this.opts.sizeXZ * CHUNK_SIZE;
    // One number, in kingHill.ts, because the crown's minimum height is
    // measured from it and the two were briefly two numbers in two files.
    const FLOOR = HILL_FLOOR;

    for (let x = 0; x < span; x++) {
      for (let z = 0; z < span; z++) {
        for (let y = 0; y < FLOOR; y++) {
          this.setGenerated(x, y, z, BlockId.Stone);
        }
      }
    }

    /*
     * Centred, and sitting on the floor.
     *
     * A hundred on a side inside a world of `span`, so there is a ring of
     * open floor round it to arrive on and to fall onto. If the world were
     * ever configured too small for that the cube is clamped rather than
     * spilling over the edge - a hill flush with the wall of the world would
     * be climbable up the wall.
     */
    const wide = Math.min(HILL_SPAN, span - 8);
    const from = Math.floor((span - wide) / 2);
    const tall = Math.min(HILL_SPAN, this.opts.sizeY * CHUNK_SIZE - FLOOR - 4);
    for (let x = from; x < from + wide; x++) {
      for (let z = from; z < from + wide; z++) {
        for (let y = FLOOR; y < FLOOR + tall; y++) {
          this.setGenerated(x, y, z, BlockId.Boom);
        }
      }
    }
    this.lairs.clear();
  }

  /**
   * Скрині з порохом, buried across the plain.
   *
   * "Нехай він буде захований в землі у скринях, нехай його буде повна
   * скриня, туди ж додай гуму." One to a district of forty-eight blocks, so
   * every quarter of the map has several and no team's corner is empty -
   * this is the only source of either material in the game, and a match
   * where one side found powder and the other did not would be over before
   * it started.
   *
   * Sealed in rock like a скарб, and for the same reason: a chest standing
   * in a passage is a chest you walk past, not one you dig for.
   */
  private buryWarSupplies(span: number): void {
    const { seed } = this.opts;
    const last = Math.floor((span - 1) / SUPPLY_CELL);
    for (let gz = 0; gz <= last; gz++) {
      for (let gx = 0; gx <= last; gx++) {
        const spot = supplySpot(seed, gx, gz);
        if (!this.withinBounds(spot.x, 1, spot.z)) continue;
        const y = WAR_GROUND - spot.depth;
        if (y < 2) continue;
        const solidAt = (dx: number, dy: number, dz: number): boolean => {
          const id = this.getBlock(spot.x + dx, y + dy, spot.z + dz);
          return isSolid(id) && isNaturalGround(id);
        };
        if (!solidAt(0, 0, 0)) continue;
        if (!solidAt(1, 0, 0) || !solidAt(-1, 0, 0)) continue;
        if (!solidAt(0, 0, 1) || !solidAt(0, 0, -1)) continue;
        if (!solidAt(0, 1, 0) || !solidAt(0, -1, 0)) continue;
        this.setGenerated(spot.x, y, spot.z, chestId(0));
        this.stockWarChest(spot.x, y, spot.z);
      }
    }
  }

  /**
   * Fill one of them, once and only once.
   *
   * Twenty slots of порох and seven of гума: "повна скриня" taken at its
   * word. A tank is one chest's worth of rubber and a long afternoon of
   * shells is one chest's worth of powder, which is the trade this map is
   * meant to have - the digging is the cost, not the rationing.
   */
  private stockWarChest(x: number, y: number, z: number): void {
    if (this.containers.at(x, y, z) !== null) return;
    const box = this.containers.open(x, y, z);
    for (let slot = 0; slot < 27; slot++) {
      box.set(slot, slot < 20
        ? { id: PlainItemId.Gunpowder, count: 64 }
        : { id: PlainItemId.Rubber, count: 32 });
    }
    this.containers.markStocked(x, y, z);
  }

  /**
   * Cut the way up to the кремль's gate.
   *
   * The fortress is raised with facing 3, which puts its front face one
   * block east of `bx` and the gate's passage across `bz` - see `offsetFor`
   * and the anchor at local x 30. So the approach runs west from the face,
   * three blocks wide, climbing or falling to the sill at no more than a
   * block a step: exactly the step a player can take, and exactly what the
   * замок's `openApproach` does.
   *
   * Built rather than guessed, and for the reason written over there: every
   * heuristic version of this traded one seed for another.
   */
  private openKremlinGate(bx: number, by: number, bz: number): void {
    const reach = KREMLIN_APPROACH;
    // The floor of the passage is the building's own local nought, which is
    // the course `by` sits on.
    const sill = by;
    if (this.probeColumn(bx - reach, bz).ground < 0) return;

    /*
     * The way out never climbs above the gate it comes from.
     *
     * The кремль is founded fifteen blocks into the island's dome, so the
     * ground immediately outside its gate is usually *higher* than the sill -
     * on the seed the game ships with it rises thirteen blocks in sixteen and
     * then falls off a cliff into the sea. Aimed at that ground, the approach
     * became a stepped brick wall climbing out of the gate to the lip of the
     * hollow and stopping there in the air, which is what the second
     * screenshot showed.
     *
     * A fortress gate does not have stairs up to the country around it. It
     * has a cutting: the way out stays at the sill, the hillside over it is
     * taken off, and it only steps *down* - and then only when the ground
     * ahead has fallen below the sill and it has somewhere to step down to.
     * Which is also how it meets the road, since the road arrives at the sill.
     */
    let floor = sill;
    for (let d = 0; d <= reach; d++) {
      // `bx` is the first column outside the face: the face itself is bx + 1.
      const x = bx - d;
      const ahead = Math.max(
        this.seaLevel + 1, this.probeColumn(x, bz).ground);
      const want = Math.min(sill, ahead);
      floor += Math.max(-1, Math.min(0, want - floor));
      for (let dz = -KREMLIN_STAIR_HALF; dz <= KREMLIN_STAIR_HALF; dz++) {
        const z = bz + dz;
        if (!this.inWorld(x, floor, z)) continue;
        /*
         * Masonry, and all the way down to whatever it is standing on.
         *
         * "Зроби сходи частиною кремліна." This used to be a ramp of earth
         * filled eight blocks deep, and the кремль stands on a plateau whose
         * western edge is a cliff into the sea - so the steps walked out over
         * that edge, ran out of fill and hung there. That is the "сходи
         * висять в повітрі" in the screenshot.
         *
         * Now each tread is the fortress's own paving on a pier of its own
         * brick, and the pier goes down until it finds ground rather than a
         * fixed count of blocks. A flight that reaches the water builds
         * itself a foot in the water; what it cannot do is stop in mid-air.
         */
        this.setGenerated(x, floor, z, BlockId.StoneTile);
        for (let down = 1; down <= KREMLIN_STAIR_PIER; down++) {
          const at = floor - down;
          if (at < 1) break;
          if (isSolid(this.getBlock(x, at, z))) break;
          this.setGenerated(x, at, z, BlockId.Brick);
        }
        /*
         * A cheek down either side, one course over the tread.
         *
         * Half a balustrade: it is what makes a flight of steps read as part
         * of the building rather than as a heap the ground happens to be
         * making, and it keeps anything walking up in the middle of it
         * instead of stepping off the side into the sea.
         */
        if (Math.abs(dz) === KREMLIN_STAIR_HALF && d > 0 &&
            !isSolid(this.getBlock(x, floor + 1, z))) {
          this.setGenerated(x, floor + 1, z, BlockId.Brick);
        }
        /*
         * And the bank over it comes off.
         *
         * Not a fixed five: the gate's own opening is five tall, but an
         * approach cut into a hillside has to lose however much hillside is
         * standing on it - and on a third of the seeds that is eight or ten
         * blocks. Only natural ground is cleared: a lair, or anything
         * somebody built, is not the hillside's to give.
         */
        /*
         * The bank comes off the way up, not off the cheeks.
         *
         * The walking width is cleared to daylight, because a stair cut into
         * a hillside with the hillside still over it is a tunnel that ends
         * in a wall. The two edge columns only need room for the balustrade
         * itself: sweeping them as well took thirty-four columns of meadow
         * off the hillside and showed up two rooms away as a fifth of the
         * world's wild wheat.
         */
        const edge = Math.abs(dz) === KREMLIN_STAIR_HALF;
        /*
         * Nothing at all is taken off the cheeks.
         *
         * They are the sides of a cutting and the bank rests on them, so a
         * cheek column keeps whatever the hill put there and the tread holds
         * it up. Clearing even one course took the ground out from under the
         * turf above it and left a shelf hanging along both edges - eighty
         * cells of it across the seeds, the same fault this all started with
         * in miniature. The cheek block itself is laid only where there is
         * air to lay it in.
         */
        /*
         * And the hillside over the way comes off, up to the sky.
         *
         * Asked as "keep going until there is nothing but air" rather than
         * "up to the surface": `probeColumn` answers with the first floor it
         * finds, and where the cutting runs six blocks under a bank that
         * answer is below the bank's own turf - which left a roof of grass
         * over the middle of the passage. Three clear courses is the sky;
         * fewer could be a pocket in the hill, and that has to come down too.
         */
        let clear = 0;
        for (let up = 1; up <= ROAD_HEADROOM && clear < 3 && !edge; up++) {
          const above = this.getBlock(x, floor + up, z);
          if (above === BlockId.Air) { clear++; continue; }
          // Only natural ground: the cheek laid a moment ago is masonry, and
          // masonry is not the hillside's to give.
          if (!isNaturalGround(above)) break;
          clear = 0;
          this.setGenerated(x, floor + up, z, BlockId.Air);
        }
      }
    }
    // Where it ends, and at what height: this is where the road stops.
    this.kremlinFoot = { x: bx - reach, z: bz, level: floor };
  }

  /**
   * The walls, the flags and the quarters: a match being set up on the plain.
   *
   * Written through `setBlock` rather than into the terrain, on purpose.
   * How many teams there are is not known until somebody presses «почати»,
   * so the wall cannot be part of what the seed grows - and a wall that is
   * an ordinary edit is a wall the server sends out like any other block,
   * saves like any other block, and can turn into planks later by editing
   * it again.
   *
   * @returns how many cells were laid.
   */
  raiseWar(
    teams: number, flagFor: (team: number) => number,
    /** Which teams get the hardcore скрині, if any. See `WAR_KIT`. */
    kitFor: (team: number) => boolean = () => false,
  ): number {
    const span = this.opts.sizeXZ * CHUNK_SIZE;
    const top = WAR_GROUND + WALL_HEIGHT;
    let laid = 0;

    // The wall: from the floor of the world to ten blocks over the grass.
    for (let x = 0; x < span; x++) {
      for (let z = 0; z < span; z++) {
        if (!onWall(x, z, teams, span)) continue;
        for (let y = 1; y <= top; y++) {
          if (this.getBlock(x, y, z) === BlockId.Bedrock) continue;
          this.setBlock(x, y, z, BlockId.Bedrock);
          laid++;
        }
      }
    }

    /*
     * And a прапор in the middle of every quarter, on a pole nobody can
     * take out from under it.
     *
     * The pole is незламний and the cloth is not: "прапор можна зламати
     * тільки кайлом з адамантію", and a pole of ordinary wood would mean
     * the way to win was to chop the pole down, which is a different game
     * and a much easier one.
     */
    for (let team = 0; team < teams; team++) {
      const at = flagAt(team, teams, span);
      for (let y = 1; y <= FLAG_HEIGHT; y++) {
        this.setBlock(at.x, WAR_GROUND + y, at.z, BlockId.Bedrock);
        laid++;
      }
      this.setBlock(at.x, WAR_GROUND + FLAG_HEIGHT + 1, at.z, flagFor(team));
      laid++;
      if (kitFor(team)) laid += this.stockWarKit(at, span);
    }
    return laid;
  }

  /**
   * Ten скринь in a row, ten blocks from the прапор on the side away from
   * the other army - "біля прапора на відстані у 10 блоків від прапора у
   * стороні, що далі від суперника". Across the map's middle along x is
   * where the wall between team 0 and team 1 stands, so away is towards the
   * map's own edge. Whatever grew there is cleared for them; the contents
   * are the same on every side of the wire, as the flags are.
   */
  private stockWarKit(flag: { x: number; z: number }, span: number): number {
    const away = flag.x < span / 2 ? -1 : 1;
    const x = flag.x + away * 10;
    const y = WAR_GROUND + 1;
    let laid = 0;
    WAR_KIT.forEach((kit, i) => {
      const z = flag.z - 4 + i;
      for (let up = 0; up < 3; up++) this.setBlock(x, y + up, z, BlockId.Air);
      this.setBlock(x, y, z, chestId(0));
      if (this.containers.at(x, y, z) === null) {
        const box = this.containers.open(x, y, z);
        kit.forEach((stack, slot) => { if (slot < CHEST_SLOTS) box.set(slot, { ...stack }); });
        this.containers.markStocked(x, y, z);
      }
      laid++;
    });
    return laid;
  }

  /**
   * The preparing is over: the wall is wood now.
   *
   * "Коли час на підготовку закінчився стіна перетворюється на блок дерева
   * і гравці можуть починати його ламати. Дно світу залишається незламним."
   * So: the wall columns and nothing else, from one block up - whatever is
   * at y=0 is the floor of the world and stays what it is.
   *
   * @returns how many cells turned.
   */
  openWarEach(
    teams: number,
    each: (x: number, y: number, z: number, id: number) => void,
  ): number {
    const span = this.opts.sizeXZ * CHUNK_SIZE;
    const top = WAR_GROUND + WALL_HEIGHT;
    let turned = 0;
    for (let x = 0; x < span; x++) {
      for (let z = 0; z < span; z++) {
        if (!onWall(x, z, teams, span)) continue;
        for (let y = 1; y <= top; y++) {
          if (this.getBlock(x, y, z) !== BlockId.Bedrock) continue;
          this.setBlock(x, y, z, BlockId.OakPlanks);
          each(x, y, z, BlockId.OakPlanks);
          turned++;
        }
      }
    }
    return turned;
  }

  openWar(teams: number): number {
    return this.openWarEach(teams, () => { /* nobody is listening */ });
  }

  /**
   * «Сучасна війна»: a plain, a wood on it, and sixty blocks of ground under.
   *
   * "Пласка поверхня, росте багато дерев, під землею 60 блоків, дуже багато
   * ресурсів." Everything about this map that matters happens *on* it -
   * walls, flags, and whoever is on the other side of them - so the country
   * itself is deliberately the least interesting in the game: one height,
   * no coast, no caves to fall into and nowhere that is better ground than
   * anywhere else. Four armies have to start on equal terms, and equal
   * terms is what flat means.
   *
   * The floor is незламний блок rather than stone: it is the bottom of the
   * world and nobody is tunnelling under the wall.
   */
  private generateWar(): void {
    const span = this.opts.sizeXZ * CHUNK_SIZE;
    const worldHeight = this.opts.sizeY * CHUNK_SIZE;
    const ground = WAR_GROUND;
    const heights = new Int16Array(span * span);

    for (let z = 0; z < span; z++) {
      for (let x = 0; x < span; x++) {
        heights[z * span + x] = ground;
        this.setGenerated(x, 0, z, BlockId.Bedrock);
        for (let y = 1; y < ground - 3; y++) {
          this.setGenerated(x, y, z, BlockId.Stone);
        }
        // Three of soil under the turf, the way the islands do it.
        for (let y = ground - 3; y < ground; y++) {
          this.setGenerated(x, y, z, BlockId.Dirt);
        }
        this.setGenerated(x, ground, z, BlockId.Grass);
        /*
         * And clay between the soil and the stone, in beds rather than a
         * sheet: "глину між землею і каменем". Two in three columns.
         */
        if (hashInt(x >> 2, z >> 2, this.opts.seed + 3301) < 0.66) {
          this.setGenerated(x, ground - 4, z, BlockId.Clay);
        }
      }
    }

    /*
     * A small lake beside every place a прапор can stand.
     *
     * "Неможливо створити смолоскипи": a смолоскип wants linen, linen wants
     * flax soaked in water, and water is carried in something made of clay -
     * and this map had neither water nor clay anywhere. The flags stand in
     * the middles of the halves with two teams and of the quarters with
     * three or four, so all six places get one, a dozen blocks towards the
     * nearer edge of the map, with clay on the bottom.
     */
    const lakes: { x: number; z: number }[] = [];
    for (const teams of [2, 4]) {
      for (let team = 0; team < teams; team++) {
        const flag = flagAt(team, teams, span);
        lakes.push({ x: flag.x, z: flag.z + (flag.z < span / 2 ? -12 : 12) });
      }
    }
    for (const lake of lakes) {
      const R = 5;
      for (let dz = -R; dz <= R; dz++) {
        for (let dx = -R; dx <= R; dx++) {
          const d = Math.hypot(dx, dz * 1.3);
          if (d > R) continue;
          const x = lake.x + dx;
          const z = lake.z + dz;
          if (x < 1 || z < 1 || x >= span - 1 || z >= span - 1) continue;
          const depth = d < 2 ? 3 : d < 4 ? 2 : 1;
          for (let y = ground - depth + 1; y <= ground; y++) {
            this.setGenerated(x, y, z, BlockId.Water);
          }
          this.setGenerated(x, ground - depth, z, BlockId.Clay);
          // Nothing grows in it: plantTrees reads this as below the water.
          heights[z * span + x] = ground - 3;
        }
      }
    }

    /*
     * "Дуже багато ресурсів": the ordinary seams, three times over.
     *
     * Three passes of the same scatter with the salt shifted, rather than a
     * second ore table: what is down there should be what is down there
     * everywhere else, and there should simply be more of it. A match is an
     * hour, not a month, and a team that spends forty minutes looking for
     * iron is a team that never fights.
     */
    for (let pass = 0; pass < 3; pass++) {
      this.scatterOres(span, worldHeight, pass * 1013);
    }

    /*
     * And a wood, everywhere, because everything here is built out of it.
     *
     * `plantTrees` decides by moisture, which on a flat map gives stands of
     * oak in the damp half and open acacia country in the dry - so the two
     * halves of the map are different to look at without either being
     * better to start on. The sea level is passed as "below the floor" so
     * nothing refuses to grow for being too near water there is none of.
     */
    /*
     * The waterline is given as three under the turf, which is a lie with a
     * purpose: `plantTrees` refuses anything more than twenty-two blocks
     * above the sea, and on a map with no sea at all that refusal turned
     * the whole forest off. Nothing else on this map asks where the water
     * is, because there is none.
     */
    this.plantTrees(heights, span, WAR_GROUND - 3, worldHeight, true,
      PINE_LINE, 0.10, 0.8);
    this.buryWarSupplies(span);
    this.lairs.clear();
  }

  /**
   * Stand the кремль on the far island.
   *
   * It used to be a nest of lairs scattered over that island and the campaign
   * was hunting them; now the fortress *is* the island's reason to exist, and
   * the lairs are inside it. One per world, on the far side of the water, so
   * crossing over is still the thing that starts the war.
   *
   * Raised through the same `raise` every drafting stone uses - the same
   * clearance, the same foundation, the same refusal at the edge of the map -
   * writing through `setGenerated` so it lands in the terrain rather than in
   * the player's own modified chunks.
   *
   * **No WORLDGEN_VERSION bump.** This does not move the ground: heights,
   * ores, caves and the coastline are all exactly where they were, and a
   * saved world's own chunks are stored whole and win over anything generated
   * under them. The rule written in saveGame.ts is about the terrain moving,
   * and it has not.
   */
  private raiseKremlin(): void {
    if (this.opts.kremlin === false) return;
    const island = this.islands[1];
    if (island === undefined) return;
    /*
     * Only on an island that can hold it.
     *
     * The fortress is sixty-one blocks across and a test world's island is
     * forty-three, so raising one there put most of it out at sea. Small
     * worlds simply do not get one, which also keeps every test that grows a
     * cheap world out of the fortress's way.
     */
    if (island.radius * 2 < KREMLIN_SPAN) return;

    /*
     * Turned so the gate faces the other island.
     *
     * The two islands sit side by side along x, and the fortress was raised
     * facing 0 - which points the gate along -z, at open sea. You sailed
     * across and arrived at the side of it, looking for a way in.
     *
     * Facing 3 turns it a quarter: the gate tower now looks back across the
     * water at the island you came from. The arithmetic is the same swap the
     * rotation is - `offsetFor` sends local depth along +x under facing 3
     * where it sent it along +z under facing 0 - so what centred it in z now
     * centres it in x.
     */
    const x = Math.round(island.x - KREMLIN_SPAN / 2);
    const z = Math.round(island.z);
    /*
     * The height comes from the middle of the island, not from the anchor.
     *
     * The anchor is thirty blocks in front of the middle - which on any
     * island this size is open water, so `groundAt` there is the seabed and
     * the whole fortress was founded seventeen blocks under the sea. That is
     * where the three hundred and thirty-seven cells of hanging water were
     * coming from, and no amount of capping the foundation was going to fix
     * a building that started at the bottom.
     */
    /*
     * Set down into the hill rather than perched on its peak.
     *
     * The island is a steep dome. Measured at the seed the game ships with:
     * the ground at the middle is 79, the sea is at 53, and six blocks
     * outside the wall the ground is already 33 - below the waterline. So the
     * fortress stood on a spike of its own foundations with its towers at
     * 118 of a 128-block world, nearly touching the ceiling.
     *
     * Fifteen down puts the floor at 65 and the spire at 103: still the
     * highest thing on the island, which a кремль should be, but standing in
     * it rather than on top of it.
     */
    const y = this.groundAt(Math.round(island.x), Math.round(island.z)) + 1
      - KREMLIN_DROP;
    raise(
      KREMLIN, 3, x, y, z,
      (bx, by, bz) => this.getBlock(bx, by, bz),
      (bx, by, bz) => this.inWorld(bx, by, bz),
      (bx, by, bz, id) => this.setGenerated(bx, by, bz, id),
      isNaturalGround,
      // Never below the waterline: see the note on `floorY`.
      this.seaLevel + 1,
    );
    /*
     * And the way in, which the fortress does not come with.
     *
     * See `KREMLIN_APPROACH`. The замок has had this since "but entrance
     * should be visible"; the кремль never did, and where its gate came out
     * was whatever the hillside thirty blocks from the island's middle
     * happened to be doing.
     */
    this.openKremlinGate(x, y, z);
    // The lairs it brought are blocks in the terrain, and setGenerated does
    // not go through setBlock - so the register learns about them the way it
    // learns about a loaded world's.
    this.lairsStale = true;
    // And where it ended up, for anything that needs to know when somebody
    // is walking up to it. See `kremlinAt`.
    this.kremlinAt = { x: Math.round(island.x), z: Math.round(island.z) };
    this.kremlinSill = y;
  }

  /**
   * The middle of the кремль, once this world has one.
   *
   * Recorded rather than worked out again by whoever wants it. The fortress
   * stands on the second island, and where *that* is comes out of the seed -
   * so there is no fixed place to write down, and anything that went looking
   * for it by coordinate would be right for exactly one world. The замок
   * taught this the hard way: two pieces of code doing the same sum is how
   * one of them ends up doing it differently.
   *
   * Null on every map without a fortress, and until it has been raised.
   */
  kremlinAt: { x: number; z: number } | null = null;

  /**
   * The course the gate's passage stands on, once there is a кремль.
   *
   * Where the fortress was founded, which is fifteen blocks into the island's
   * dome and has nothing to do with the ground in front of the gate - so
   * anything that wants to arrive *at* the gate has to be told this number
   * rather than measure the hillside. The road is what wants it.
   */
  kremlinSill: number | null = null;

  /**
   * The far end of the кремль's own way out, once it has been cut.
   *
   * The fortress builds the last sixteen blocks in front of its gate itself -
   * a paved cutting through the lip of the hollow it stands in, see
   * `openKremlinGate`. This is where that ends and what height it ends at, so
   * the road can stop there instead of running over it: two things paving the
   * same corridor is how the кремль's own stone ended up under a track of
   * mud.
   */
  kremlinFoot: { x: number; z: number; level: number } | null = null;

  /**
   * Sow wild wheat and wild flax across the open steppe.
   *
   * Clumped by a noise field rather than sprinkled evenly - wheat grows in
   * stands, and an even scatter reads as decoration rather than as something
   * that grew there. Nothing is placed under a canopy, so a forest floor stays
   * bare and the steppe is where you go looking.
   */
  private scatterWildPlants(
    heights: Int16Array, spanXZ: number, seaLevel: number,
  ): void {
    const { seed } = this.opts;

    for (let z = 0; z < spanXZ; z++) {
      for (let x = 0; x < spanXZ; x++) {
        const ground = heights[z * spanXZ + x]!;
        if (ground <= seaLevel + 1) continue;

        // Only on open grass with air above: not on sand, not under a tree.
        if (this.getBlock(x, ground, z) !== BlockId.Grass) continue;
        if (this.getBlock(x, ground + 1, z) !== BlockId.Air) continue;
        this.sowWildPlant(x, ground, z, seed);
      }
    }
  }

  /**
   * What, if anything, comes up out of one cell of open grass.
   *
   * Split out of the pass above so a single column can ask the same question
   * without a map-sized heightmap to walk: the endless world grows a column
   * at a time and has no "every cell in the world" to loop over.
   */
  private sowWildPlant(x: number, ground: number, z: number, seed: number): void {
    // Wheat and flax both grow in stands, but from different noise, so they
    // interleave across the steppe instead of sharing every patch.
    const wheatStand = fbm2(x / 26, z / 26, seed + 3301, 3);
    const flaxStand = fbm2(x / 21, z / 21, seed + 7717, 3);

    let plant: number = BlockId.Air;
    if (flaxStand > 0.55 && flaxStand > wheatStand) {
      const density = (flaxStand - 0.55) * 1.6;
      if (hashInt(x, z, seed + 5501) <= density) plant = BlockId.WildFlax;
    } else if (wheatStand >= 0.52) {
      const density = (wheatStand - 0.52) * 1.7;
      if (hashInt(x, z, seed + 4409) <= density) plant = BlockId.WildWheat;
    }

    if (plant !== BlockId.Air) {
      this.setGenerated(x, ground + 1, z, plant);
      return;
    }

    /*
     * Буряк, wild, in the cells the two of them left alone.
     *
     * Its own noise and its own leftovers, the same shape as the hemp below
     * it: a beet is not a grass and does not grow in a stand of wheat. Rarer
     * than either, because what it is for is being *found* - a handful of
     * seed to start a garden with, not a crop you can live off where it
     * stands.
     */
    const beetStand = fbm2(x / 19, z / 19, seed + 8123, 3);
    if (beetStand > 0.62) {
      const beetDensity = (beetStand - 0.62) * 0.9;
      if (hashInt(x, z, seed + 7307) <= beetDensity) {
        this.setGenerated(x, ground + 1, z, BlockId.WildBeet);
        return;
      }
    }

    // Hemp, for grown-ups. It goes last and only into cells nothing else
    // wanted, which is what keeps the terrain identical in both modes.
    if (this.opts.adult !== true) return;
    const hempStand = fbm2(x / 17, z / 17, seed + 9931, 3);
    if (hempStand < HEMP_THRESHOLD) return;
    const hempDensity = (hempStand - HEMP_THRESHOLD) * HEMP_DENSITY;
    if (hashInt(x, z, seed + 6607) > hempDensity) return;

    this.setGenerated(x, ground + 1, z, BlockId.WildHemp);
  }

  /**
   * Write a block during generation.
   *
   * Deliberately not `setBlock`: worldgen output must not count as a player
   * modification, or every chunk would end up in the save file and the
   * seed-plus-differences format would be pointless.
   */
  private setGenerated(x: number, y: number, z: number, id: number): void {
    const cx = floorDiv(x, CHUNK_SIZE);
    const cy = floorDiv(y, CHUNK_SIZE);
    const cz = floorDiv(z, CHUNK_SIZE);
    /*
     * Not into a chunk somebody has changed.
     *
     * Only reachable in a world grown as it is walked into: a save is loaded
     * first and the country round it is generated afterwards, so a tree that
     * was chopped down would grow back through the roof of the house built
     * where it stood. In a world made all at once nothing is modified while
     * it is being made, and this never fires.
     */
    if (this.modified.has(key(cx, cy, cz))) return;
    const chunk = this.ensureChunk(cx, cy, cz);
    chunk?.set(mod(x, CHUNK_SIZE), mod(y, CHUNK_SIZE), mod(z, CHUNK_SIZE), id);
  }

  /**
   * How much land there is at this column, 0 in open water to 1 inland.
   *
   * Two islands and the neck between them, taking whichever is highest. The
   * neck is the whole reason the orcs are reachable at all: they have no boats
   * and cannot swim, so without a bar of land between the two islands they
   * would never arrive and could never be reached - and a narrow one is worth
   * more than a wide one, because a narrow one is somewhere to build a wall.
   */
  private landAt(x: number, z: number, spanXZ: number): { land: number; neck: number } {
    let best = 0;
    for (const island of this.islands) {
      const d = Math.hypot(x - island.x, z - island.z);
      // Ragged rather than a circle: the same noise that shapes the hills
      // pushes the coastline in and out, so an island is not a coin.
      const wobble = (fbm2(x / 26, z / 26, this.opts.seed + 4801, 3) - 0.5) * 0.22;
      best = Math.max(best, falloff(d / island.radius + wobble));
    }

    const neck = this.islands[0] !== undefined && this.islands[1] !== undefined
      ? falloff(
        distanceToSegment(
          x, z,
          this.islands[0].x, this.islands[0].z,
          this.islands[1].x, this.islands[1].z,
        ) / (spanXZ * NECK_WIDTH),
      ) * NECK_HEIGHT
      : 0;

    /*
     * The neck runs centre to centre, so it lies across the islands as well as
     * between them - and damping the relief by it flattened the islands and
     * then switched the hills back on at their shores, which is a cliff eight
     * blocks high exactly where the orcs have to come up out of the water.
     *
     * Fading its influence out as the island fades in makes the two agree:
     * full on the bar in the middle, nothing at all inland.
     */
    return { land: Math.max(best, neck), neck: neck * (1 - best) };
  }

  /**
   * What lies under the ground, and where.
   *
   * The depths follow the real ones as far as a 96-block world can. Two of
   * them are worth stating because they are the ones an outsider gets wrong:
   * manganese is shallow sedimentary ore, not something you go deep for, and
   * titanium comes out of **sand**, not rock - the Irshansk placers are old
   * beaches. So the titanium ores here have sand for a host, which means you
   * find them along a shore rather than down a shaft.
   *
   * Bands are fractions of the world's height rather than fixed numbers, so a
   * taller world spreads them out instead of piling everything at the bottom.
   */
  private static readonly ORES: readonly OreVein[] = [
    // Донбас. The commonest thing down there, and the shallowest fuel.
    { block: BlockId.CoalOre, host: BlockId.Stone,
      from: 0.08, to: 0.42, rarity: 3200, size: 9, salt: 101 },
    /*
     * Кривий Ріг, and three times as much of it as there was.
     *
     * "Спавниться занадто мало залізної руди, зроби втричі більше" - one
     * vein per 1733 cells of the band instead of one per 5200, which is
     * 3378 blocks of it in a world against 1126. The knob is the rarity and
     * not the vein size on purpose: what makes ore feel findable is how
     * often you walk into a seam, not how big the seam is once you have.
     *
     * It leaves iron the commonest ore in the ground, ahead of coal. That is
     * not the accident it looks like - Кривий Ріг is one of the largest iron
     * basins there is - and coal remains the shallowest fuel and far from
     * the only one: wood, торф and нафтоносний пласт all burn.
     */
    { block: BlockId.IronOre, host: BlockId.Stone,
      from: 0.04, to: 0.34, rarity: 1733, size: 7, salt: 211 },
    // Нікополь - shallow, and in a narrow band, because that is how it lies.
    { block: BlockId.ManganeseOre, host: BlockId.Stone,
      from: 0.12, to: 0.30, rarity: 9000, size: 6, salt: 307 },
    // Завалля. Deep and scarce.
    { block: BlockId.GraphiteOre, host: BlockId.Stone,
      from: 0.02, to: 0.20, rarity: 13000, size: 6, salt: 401 },
    // Прикарпаття. Fewer pockets, but a pocket is a big one.
    { block: BlockId.OilShale, host: BlockId.Stone,
      from: 0.01, to: 0.14, rarity: 19000, size: 14, salt: 503 },
    /*
     * Адамантій: the bottom ten blocks of the world, and nowhere else.
     *
     * Not a real deposit and not pretending to be one - it is the material
     * the war map is fought over, and where it lies is the rule that makes
     * that fight cost something: everybody has to go down to the floor for
     * it, and a wooden pick will not open it when they get there.
     *
     * Scarce, but scarce in a band nine blocks tall, so a shaft along the
     * bottom finds some. In a кремль world that is about one seam per three
     * chunk columns.
     */
    /*
     * Нафта: pockets of it, sealed in the rock near the floor.
     *
     * "Додай нафту десь ближче до дна." Laid as a seam rather than as a
     * cave-filling, which is what makes it something to find: a pocket is
     * dry rock until somebody digs into it, and then it runs - slowly - into
     * whatever they dug. Broad and flat, because that is what a reservoir
     * is, and rare enough to be worth the shaft.
     */
    { block: OIL as BlockId, host: BlockId.Stone,
      from: 0, to: 0, floorBand: 26, rarity: 14000, size: 20, salt: 1103,
      flat: true },
    // `flat` so a seam stays on the floor it was seeded on: a walk that can
    // step upward puts crystal fifteen blocks up, and then "at the very
    // bottom of the world" is a thing the map says and the ground does not.
    { block: BlockId.Adamant, host: BlockId.Stone,
      from: 0, to: 0, floorBand: 10, rarity: 4000, size: 4, salt: 907,
      flat: true },
    // Житомирщина. Not ore - masses of building stone, so the veins are large.
    { block: BlockId.Granite, host: BlockId.Stone,
      from: 0.00, to: 0.28, rarity: 2600, size: 26, salt: 601 },
    // Іршанськ: titanium out of sand, not out of rock.
    // Rarity here counts cells of *sand*, not cells of the band - there are
    // only about five thousand of them in a world, against half a million of
    // stone.
    // Beaches and the sea floor both, which is where heavy mineral sand lies
    // and is now most of it: the shore is a strip a few blocks wide, and the
    // bottom of a fifty-block sea is where the sand actually is.
    { block: BlockId.Ilmenite, host: BlockId.Sand,
      from: 0, to: 0, shore: { below: 30, above: 4 },
      rarity: 700, size: 7, salt: 701, flat: true },
    { block: BlockId.Rutile, host: BlockId.Sand,
      from: 0, to: 0, shore: { below: 30, above: 4 },
      rarity: 1700, size: 5, salt: 809, flat: true },
  ];

  /**
   * Place every ore.
   *
   * Veins are seeded and grown rather than tested for at every cell. Asking a
   * noise field about all 1.5 million cells, nine times over, is a second of
   * work at world start and far more across a test suite that builds dozens of
   * worlds; growing a few hundred short walks costs nothing and gives rounder
   * seams than a threshold does.
   */
  private scatterOres(spanXZ: number, worldHeight: number, salt = 0): void {
    const { seed: baseSeed } = this.opts;
    // A shift, so the same table can be scattered more than once over the
    // same world without laying every seam on top of the last pass's.
    const seed = baseSeed + salt;

    for (const ore of World.ORES) {
      const low = Math.max(1, ore.floorBand !== undefined
        ? 1
        : ore.shore !== undefined
          ? this.seaLevel - ore.shore.below
          : Math.floor(ore.from * worldHeight));
      const high = Math.min(worldHeight - 1, ore.floorBand !== undefined
        ? ore.floorBand
        : ore.shore !== undefined
          ? this.seaLevel + ore.shore.above
          : Math.floor(ore.to * worldHeight));
      if (high <= low) continue;

      if (ore.host === BlockId.Stone) {
        // Stone is most of what is down there, so a seed thrown at the band
        // lands in it. One roll per cell of the band, and the walk sorts out
        // the few that miss.
        const veins = Math.max(1, Math.round((spanXZ * spanXZ * (high - low)) / ore.rarity));
        for (let i = 0; i < veins; i++) {
          const x = Math.floor(hashInt(i, ore.salt, seed) * spanXZ);
          const z = Math.floor(hashInt(i, ore.salt + 1, seed) * spanXZ);
          const y = low + Math.floor(hashInt(i, ore.salt + 2, seed) * (high - low));
          this.growVein(ore, x, y, z, seed + ore.salt + i);
        }
        continue;
      }

      // Sand is a shell a few blocks thick along the shores, not a mass. A
      // seed thrown at the band lands in stone or in air essentially every
      // time: thrown blind, the titanium sands came out at twenty-one blocks
      // and four in a whole world. So the sand is found first and the seams
      // are planted in it.
      const host = this.collectHost(ore.host, low, high);
      const veins = Math.max(1, Math.round(host.length / ore.rarity));
      for (let i = 0; i < veins; i++) {
        const pick = host[Math.floor(hashInt(i, ore.salt, seed) * host.length)];
        if (pick === undefined) break;
        this.growVein(
          ore, pick.x, pick.y, pick.z, seed + ore.salt + i,
        );
      }
    }
  }

  /**
   * Every cell of a given rock inside a depth band.
   *
   * Only worth doing for hosts that are scarce - it is a scan of the band,
   * which is a few hundred thousand reads. Stone does not need it.
   */
  private collectHost(host: number, low: number, high: number): Cell[] {
    const out: Cell[] = [];
    const { sizeXZ } = this.opts;

    /*
     * Chunk by chunk, reading the block arrays directly.
     *
     * The obvious version calls getBlock per cell, and getBlock builds a
     * string key and looks it up in a map. Over the sand band that is a
     * million lookups, and it was 1134 ms of a 1957 ms world generation -
     * more than half the startup, to find a few thousand grains of sand.
     * extractPadded already skips the lookup for interior reads for exactly
     * this reason.
     */
    for (let cy = Math.floor(low / CHUNK_SIZE); cy <= Math.floor((high - 1) / CHUNK_SIZE); cy++) {
      for (let cz = 0; cz < sizeXZ; cz++) {
        for (let cx = 0; cx < sizeXZ; cx++) {
          const chunk = this.getChunk(cx, cy, cz);
          if (chunk === undefined) continue;

          const oy = cy * CHUNK_SIZE;
          const y0 = Math.max(low, oy) - oy;
          const y1 = Math.min(high, oy + CHUNK_SIZE) - oy;

          for (let y = y0; y < y1; y++) {
            for (let z = 0; z < CHUNK_SIZE; z++) {
              for (let x = 0; x < CHUNK_SIZE; x++) {
                if (chunk.blocks[blockIndex(x, y, z)] !== host) continue;
                out.push({
                  x: cx * CHUNK_SIZE + x, y: oy + y, z: cz * CHUNK_SIZE + z,
                });
              }
            }
          }
        }
      }
    }
    return out;
  }

  /**
   * One seam: a short walk that leaves ore behind it.
   *
   * The walk may double back on itself, and it only writes where the host rock
   * actually is - a seam that runs out of the stone into a cave simply stops,
   * which is what makes seams look like they belong to the rock rather than
   * having been dropped into it.
   */
  private growVein(
    ore: OreVein, x: number, y: number, z: number, seed: number,
  ): void {
    let cx = x;
    let cy = y;
    let cz = z;

    for (let step = 0; step < ore.size; step++) {
      if (this.getBlock(cx, cy, cz) === ore.host) {
        this.setGenerated(cx, cy, cz, ore.block);
      }
      const roll = hashInt(step, cx + cz, seed);
      // Seams lie in beds rather than standing on end, so vertical steps are
      // rarer than sideways ones even when they are allowed at all.
      const dir = Math.floor(roll * (ore.flat === true ? 4 : 6));
      if (dir === 0) cx++;
      else if (dir === 1) cx--;
      else if (dir === 2) cz++;
      else if (dir === 3) cz--;
      else if (dir === 4) cy++;
      else cy--;
    }
  }

  /**
   * Peat, in the bogs.
   *
   * Not a vein: peat is what a wetland leaves behind, so it lies in flat sheets
   * just under the surface wherever the ground is low and damp. Generated from
   * its own noise so bogs are patches rather than a ring round every lake.
   */
  private layPeat(heights: Int16Array, spanXZ: number, seaLevel: number): void {
    const { seed } = this.opts;

    for (let z = 0; z < spanXZ; z++) {
      for (let x = 0; x < spanXZ; x++) {
        const ground = heights[z * spanXZ + x]!;
        // Low ground only: a bog on a hilltop is not a bog.
        if (ground < seaLevel || ground > seaLevel + 3) continue;
        if (this.getBlock(x, ground, z) !== BlockId.Grass) continue;

        const bog = fbm2(x / 23, z / 23, seed + 8821, 3);
        if (bog < 0.58) continue;

        // A sheet one to three blocks thick, starting just under the turf.
        const depth = 1 + Math.floor(hashInt(x, z, seed + 8831) * 3);
        for (let d = 1; d <= depth; d++) {
          const y = ground - d;
          if (y < 1) break;
          const here = this.getBlock(x, y, z);
          if (here !== BlockId.Dirt && here !== BlockId.Chornozem) break;
          this.setGenerated(x, y, z, BlockId.Peat);
        }
      }
    }
  }

  /**
   * Scatter oaks and acacias.
   *
   * Candidates come from a grid rather than from a per-column roll, which is
   * what stops trees growing into each other: one possible trunk per cell,
   * jittered inside it. Everything is derived from the seed and the position,
   * so regenerating the world plants exactly the same forest - the save format
   * depends on that, since it stores only what the player changed.
   */
  private plantTrees(
    heights: Int16Array,
    spanXZ: number,
    seaLevel: number,
    worldHeight: number,
    /**
     * A wood rather than scattered stands. For «Сучасна війна».
     *
     * "Росте багато дерев" - and on that map wood is the whole economy:
     * every wall, every tool handle and the requisitioned planks of the
     * barrier itself. Scattered stands on a flat map would mean the team
     * that spawned in the damp half started with a forest and the team in
     * the dry half started with a lawn.
     */
    thick = false,
    /**
     * How far over the sea сосна takes over, on this map.
     *
     * The adventure's +17 is the top fifth of the ground a tree can grow on
     * *there*, where mountains are everywhere. On the islands the only
     * ground that high is the dome the кремль is founded on - and the
     * fortress is raised after the trees and clears everything it covers,
     * so every pine the map grew stood inside the walls and went with them.
     * Four pines in a world and no смола anywhere: "майже не було сосни".
     */
    pineLine = PINE_LINE,
    /**
     * How often a dry cell grows anything at all.
     *
     * A tenth, on a map where the damp half is at 0.85 - which on the wide
     * adventure is right, because a dry cell there is steppe and the steppe
     * is meant to be open. On the islands it is the difference between a
     * wood and a lawn: seeds whose island happens to be dry came out with
     * two or three trees on them, and a кремль map with no wood on it is a
     * map with no planks, no tools and no fire.
     */
    dryDensity = 0.10,
    /**
     * A share of the trees that are сосна whatever the ground, before
     * height and moisture are asked. For «Сучасна війна», which is flat and
     * so had no high ground for a pine at all: "всі дерева там акації,
     * нехай 80% дерев будуть сосни".
     */
    pineShare = 0,
  ): void {
    const { seed } = this.opts;
    const CELL = thick ? 5 : 7;

    for (let cellZ = 0; cellZ * CELL < spanXZ; cellZ++) {
      for (let cellX = 0; cellX * CELL < spanXZ; cellX++) {
        const roll = hashInt(cellX, cellZ, seed + 5501);
        // Moisture decides both whether a tree grows and which kind. Oaks
        // gather in the damp hollows, acacias hold the dry open steppe.
        const moisture = fbm2(
          (cellX * CELL) / 70, (cellZ * CELL) / 70, seed + 991, 3,
        );
        const density = thick
          ? (moisture > 0.5 ? 0.95 : 0.6)
          : (moisture > 0.5 ? 0.85 : dryDensity);
        if (roll > density) continue;

        const x = cellX * CELL + Math.floor(hashInt(cellX, cellZ, seed + 17) * CELL);
        const z = cellZ * CELL + Math.floor(hashInt(cellX, cellZ, seed + 23) * CELL);
        if (x < 3 || z < 3 || x >= spanXZ - 3 || z >= spanXZ - 3) continue;

        const ground = heights[z * spanXZ + x]!;
        /*
         * Nothing grows below the waterline or on the beach - and the tops
         * of the islands are no longer bare.
         *
         * The ceiling was twenty-two blocks over the sea, which on these
         * islands is where the ground starts being *interesting*: a quarter
         * of the land stands higher than that and every bit of it was bald.
         * Сосна only grows above seventeen, so the pine band was four blocks
         * tall and the whole map came out with four pines in it - reported
         * as "майже не було сосни, то ж немає де брати смолу". Thirty-six
         * gives the pine the high ground it is for and leaves the peaks
         * above it bare.
         */
        if (ground <= seaLevel + 1 || ground > seaLevel + 36) continue;

        const maxTop = worldHeight - 2;
        // Three species now, and height decides between two of them the way
        // moisture decides the other. Сосна takes the high dry ground: it is
        // the tree that grows where the soil is poor, which is also the only
        // place in this world nothing else wanted.
        // Only the highest ground, which is about a fifth of what a tree can
        // grow on. The first version said +14, which is fifty-eight per cent
        // of it: the world came out almost entirely сосна and the acacia
        // nearly vanished, which is not a thing that was asked for.
        const high = ground > seaLevel + pineLine ||
          hashInt(cellX, cellZ, seed + 7919) < pineShare;
        if (high) this.growPine(x, ground, z, seed, maxTop, false);
        else if (moisture > 0.52) this.growOak(x, ground, z, seed, maxTop, false);
        else this.growAcacia(x, ground, z, seed, maxTop, false);
      }
    }
  }

  /**
   * Raise a tree here, as a паросток does when it comes of age.
   *
   * The same two shapes generation uses, written through setBlock instead of
   * straight into the chunk, so the mesher hears about it - and refusing to
   * overwrite anything that is not air, so a tree that comes up beside a wall
   * grows around it rather than through it.
   *
   * @returns whether anything was actually raised.
   */
  growTree(
    x: number, ground: number, z: number, kind: "oak" | "acacia" | "pine",
  ): boolean {
    const maxTop = this.opts.sizeY * CHUNK_SIZE - 2;
    const seed = this.opts.seed;
    if (kind === "pine") return this.growPine(x, ground, z, seed, maxTop, true);
    return kind === "oak"
      ? this.growOak(x, ground, z, seed, maxTop, true)
      : this.growAcacia(x, ground, z, seed, maxTop, true);
  }

  /**
   * Put a block down while building a tree.
   *
   * `live` says whether the tree is coming up in a world somebody is standing
   * in. During generation nothing is in the way and nothing is watching, so it
   * goes straight into the chunk; afterwards it has to go through setBlock,
   * and it has to leave alone whatever is already there.
   */
  private treeBlock(x: number, y: number, z: number, id: number, live: boolean): void {
    if (!live) {
      this.setGenerated(x, y, z, id);
      return;
    }
    if (this.getBlock(x, y, z) !== BlockId.Air) return;
    this.setBlock(x, y, z, id);
  }

  /** Heavy, rounded, low-branching. */
  private growOak(
    x: number, ground: number, z: number, seed: number, maxTop: number,
    live: boolean,
  ): boolean {
    const height = 4 + Math.floor(hashInt(x, z, seed + 71) * 3);
    const top = ground + height;
    if (top + 2 > maxTop) return false;

    for (let y = ground + 1; y <= top; y++) {
      this.treeBlock(x, y, z, BlockId.OakLog, live);
    }

    // A rough sphere around the crown, thinned at the corners so it does not
    // read as a cube.
    const radius = 2;
    for (let dy = -2; dy <= 2; dy++) {
      const ry = radius - Math.abs(dy) * 0.4;
      for (let dz = -radius; dz <= radius; dz++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const d = Math.hypot(dx, dz, dy * 1.3);
          if (d > ry + 0.6) continue;
          if (d > ry && hashInt(x + dx, z + dz, seed + dy * 13 + 101) > 0.45) continue;
          this.placeLeaf(x + dx, top + dy, z + dz, BlockId.OakLeaves, live);
        }
      }
    }
    return true;
  }

  /** Taller, paler, with an airier crown held higher up. */
  private growAcacia(
    x: number, ground: number, z: number, seed: number, maxTop: number,
    live: boolean,
  ): boolean {
    const height = 6 + Math.floor(hashInt(x, z, seed + 73) * 3);
    const top = ground + height;
    if (top + 2 > maxTop) return false;

    for (let y = ground + 1; y <= top; y++) {
      this.treeBlock(x, y, z, BlockId.AcaciaLog, live);
    }

    // Wide and flat rather than round - the silhouette is what tells the two
    // species apart from a distance.
    for (let dy = -1; dy <= 1; dy++) {
      const radius = dy === 0 ? 3 : 2;
      for (let dz = -radius; dz <= radius; dz++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const d = Math.hypot(dx, dz);
          if (d > radius + 0.4) continue;
          if (d > radius - 0.6 && hashInt(x + dx, z + dz, seed + dy * 7 + 211) > 0.4) continue;
          this.placeLeaf(x + dx, top + dy, z + dz, BlockId.AcaciaLeaves, live);
        }
      }
    }
    return true;
  }

  /**
   * Сосна: a tall bare trunk with a spire of needles on top.
   *
   * Narrow and tiered rather than round or flat - the silhouette is what
   * tells the three species apart across a valley, and a pine drawn as a
   * green ball is an oak that happens to be taller.
   */
  private growPine(
    x: number, ground: number, z: number, seed: number, maxTop: number,
    live: boolean,
  ): boolean {
    const height = 7 + Math.floor(hashInt(x, z, seed + 79) * 4);
    const top = ground + height;
    if (top + 2 > maxTop) return false;

    for (let y = ground + 1; y <= top; y++) {
      this.treeBlock(x, y, z, BlockId.PineLog, live);
    }

    /*
     * Tiers, counted down from the cap: 0 is the single leaf above the trunk,
     * and every level below it is at least one block wider than the trunk.
     *
     * That "at least" is the whole fix. The first version derived the radius
     * as floor(i / 2), which is 0 for the top two levels - and a radius of 0
     * is exactly the cell the trunk already occupies, so placeLeaf refused it
     * and nothing was drawn. The result was a bare pole with a leaf hat
     * floating two blocks above the foliage, which is what a totem pole looks
     * like and not what a сосна does.
     */
    const crown = Math.max(5, Math.floor(height * 0.6));
    for (let i = 0; i < crown; i++) {
      const y = top + 1 - i;
      const radius = i === 0 ? 0 : Math.min(2, Math.ceil(i / 2));
      for (let dz = -radius; dz <= radius; dz++) {
        for (let dx = -radius; dx <= radius; dx++) {
          // A true diamond, not a square with the corners knocked off. One
          // step looser and the widest tier is as broad as an oak's crown,
          // which loses the taper that is the only thing telling a сосна from
          // a taller oak at a distance.
          if (Math.abs(dx) + Math.abs(dz) > radius) continue;
          this.placeLeaf(x + dx, y, z + dz, BlockId.PineLeaves, live);
        }
      }
    }
    return true;
  }

  /** Leaves never replace anything - a trunk must stay visible through its own crown. */
  private placeLeaf(x: number, y: number, z: number, id: BlockId, live: boolean): void {
    if (this.getBlock(x, y, z) !== BlockId.Air) return;
    this.treeBlock(x, y, z, id, live);
  }

  /**
   * How far water reaches to keep soil moist, in blocks.
   *
   * Horizontal radius; vertically it counts water level with the soil or one
   * block above it, which is what lets a field beside a lake stay wet.
   */
  static readonly WATER_REACH = 4;

  /** Cells a single flood may fill. Stops a dug-out cavern from freezing the game. */
  static readonly MAX_FLOOD = 8192;

  /**
   * Water on its way, and the clock it is measured against.
   *
   * Keyed by cell, so a second edit that changes its mind about a cell
   * replaces the first answer rather than racing it.
   */
  private readonly coming =
    new Map<string, { x: number; y: number; z: number; id: number; at: number }>();
  private fluidClock = 0;

  /**
   * Let water find its level after an edit.
   *
   * Water is now two things. A **source** is what generation puts below sea
   * level and what a bucket pours out: infinite, and it never moves.
   * Everything a source spreads is a **flow**, carrying the number of steps it
   * has travelled, and it stops at WATER_RANGE - eight blocks.
   *
   * The method is recompute, not simulate. Every flow near the edit is wiped,
   * and then the sources that could possibly reach it flood outward again,
   * nearest level first. That removes the whole class of bug that makes
   * hand-written fluids miserable - flows that outlive the source that fed
   * them, or that need a scheduler to drain - at the cost of doing more work
   * per edit than strictly necessary. The work is bounded, and breaking a
   * block is something a person does a few times a second at most.
   *
   * The rule this replaces was "everything below sea level that is open to the
   * sea is wet", which made every lake infinite in extent and did nothing at
   * all above sea level. Digging a channel from a lake now fills eight blocks
   * of it and no more, and a bucket emptied on a hilltop runs downhill.
   *
   * Deliberately not modelled: pressure, currents, and water preferring a hole
   * to fall into over spreading evenly. Minecraft does the last one and it is
   * a real improvement; it is also a separate piece of work.
   *
   * @returns how many cells now hold flowing water.
   */
  updateWater(x: number, y: number, z: number): number {
    const origin = { x, y, z };

    /*
     * 1. Gather the body of flowing water this edit can affect, and the
     *    sources feeding it, by following the water itself.
     *
     * This used to be two boxes of fixed radius around the edit - wipe within
     * nine, look for sources within seventeen - on the reasoning that a flow
     * is at most eight steps from its source. That reasoning is wrong,
     * because falling costs distance but not level: a waterfall thirty blocks
     * tall ends in water one step from a source that is thirty blocks
     * overhead. Digging at the foot of it wiped the pool, found no source
     * inside the box, and left the column standing in mid-air with nothing
     * under it.
     *
     * Connectivity has no such blind spot, and it is cheaper besides: it
     * touches the water that exists rather than every cell in a 35-block cube.
     */
    const body: Cell[] = [];
    const seeds: Cell[] = [];
    const seen = new Set<string>([cellKey(origin)]);
    // Breadth first, so a cell is reached by its shortest path and the air
    // budget below is spent honestly.
    const search: { cell: Cell; airSteps: number }[] = [{ cell: origin, airSteps: 0 }];
    let scanned = 0;

    // The edited cell counts as much as its neighbours do. A bucket emptied
    // onto the ground *is* the new source, and leaving it out of the seeds
    // meant it sat there as a single block and never ran anywhere.
    const here = this.getBlock(x, y, z);
    if (here === BlockId.Water) seeds.push(origin);
    else if (isWaterFlow(here)) body.push(origin);

    while (scanned < search.length && body.length < World.MAX_FLOOD) {
      const { cell, airSteps } = search[scanned]!;
      scanned++;

      for (const [dx, dy, dz] of NEIGHBOURS) {
        const next = { x: cell.x + dx, y: cell.y + dy, z: cell.z + dz };
        const k = cellKey(next);
        if (seen.has(k)) continue;
        seen.add(k);

        const id = this.getBlock(next.x, next.y, next.z);

        // A source is where the recompute starts, not something to recompute.
        if (id === BlockId.Water) { seeds.push(next); continue; }

        // Flow is followed as far as it goes, and costs no budget: a column of
        // water thirty blocks tall is one body with one source at the top of
        // it, and that source has to be found or the column is left hanging.
        if (isWaterFlow(id)) {
          body.push(next);
          search.push({ cell: next, airSteps });
          continue;
        }

        // Air is crossed too, but only WATER_RANGE deep. A hole the player has
        // just dug is air, and the lake that should pour into it is on the far
        // side of that air - following water alone would never find it, which
        // left a trench beside a pool bone dry until the very last block
        // between them was the one being broken.
        if (id === BlockId.Air && airSteps < WATER_RANGE) {
          search.push({ cell: next, airSteps: airSteps + 1 });
        }
      }
    }

    /*
     * 2. Work out what the answer *is*, before changing anything.
     *
     * The flood below used to write as it went, which made the whole of this
     * instantaneous: dig under a lake and the far end of the trench is full
     * in the same frame. Water that arrives has to travel, so the answer is
     * computed into `want` first and then handed to the clock, which lets it
     * out a couple of blocks a second - see `stepFluids`.
     *
     * Which also fixes something the writing version got away with: a lake
     * beside an edit was wiped and refilled every time anybody dug near it.
     * That was invisible while the refill happened in the same breath. Timed,
     * it would be a lake blinking out and creeping back. So nothing is wiped
     * until the answer is known, and a cell that already holds what it should
     * is left alone.
     */
    const want = new Map<string, { cell: Cell; id: number; level: number }>();
    /*
     * The body counts as empty while the answer is being worked out.
     *
     * The old order was wipe-then-flood, and the flood asked the world
     * whether a cell was air. Nothing is wiped up front any more - that is
     * what would make a lake blink - so the flood has to be told that the
     * water it is standing in is water it is about to replace. Without this
     * it cannot re-enter its own body at all and the answer comes back
     * nearly empty: a trench that had water in it a moment ago stays dry.
     */
    const inBody = new Set(body.map(cellKey));
    const free = (at: Cell): boolean =>
      inBody.has(cellKey(at)) || this.canFlowInto(at);

    // 3. Flood again from every source that touched it.
    //
    // The level has to travel with the cell, not with the round of the
    // search. Counting rounds is the obvious shortcut and it is wrong for the
    // same reason as the boxes were: a waterfall spends one round per block of
    // fall, so water dropping six blocks arrived with two levels of spread
    // left and made a puddle two wide instead of a pool eight wide.
    const assigned = new Map<string, number>();
    let queue: { cell: Cell; level: number; sea: boolean }[] =
      seeds.map((cell) => ({ cell, level: 0, sea: this.isSea(cell) }));
    let filled = 0;
    let head = 0;

    while (head < queue.length && filled < World.MAX_FLOOD) {
      const { cell, level, sea } = queue[head]!;
      head++;

      // Stale entry: something reached this cell by a shorter path after it
      // was queued, and that shorter path has already expanded it.
      const best = assigned.get(cellKey(cell));
      if (best !== undefined && best < level) continue;

      // Down first, and falling water lands at full strength: a waterfall
      // makes a full-sized pool at the bottom however far it fell. That is
      // what makes carrying a bucket uphill worth doing. Falling water does
      // not spread sideways on the way down - a column, not a curtain.
      const below = { x: cell.x, y: cell.y - 1, z: cell.z };
      const belowKey = cellKey(below);
      if (!assigned.has(belowKey) && free(below)) {
        want.set(belowKey, { cell: below, id: this.fillFor(below, sea, 1), level: 0 });
        assigned.set(belowKey, 0);
        queue.push({ cell: below, level: 0, sea });
        filled++;
        continue;
      }
      /*
       * And water standing on water spreads, the same as water standing on
       * ground.
       *
       * The old version asked the world whether the cell below was free, and
       * by the time the cell *above* it was reached the flood had already
       * written water there - so it read "not free" and spread sideways.
       * Nothing writes as it goes now, so the same question has to be asked
       * of the answer being built: `assigned` above does it. Skipping those
       * cells instead - which is what the first attempt did - left a trench
       * filled along its floor and dry at the top, because every cell that
       * had water arriving underneath it declined to spread.
       */

      // Standing on something: spread sideways, one level further out - unless
      // this is the sea, which has no far end.
      const spreading = sea && cell.y <= this.seaLevel;
      if (level >= WATER_RANGE && !spreading) continue;
      for (const [dx, dz] of HORIZONTAL) {
        const side = { x: cell.x + dx, y: cell.y, z: cell.z + dz };
        const sideKey = cellKey(side);
        const known = assigned.get(sideKey);
        // Either the cell is empty, or water already got here the long way
        // round and this path is shorter - which happens wherever a flow and
        // a waterfall reach the same place.
        const improves = known === undefined
          ? free(side)
          : level + 1 < known;
        if (!improves) continue;

        // Sea stays sea: written as a source and kept at level 0, so it goes
        // on as far as the hollow does. Anywhere else it is a flow one step
        // further from where it started.
        const next = spreading && side.y <= this.seaLevel ? 0 : level + 1;
        want.set(sideKey, {
          cell: side, id: this.fillFor(side, sea, level + 1), level: level + 1,
        });
        assigned.set(sideKey, next);
        queue.push({ cell: side, level: next, sea });
        if (known === undefined) filled++;
      }

      // The queue only ever grows; drop the consumed head occasionally so a
      // long waterfall does not hold on to every cell it ever queued.
      if (head > 4096) {
        queue = queue.slice(head);
        head = 0;
      }
    }
    /*
     * 4. Take away what is not wanted, and let the rest arrive in its own
     *    time.
     *
     * Draining is immediate and spreading is not, which is the asymmetry
     * every game of this kind has: water that has lost its source is water
     * with nothing holding it up, and watching a trench empty one block a
     * second is watching nothing happen.
     */
    for (const cell of body) {
      if (want.has(cellKey(cell))) continue;
      this.setBlock(cell.x, cell.y, cell.z, BlockId.Air);
    }
    for (const [at, put] of want) {
      if (this.getBlock(put.cell.x, put.cell.y, put.cell.z) === put.id) {
        this.coming.delete(at);
        continue;
      }
      this.coming.set(at, {
        x: put.cell.x, y: put.cell.y, z: put.cell.z, id: put.id,
        at: this.fluidClock + put.level / WATER_SPEED,
      });
    }
    return filled;
  }

  /**
   * Let лава find its level after an edit.
   *
   * The same shape as `updateWater` and deliberately its own function. Water
   * is complicated by exactly one thing - the sea, which is infinite, has a
   * level of its own, freezes, and must never be mistaken for a puddle - and
   * лава has none of that. A pool of лава is a pool: it spreads four blocks,
   * it falls, and it stops. Folding the two together would mean carrying the
   * sea's special cases through a fluid that has no sea, in the most
   * bug-prone code in the game.
   *
   * What they do share is the clock: both hand their answer to `coming` and
   * both arrive at `level / speed` seconds. Лава runs at one block a second
   * against water's two, which is the whole of what makes a pool of it
   * something you can get away from.
   *
   * @returns how many cells now hold flowing лава.
   */
  updateLava(x: number, y: number, z: number): number {
    const origin = { x, y, z };
    const body: Cell[] = [];
    const seeds: Cell[] = [];
    const seen = new Set<string>([cellKey(origin)]);
    const search: { cell: Cell; airSteps: number }[] = [
      { cell: origin, airSteps: 0 },
    ];
    let scanned = 0;

    const here = this.getBlock(x, y, z);
    if (here === LAVA) seeds.push(origin);
    else if (isLavaFlow(here)) body.push(origin);

    while (scanned < search.length && body.length < World.MAX_FLOOD) {
      const { cell, airSteps } = search[scanned]!;
      scanned++;
      for (const [dx, dy, dz] of NEIGHBOURS) {
        const next = { x: cell.x + dx, y: cell.y + dy, z: cell.z + dz };
        const k = cellKey(next);
        if (seen.has(k)) continue;
        seen.add(k);
        const id = this.getBlock(next.x, next.y, next.z);
        if (id === LAVA) { seeds.push(next); continue; }
        if (isLavaFlow(id)) {
          body.push(next);
          search.push({ cell: next, airSteps });
          continue;
        }
        if (id === BlockId.Air && airSteps < LAVA_RANGE) {
          search.push({ cell: next, airSteps: airSteps + 1 });
        }
      }
    }

    const want = new Map<string, { cell: Cell; id: number; level: number }>();
    const inBody = new Set(body.map(cellKey));
    const free = (at: Cell): boolean =>
      inBody.has(cellKey(at)) || this.canFlowInto(at);

    const assigned = new Map<string, number>();
    let queue: { cell: Cell; level: number }[] =
      seeds.map((cell) => ({ cell, level: 0 }));
    let filled = 0;
    let head = 0;

    while (head < queue.length && filled < World.MAX_FLOOD) {
      const { cell, level } = queue[head]!;
      head++;
      const best = assigned.get(cellKey(cell));
      if (best !== undefined && best < level) continue;

      // Down first, and falling лава lands at full strength, the same as
      // water: what runs off a ledge makes a pool under it.
      const below = { x: cell.x, y: cell.y - 1, z: cell.z };
      const belowKey = cellKey(below);
      if (!assigned.has(belowKey) && free(below)) {
        want.set(belowKey, { cell: below, id: lavaFlowId(1), level: 0 });
        assigned.set(belowKey, 0);
        queue.push({ cell: below, level: 0 });
        filled++;
        continue;
      }

      if (level >= LAVA_RANGE) continue;
      for (const [dx, dz] of HORIZONTAL) {
        const side = { x: cell.x + dx, y: cell.y, z: cell.z + dz };
        const sideKey = cellKey(side);
        const known = assigned.get(sideKey);
        const improves = known === undefined ? free(side) : level + 1 < known;
        if (!improves) continue;
        want.set(sideKey, {
          cell: side, id: lavaFlowId(level + 1), level: level + 1,
        });
        assigned.set(sideKey, level + 1);
        queue.push({ cell: side, level: level + 1 });
        if (known === undefined) filled++;
      }

      if (head > 4096) { queue = queue.slice(head); head = 0; }
    }

    for (const cell of body) {
      if (want.has(cellKey(cell))) continue;
      this.setBlock(cell.x, cell.y, cell.z, BlockId.Air);
    }
    for (const [at, put] of want) {
      if (this.getBlock(put.cell.x, put.cell.y, put.cell.z) === put.id) {
        this.coming.delete(at);
        continue;
      }
      this.coming.set(at, {
        x: put.cell.x, y: put.cell.y, z: put.cell.z, id: put.id,
        at: this.fluidClock + put.level / LAVA_SPEED,
      });
    }
    return filled;
  }

  /**
   * Нафта, which runs the way лава runs and at its own pace.
   *
   * The same flood as `updateLava` - a pool, a body of flow around it, and a
   * ring that arrives a step at a time - because that is what a liquid
   * standing in the ground does, and neither of them has the sea's special
   * cases to carry. What differs is the clock: water crosses two cells a
   * second, нафта one and two fifths, and that is the whole of "тече
   * повільніше на 30%".
   *
   * @returns how many cells now hold flowing нафта.
   */
  updateOil(x: number, y: number, z: number): number {
    const origin = { x, y, z };
    const body: Cell[] = [];
    const seeds: Cell[] = [];
    const seen = new Set<string>([cellKey(origin)]);
    const search: { cell: Cell; airSteps: number }[] = [
      { cell: origin, airSteps: 0 },
    ];
    let scanned = 0;

    const here = this.getBlock(x, y, z);
    if (here === OIL) seeds.push(origin);
    else if (isOilFlow(here)) body.push(origin);

    while (scanned < search.length && body.length < World.MAX_FLOOD) {
      const { cell, airSteps } = search[scanned]!;
      scanned++;
      for (const [dx, dy, dz] of NEIGHBOURS) {
        const next = { x: cell.x + dx, y: cell.y + dy, z: cell.z + dz };
        const k = cellKey(next);
        if (seen.has(k)) continue;
        seen.add(k);
        const id = this.getBlock(next.x, next.y, next.z);
        if (id === OIL) { seeds.push(next); continue; }
        if (isOilFlow(id)) {
          body.push(next);
          search.push({ cell: next, airSteps });
          continue;
        }
        if (id === BlockId.Air && airSteps < OIL_RANGE) {
          search.push({ cell: next, airSteps: airSteps + 1 });
        }
      }
    }

    const want = new Map<string, { cell: Cell; id: number; level: number }>();
    const inBody = new Set(body.map(cellKey));
    const free = (at: Cell): boolean =>
      inBody.has(cellKey(at)) || this.canFlowInto(at);

    const assigned = new Map<string, number>();
    let queue: { cell: Cell; level: number }[] =
      seeds.map((cell) => ({ cell, level: 0 }));
    let filled = 0;
    let head = 0;

    while (head < queue.length && filled < World.MAX_FLOOD) {
      const { cell, level } = queue[head]!;
      head++;
      const best = assigned.get(cellKey(cell));
      if (best !== undefined && best < level) continue;

      // Down first, and falling лава lands at full strength, the same as
      // water: what runs off a ledge makes a pool under it.
      const below = { x: cell.x, y: cell.y - 1, z: cell.z };
      const belowKey = cellKey(below);
      if (!assigned.has(belowKey) && free(below)) {
        want.set(belowKey, { cell: below, id: oilFlowId(1), level: 0 });
        assigned.set(belowKey, 0);
        queue.push({ cell: below, level: 0 });
        filled++;
        continue;
      }

      if (level >= OIL_RANGE) continue;
      for (const [dx, dz] of HORIZONTAL) {
        const side = { x: cell.x + dx, y: cell.y, z: cell.z + dz };
        const sideKey = cellKey(side);
        const known = assigned.get(sideKey);
        const improves = known === undefined ? free(side) : level + 1 < known;
        if (!improves) continue;
        want.set(sideKey, {
          cell: side, id: oilFlowId(level + 1), level: level + 1,
        });
        assigned.set(sideKey, level + 1);
        queue.push({ cell: side, level: level + 1 });
        if (known === undefined) filled++;
      }

      if (head > 4096) { queue = queue.slice(head); head = 0; }
    }

    for (const cell of body) {
      if (want.has(cellKey(cell))) continue;
      this.setBlock(cell.x, cell.y, cell.z, BlockId.Air);
    }
    for (const [at, put] of want) {
      if (this.getBlock(put.cell.x, put.cell.y, put.cell.z) === put.id) {
        this.coming.delete(at);
        continue;
      }
      this.coming.set(at, {
        x: put.cell.x, y: put.cell.y, z: put.cell.z, id: put.id,
        at: this.fluidClock + put.level / OIL_SPEED,
      });
    }
    return filled;
  }

  /**
   * How far the water has got, and how far it still has to go.
   *
   * The world keeps one clock for every fluid in it and a list of cells
   * waiting their turn. A cell is scheduled for `level / speed` seconds after
   * the edit that called for it - so the first ring arrives at once, the
   * second half a second later, and what a player sees is water running
   * along a trench rather than appearing in it.
   *
   * Called from the game loop; a world nobody is ticking simply holds still,
   * which is what a world nobody is standing in should do.
   *
   * @returns the cells that arrived, so a server can put them on the wire:
   * a trench filling is the same shape of message as somebody digging one.
   */
  stepFluids(dt: number): { x: number; y: number; z: number; id: number }[] {
    this.fluidClock += dt;
    const arrived: { x: number; y: number; z: number; id: number }[] = [];
    if (this.coming.size === 0) return arrived;
    for (const [at, put] of [...this.coming]) {
      if (put.at > this.fluidClock) continue;
      this.coming.delete(at);
      /*
       * Still worth putting down? Somebody may have built in the cell while
       * it was on its way, and water must not wash a wall out of the world.
       */
      if (this.getBlock(put.x, put.y, put.z) !== BlockId.Air) continue;
      this.setBlock(put.x, put.y, put.z, put.id);
      arrived.push({ x: put.x, y: put.y, z: put.z, id: put.id });
    }
    return arrived;
  }

  /** Is there anything still on its way? */
  get fluidsMoving(): boolean { return this.coming.size > 0; }

  /**
   * Run every fluid to a standstill, for a caller that cannot wait.
   *
   * Loading a save, growing a world, a check that is about the shape of a
   * lake rather than about how long it took to fill: none of those want to
   * watch it happen. Bounded, because a cell that arrives can schedule more.
   */
  settleFluids(): void {
    for (let round = 0; round < 512 && this.coming.size > 0; round++) {
      this.stepFluids(WATER_RANGE + 1);
    }
  }

  /**
   * Which way the water in this cell is running, and how hard.
   *
   * Downhill, and "hill" here is the same fill the mesher draws with: a cell
   * pushes toward each neighbour that stands lower than it, in proportion to
   * the drop. So a stream running eight blocks from a source pushes hardest
   * near the source, where the step down is steepest, and barely at all at the
   * far end where the water is a film - which is what water does.
   *
   * A source pushes too, when there is a flow beside it lower than itself.
   * Standing in the middle of a lake is standing between cells of equal
   * height, and the sum comes out zero without needing a special case.
   *
   * Returns a vector in blocks per second, not a direction: the caller adds it
   * to a velocity.
   */
  flowAt(x: number, y: number, z: number): { x: number; z: number } {
    const here = this.getBlock(x, y, z);
    if (!isWater(here)) return { x: 0, z: 0 };

    const mine = waterFill(here, this.getBlock(x, y + 1, z));
    let dx = 0;
    let dz = 0;

    for (const [ox, oz] of HORIZONTAL) {
      const there = this.getBlock(x + ox, y, z + oz);
      // Air off the edge of a stream is the steepest drop there is: water at
      // the lip of a waterfall runs at it, not away from it.
      const theirs = isWater(there)
        ? waterFill(there, this.getBlock(x + ox, y + 1, z + oz))
        : isSolid(there) ? mine : 0;
      const drop = mine - theirs;
      if (drop <= 0) continue;
      dx += ox * drop;
      dz += oz * drop;
    }

    const length = Math.hypot(dx, dz);
    if (length < 1e-6) return { x: 0, z: 0 };
    // Normalised and scaled, so the push is a current rather than a catapult
    // when several neighbours all fall away at once.
    return { x: (dx / length) * FLOW_PUSH, z: (dz / length) * FLOW_PUSH };
  }

  /**
   * What to write into a cell the flood has reached.
   *
   * Below sea level, water that came from the sea *is* the sea: a source, so
   * it neither runs out nor stands lower than its neighbours. Above sea level,
   * and anywhere the water did not come from the sea, it is a flow that gets
   * one step weaker and stops at eight.
   *
   * Without this the two rules contradict each other under water. A tunnel cut
   * into the seabed flooded nine blocks of twenty and left an air pocket under
   * the ocean, and a hole dug at the surface refilled with flows standing a
   * ninth of a block lower than the sea around them - which is what turned the
   * lake into a staircase.
   */
  private fillFor(at: Cell, sea: boolean, level: number): number {
    if (sea && at.y <= this.seaLevel) return BlockId.Water;
    return waterFlowId(level);
  }

  /**
   * Is this source part of the sea, rather than a puddle somebody poured?
   *
   * Below sea level with an unbroken column of water above it, up to the
   * waterline. A bucket emptied into a dry cave fails that and stays a bucket;
   * the ocean passes it everywhere.
   */
  private isSea(at: Cell): boolean {
    if (at.y > this.seaLevel) return false;
    for (let y = at.y + 1; y <= this.seaLevel; y++) {
      if (!isWater(this.getBlock(at.x, y, at.z))) return false;
    }
    return true;
  }

  /**
   * Can water move into this cell?
   *
   * Air only. Water in this game washes nothing away - a crop drowned by a
   * misplaced bucket would be a way to lose a season's work to a misclick.
   */
  private canFlowInto(at: Cell): boolean {
    // Off the edge of the map, setBlock quietly drops the write while getBlock
    // keeps answering "air" - so without this the search walks out into the
    // void, expanding cells that can never be filled. At a shoreline three
    // blocks from the border that turned one dug block into 3819 phantom fills.
    const limit = this.opts.sizeXZ * CHUNK_SIZE;
    if (at.y < 0 || at.x < 0 || at.x >= limit || at.z < 0 || at.z >= limit) {
      return false;
    }
    return this.getBlock(at.x, at.y, at.z) === BlockId.Air;
  }

  /** Is there water close enough to keep this soil moist? */
  isHydrated(x: number, y: number, z: number): boolean {
    const r = World.WATER_REACH;
    for (let dy = 0; dy <= 1; dy++) {
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          if (isFluid(this.getBlock(x + dx, y + dy, z + dz))) return true;
        }
      }
    }
    return false;
  }

  /**
   * Re-check every piece of farmland an edit could have affected.
   *
   * Called after the player changes a block. Digging a channel toward a field
   * should turn it dark, and filling one in should dry it out again - without
   * this, moisture would be frozen at the moment the soil was ploughed.
   */
  refreshFarmlandAround(x: number, y: number, z: number): void {
    const r = World.WATER_REACH;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          const bx = x + dx;
          const by = y + dy;
          const bz = z + dz;
          const here = this.getBlock(bx, by, bz);
          if (!isFarmland(here)) continue;
          const want = this.isHydrated(bx, by, bz)
            ? BlockId.FarmlandWet
            : BlockId.Farmland;
          if (here !== want) this.setBlock(bx, by, bz, want);
        }
      }
    }
  }

  /**
   * Clear out anything the 18+ switch gates, in the cells the player changed.
   *
   * Unmodified chunks need no sweep - they are regenerated from the seed, and
   * generation already asks the switch. Modified ones can still be holding
   * hemp a player planted while the switch was on, and turning it off has to
   * mean it is gone, not merely that no more will grow.
   *
   * @returns how many blocks were removed.
   */
  stripAdultBlocks(): number {
    let removed = 0;
    for (const chunk of this.modifiedChunks()) {
      const ox = chunk.cx * CHUNK_SIZE;
      const oy = chunk.cy * CHUNK_SIZE;
      const oz = chunk.cz * CHUNK_SIZE;
      for (let y = 0; y < CHUNK_SIZE; y++) {
        for (let z = 0; z < CHUNK_SIZE; z++) {
          for (let x = 0; x < CHUNK_SIZE; x++) {
            if (!isAdultOnly(chunk.get(x, y, z))) continue;
            this.setBlock(ox + x, oy + y, oz + z, BlockId.Air);
            removed++;
          }
        }
      }
    }
    return removed;
  }

  /** Drop from the list any lair whose cell no longer holds one. */
  private forgetBrokenLairs(): void {
    for (const [k, c] of this.lairs) {
      if (this.getBlock(c.x, c.y, c.z) !== BlockId.OrcSpawner) this.lairs.delete(k);
    }
  }

  /**
   * Every orc lair still standing, in no particular order.
   *
   * Rebuilt from the blocks when a load has happened, never trusted across
   * one. `loadChunk` writes straight into the chunk array without going
   * through `setBlock`, so nothing tells the register that a saved fortress
   * full of lairs has just arrived - and the raids would never come. This is
   * the same trap that once left every смолоскип in a reloaded world dark,
   * and it is answered the same way.
   */
  standingLairs(): Cell[] {
    if (this.lairsStale) {
      this.lairsStale = false;
      this.rescanLairs();
    }
    return [...this.lairs.values()];
  }

  /** Set by loadChunk: what the register knows is no longer the whole truth. */
  private lairsStale = false;

  /**
   * Walk the chunk arrays and find every lair there is.
   *
   * Reads the arrays directly rather than calling getBlock, for the reason
   * lightWorld gives: this touches every cell in the world, and a chunk
   * lookup per cell is what once cost this project a second of load time.
   */
  private rescanLairs(): void {
    this.lairs.clear();
    for (const chunk of this.chunks.values()) {
      const ox = chunk.cx * CHUNK_SIZE;
      const oy = chunk.cy * CHUNK_SIZE;
      const oz = chunk.cz * CHUNK_SIZE;
      for (let y = 0; y < CHUNK_SIZE; y++) {
        for (let z = 0; z < CHUNK_SIZE; z++) {
          for (let x = 0; x < CHUNK_SIZE; x++) {
            if (chunk.get(x, y, z) !== BlockId.OrcSpawner) continue;
            const cell = { x: ox + x, y: oy + y, z: oz + z };
            this.lairs.set(cellKey(cell), cell);
          }
        }
      }
    }
  }


  /** Chunks that differ from what generation would produce. */
  modifiedChunks(): Chunk[] {
    const out: Chunk[] = [];
    for (const k of this.modified) {
      const chunk = this.chunks.get(k);
      if (chunk !== undefined) out.push(chunk);
    }
    return out;
  }

  /**
   * Restore a chunk from a save, replacing whatever generation produced.
   *
   * Marked modified so it is written out again by the next save - a loaded
   * world must survive being saved.
   */
  loadChunk(cx: number, cy: number, cz: number, blocks: Uint16Array): void {
    // Whatever was lit is no longer the truth: this writes a chunk wholesale,
    // torches and walls and all. Dropping the light makes the next read
    // rebuild it from the blocks that are actually there now.
    // Whatever was lit near here is no longer the truth: this writes a chunk
    // wholesale, torches and walls and all.
    this.forgetLight(cx, cz);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) this.forgetLight(cx + dx, cz + dz);
    }
    // And the same for the lairs, for exactly the same reason.
    this.lairsStale = true;
    const chunk = this.ensureChunk(cx, cy, cz);
    if (chunk === undefined) return;
    chunk.blocks.set(blocks);
    chunk.recount();
    chunk.touch();
    this.modified.add(key(cx, cy, cz));
    // This writes into the chunk directly, so setBlock's bookkeeping never
    // runs. The chests get away with that because their contents are saved
    // separately; the lairs would not - a save made after the players broke
    // one would load with the lair back on the list and raids coming out of
    // a cell that is now empty air.
    this.forgetBrokenLairs();
    // Neighbours cull against this chunk, so their meshes are stale too.
    for (const [dx, dy, dz] of NEIGHBOURS) {
      this.chunks.get(key(cx + dx, cy + dy, cz + dz))?.touch();
    }
  }

  /**
   * Copy one chunk plus a one-block margin of its neighbours into a flat array.
   *
   * This is the unit of work handed to a meshing worker: everything the mesher
   * needs to cull faces correctly at chunk seams, with no reference back to
   * the World, so it can be transferred to another thread.
   */
  extractPadded(cx: number, cy: number, cz: number): Uint16Array {
    const out = new Uint16Array(PADDED_VOLUME);
    const ox = cx * CHUNK_SIZE;
    const oy = cy * CHUNK_SIZE;
    const oz = cz * CHUNK_SIZE;

    const chunk = this.getChunk(cx, cy, cz);

    for (let y = -PAD; y < CHUNK_SIZE + PAD; y++) {
      for (let z = -PAD; z < CHUNK_SIZE + PAD; z++) {
        for (let x = -PAD; x < CHUNK_SIZE + PAD; x++) {
          const inside =
            x >= 0 && x < CHUNK_SIZE &&
            y >= 0 && y < CHUNK_SIZE &&
            z >= 0 && z < CHUNK_SIZE;
          // Interior reads skip the chunk lookup entirely - this loop runs
          // 5832 times per chunk, so the fast path is worth having.
          const id = inside && chunk !== undefined
            ? chunk.blocks[blockIndex(x, y, z)]!
            : this.sample(ox + x, oy + y, oz + z);
          out[paddedIndex(x, y, z)] = id;
        }
      }
    }
    return out;
  }

  /**
   * The light for one chunk plus the same one-block margin.
   *
   * Packed the way the field stores it - sky in the high nibble, torchlight in
   * the low one - because the mesher only ever passes it through to a vertex
   * attribute and never has to reason about either half.
   *
   * A face takes its light from the cell it *looks into*, not the one it
   * belongs to, which is why the margin has to be here at all: the lit air in
   * front of a wall is in the next chunk half the time.
   */
  extractPaddedLight(cx: number, cy: number, cz: number): Uint8Array {
    const out = new Uint8Array(PADDED_VOLUME);
    const ox = cx * CHUNK_SIZE;
    const oy = cy * CHUNK_SIZE;
    const oz = cz * CHUNK_SIZE;
    const span = CHUNK_SIZE + 2 * PAD;
    const field = this.light;

    // Row by row, not cell by cell. A row of the light field is contiguous,
    // so each of these is one array copy instead of eighteen lookups - and
    // this runs for every chunk the renderer redraws.
    for (let y = -PAD; y < CHUNK_SIZE + PAD; y++) {
      for (let z = -PAD; z < CHUNK_SIZE + PAD; z++) {
        field.copyRow(
          out, paddedIndex(-PAD, y, z), ox - PAD, oy + y, oz + z, span,
        );
      }
    }
    return out;
  }

  /**
   * A read for the mesher's one-block margin, where outside the world is
   * *not* air.
   *
   * getBlock answers air beyond the edges, which is right for gameplay and
   * wrong for meshing: it makes the outermost faces of the map visible, so the
   * sea ends in a vertical wall of water and the ground shows its layers like
   * a cutaway diagram. Reporting stone instead hides those faces, and the
   * world simply stops instead of being sliced open.
   *
   * Only sideways and downwards. Above the world must stay air, or the top
   * face of anything reaching the ceiling would vanish into the sky.
   */
  private sample(x: number, y: number, z: number): number {
    const limit = this.opts.sizeXZ * CHUNK_SIZE;
    const outside = x < 0 || x >= limit || z < 0 || z >= limit || y < 0;
    return outside ? BlockId.Stone : this.getBlock(x, y, z);
  }

  /** Highest non-air block at this column, or -1 if the column is empty. */
  surfaceAt(x: number, z: number): number {
    const top = this.opts.sizeY * CHUNK_SIZE - 1;
    for (let y = top; y >= 0; y--) {
      if (this.getBlock(x, y, z) !== BlockId.Air) return y;
    }
    return -1;
  }

  /**
   * Highest block you could actually stand on: solid, opaque, and not water.
   *
   * Distinct from `surfaceAt`, which returns the topmost block of any kind.
   * Once trees existed, that was usually a leaf twenty blocks up - spawning
   * there dropped the player out of a canopy.
   */
  groundAt(x: number, z: number): number {
    const top = this.opts.sizeY * CHUNK_SIZE - 1;
    for (let y = top; y >= 0; y--) {
      const id = this.getBlock(x, y, z);
      if (isSolid(id) && isOpaque(id)) return y;
    }
    return -1;
  }

  /**
   * A dry, open place to stand.
   *
   * Searches outward from the requested column in a widening ring until it
   * finds ground with air above it. Roughly a sixth of the world is under
   * water, so spawning blind puts the player in a lake often enough to matter.
   */
  findSpawn(
    preferX: number, preferZ: number,
    /**
     * Refuse anywhere at or below the waterline.
     *
     * Air overhead is enough for a person, who can stand on a beach and get
     * their feet wet. It is not enough for livestock: an animal put down on
     * the last dry block of a shore is one step from the sea, and the first
     * herd spawned that way had nine of fourteen swimming inside a minute.
     */
    aboveWater = false,
  ): { x: number; y: number; z: number } {
    const limit = this.opts.sizeXZ * CHUNK_SIZE;
    const inside = (v: number): boolean => v >= 1 && v < limit - 1;

    for (let radius = 0; radius < limit / 2; radius++) {
      for (let dz = -radius; dz <= radius; dz++) {
        for (let dx = -radius; dx <= radius; dx++) {
          // Only the ring's edge is new; the interior was checked already.
          if (radius > 0 && Math.abs(dx) !== radius && Math.abs(dz) !== radius) continue;

          const x = preferX + dx;
          const z = preferZ + dz;
          if (!inside(x) || !inside(z)) continue;

          /*
           * Grow it before looking at it.
           *
           * A world that makes its country as somebody walks into it has
           * nothing at all where nobody has been - and a search that reads
           * air, finds air above it, and calls that dry land puts the player
           * down in the sky over ground that does not exist yet.
           */
          if (this.opts.terrain === "adventure") {
            this.ensureColumn(floorDiv(x, CHUNK_SIZE), floorDiv(z, CHUNK_SIZE));
          }
          const ground = this.groundAt(x, z);
          if (ground < 0) continue;
          if (aboveWater && ground <= this.seaLevel + 1) continue;
          // Air directly above, so this is not the floor of a lake or a cave.
          if (this.getBlock(x, ground + 1, z) !== BlockId.Air) continue;
          if (this.getBlock(x, ground + 2, z) !== BlockId.Air) continue;

          return { x: x + 0.5, y: ground + 1, z: z + 0.5 };
        }
      }
    }
    // Nowhere dry: drop them in the middle and let them swim.
    return { x: preferX + 0.5, y: this.opts.sizeY * CHUNK_SIZE - 2, z: preferZ + 0.5 };
  }
}
